// The venue console's copy (spec/13) — the `console` namespace. Loaded by the console alone, through
// `@clavian/i18n/console`, so the kiosk and the apps do not carry it.
//
// The voice is BRAND.md §6's: say what happens, plainly; a control says exactly what it does. The
// words are the glossary's: organisation, venue, member, station.
//
// Setting keys contain dots (`venue.closingAction`), which i18next reads as nesting, so a setting's
// copy is filed under its key with the dots written as `__` (`venue__closingAction`).
import { addonsCopy, bootCopy } from './boot.js';
import { bootGuideCopy } from './boot-guide.js';
import problemsConsole from './problems-console.js';
export const consoleCopy = {
    // Refusals worded for staff, over the shared lines in `common.problems`.
    problems: problemsConsole,
    // Clavian Boot (boot.ts): the venue's boot server, images, PCs and health; and the organisation's read-only add-ons.
    boot: bootCopy,
    addons: addonsCopy,
    // Problem cards and the guided Clavian Boot setup (boot-guide.ts).
    bootGuide: bootGuideCopy,
    app: {
        skipToContent: 'Skip to the page',
        loading: 'Loading',
    },
    // Words the console's shared building blocks use on every screen (`src/ui`).
    common: {
        actions: 'Actions',
        actionsFor: 'Actions for {{name}}',
        search: 'Search',
        filter: 'Filter',
        all: 'All',
        close: 'Close',
        cancel: 'Cancel',
        back: 'Back',
        next: 'Continue',
        done: 'Done',
        undo: 'Undo',
        edit: 'Edit',
        editField: 'Edit {{label}}',
        save: 'Save',
        saving: 'Saving…',
        open: 'Open',
        remove: 'Remove',
        none: 'None',
        forbiddenTitle: 'Not for your role',
        noMatches: 'Nothing matches “{{query}}”.',
        clearSearch: 'Clear the search',
        view: {
            label: 'View',
            grid: 'Cards',
            list: 'List',
        },
        stepOf: 'Step {{step}} of {{total}}',
        // The confirm a dialog shows before a stray dismiss would discard something shown only once.
        guard: {
            keep: 'Keep it open',
            discard: 'Close anyway',
        },
    },
    errors: {
        generic: 'That did not work. Try again, and if it keeps failing, quote the reference below to support.',
        network: 'Clavian could not be reached. Check the connection and try again.',
        reference: 'Reference {{id}}',
        retry: 'Try again',
    },
    signIn: {
        title: 'Sign in',
        intro: 'Staff sign-in to the venue console.',
        newHere: 'New here? Create an account',
        footer: 'For the staff and owners who run a gaming venue. Players use the Clavian app.',
        method: {
            legend: 'Sign in with',
            phone: 'A code to my phone',
            password: 'A password',
        },
        phone: {
            number: 'Phone number',
            numberHint: 'With the country code, for example +91 98765 43210.',
            send: 'Send the code',
            sent: 'Code sent to {{phone}}. It works for 10 minutes.',
            code: 'Code',
            codeHint: 'The six digits sent to {{phone}}.',
            verify: 'Sign in',
            change: 'Use another number',
        },
        password: {
            identifier: 'Email or gamer tag',
            password: 'Password',
            submit: 'Sign in',
        },
        required: 'Fill this in.',
        invalidPhone: 'Write the number with its country code, starting with +.',
        failure: {
            credentials: 'Incorrect email, gamer tag or password.',
            code: 'That code is not right, or it has expired.',
            rateLimited: 'Too many attempts. Wait a few minutes, then try again.',
            banned: 'This account cannot sign in. Please contact support.',
            notVerified: 'This account has no verified email or phone to sign in with.',
            delivery: 'The code could not be sent, and nothing was sent. Try again shortly.',
            noAccount: 'No staff account uses this number. Ask an owner to invite you.',
            phoneOff: 'Signing in with a phone number isn’t available right now. Sign in with your email or gamer tag and password instead.',
        },
    },
    signUp: {
        title: 'Create an account',
        intro: 'Create an account to sign in to the venue console.',
        footer: 'For new venue staff and owners setting up on Clavian.',
        name: 'Name',
        email: 'Email',
        password: 'Password',
        passwordHint: 'At least 8 characters. Checked against known breached passwords.',
        submit: 'Create account',
        signInInstead: 'Already have an account? Sign in',
        steps: {
            details: 'Your details',
            verify: 'Verify email',
        },
        legal: {
            terms: 'Terms of Service',
            privacy: 'Privacy Policy',
            unavailable: 'Sign-up needs the terms and the privacy policy, which are not set up yet. Try again shortly.',
        },
        consent: {
            label: 'I agree to the Terms of Service and the Privacy Policy.',
            // The same sentence with each document as a link: `{{terms}}` and `{{privacy}}` are where the links stand.
            labelLinked: 'I agree to the {{terms}} and the {{privacy}}.',
            required: 'Agree to the terms and the privacy policy to continue.',
            marketing: 'Send me product news and offers.',
        },
        verify: {
            title: 'Verify your email',
            intro: 'Enter the six-digit code sent to {{email}}.',
            code: 'Code',
            codeHint: 'The six digits sent to {{email}}.',
            verify: 'Verify',
            resend: 'Send the code again',
            resent: 'Code sent to {{email}}. It works for 10 minutes.',
            changeEmail: 'Use a different email address',
        },
        // From an invitation: the invited address is the account's, prefilled and not to be changed.
        invitedIntro: 'Create your account to accept your invitation to {{organization}}.',
        invitedEmailHint: 'The address your invitation was sent to. The account is created with it.',
        failure: {
            emailTaken: 'An account already uses this email address. Sign in instead.',
            invalidEmail: 'Write a valid email address.',
            passwordTooShort: 'Use at least 8 characters.',
            passwordTooLong: 'That password is too long.',
            passwordCompromised: 'That password has appeared in a data breach. Choose a different one.',
            consentRequired: 'Agree to the terms and the privacy policy to continue.',
            refused: 'An account cannot be created with this email address. Contact support if you think this is a mistake.',
        },
    },
    invitations: {
        accept: {
            title: 'Join the staff',
            // A café's first owner, invited by the Clavian team: a welcome rather than a staff invitation.
            titleOwner: 'Welcome to Clavian',
            subtitle: 'Accept your invitation to an organisation’s staff.',
            footer: 'For the staff and owners who run a gaming venue. Players use the Clavian app.',
            footerOwner: 'After you accept, a short guided setup takes you through your business details, your first venue and going live.',
            invitedByHq: 'The Clavian team has invited you to run {{organization}} on Clavian, as its owner.',
            invitedByMember: '{{name}} has invited you to join {{organization}} as {{role}}.',
            invitedByOrganization: '{{organization}} has invited you to join its staff as {{role}}.',
            role: {
                owner: 'an owner',
                manager: 'a manager',
                cashier: 'a cashier',
                floor: 'floor staff',
                kitchen: 'kitchen staff',
                technician: 'a technician',
                accountant: 'the accountant',
            },
            forContact: 'This invitation is for {{destination}}. It works until {{date}}.',
            emailIntro: 'Create your account with {{email}} — we email you a code to confirm it — or sign in if you already have one. Then accept.',
            phoneIntro: 'We text a code to this number. It signs you in, or creates your account if you do not have one yet. Then accept.',
            // Phone sign-in turned off for now: no code can go to the invited number.
            phoneOff: 'This invitation was sent to a phone number, and signing in with a phone number isn’t available right now. If your account has a password, sign in with it, then accept. Otherwise, ask whoever invited you to invite you by email.',
            phoneLocked: 'The number your invitation was sent to.',
            newNumber: 'No account uses this number yet. Agree to the terms to create yours with the same code.',
            createWithPhone: 'Create my account',
            sendAgain: 'Send the code again',
            missingToken: 'This invitation link is incomplete. Open the link from the invitation message again.',
            signIn: 'Sign in',
            createAccount: 'Create your account',
            confirmIntro: 'Signed in as {{name}}.',
            accept: 'Accept the invitation',
            useThatOne: 'Sign out and use that one',
            secondFactorLabel: 'Set up two-step sign-in',
            secondFactorOwner: 'Owners protect their café’s money and staff with two-step sign-in. Set it up now, and the invitation is accepted straight after.',
            secondFactorRole: 'This role needs two-step sign-in. Set it up now, and the invitation is accepted straight after.',
            failure: {
                unavailable: 'This invitation does not exist, was used, was withdrawn, or has expired. Ask whoever invited you for a fresh one.',
                mismatchFor: 'This invitation is for {{destination}}, and you are signed in with a different account. Sign out, then sign in or create your account with that one.',
                alreadyMember: 'You are already on this organisation’s staff.',
                sharedDevice: 'Accept an invitation on your own device, signed in as you.',
            },
        },
    },
    // Continue with Google on the sign-in and sign-up screens, and what a refused round trip came back with.
    social: {
        or: 'or',
        google: 'Continue with Google',
        groupLabel: 'Continue with',
        continue: {
            google: 'Continue with Google',
            discord: 'Continue with Discord',
            steam: 'Continue with Steam',
        },
        provider: {
            google: 'Google',
            discord: 'Discord',
            steam: 'Steam',
        },
        dialog: {
            title: 'Create your Clavian account',
            body: 'You are new here. Agree to the terms to create your account with {{provider}}.',
            confirm: 'Continue with {{provider}}',
            cancel: 'Not now',
        },
        error: {
            noAccount: 'There is no Clavian account for that {{provider}} account yet.',
            signUpInstead: 'Create your account',
            notLinked: 'This email already has a Clavian account. Sign in with your password, then link {{provider}} from Your account.',
            cancelled: 'You cancelled signing in with {{provider}}.',
            expired: 'That sign-in took too long or was started in another browser. Start again.',
            generic: 'Signing in with {{provider}} did not work. Try again, or sign in another way.',
            start: 'Google sign-in could not start. Try again shortly.',
            startProvider: 'We could not reach {{provider}}. Try again shortly.',
        },
    },
    // "Complete your profile": a staff member's first sign-in with Google, Discord or Steam.
    welcome: {
        title: 'Complete your profile',
        intro: 'Check your name before you go on. It is what your team and the activity log show.',
        name: 'Your name',
        submit: 'Save and continue',
        failed: 'That did not save. Try again.',
    },
    secondFactor: {
        title: 'Confirm it is you',
        intro: 'Enter the six-digit code from your authenticator app.',
        stepUpIntro: 'Your role needs a second factor before the console will open. Enter the code from your authenticator app.',
        code: 'Authenticator code',
        backupCode: 'Backup code',
        useBackup: 'Use a backup code instead',
        useApp: 'Use the authenticator app instead',
        confirm: 'Confirm',
        signOut: 'Sign out instead',
        enrol: {
            title: 'Set up an authenticator',
            intro: 'Your role needs a second factor. Add Clavian to an authenticator app on your phone, then enter the code it shows.',
            start: 'Set up an authenticator',
            password: 'Your password',
            passwordHint: 'Only if your account has one. Without one, the setup needs a sign-in from the last ten minutes.',
            key: 'Setup key',
            keyHint: 'Type this into the authenticator app, or open the link on the phone that has it.',
            openLink: 'Open in an authenticator app',
            backupCodes: 'Backup codes',
            backupCodesHint: 'Each works once, in place of the app. Keep them somewhere safe: they are shown only now.',
            staleSession: 'Sign in again first: setting up an authenticator without a password needs a sign-in from the last ten minutes.',
        },
    },
    shell: {
        mainNav: 'Main',
        // The top bar's switcher: where you are, and every venue and organisation you can go to instead.
        switcher: {
            // The trigger's name: the place it shows, then what it does.
            trigger: '{{place}}: switch venue or organisation',
            title: 'Switch venue or organisation',
            wholeOrganisation: 'Whole organisation',
            search: 'Find a venue or organisation',
            results: 'Venues and organisations',
            empty: 'No venue or organisation matches “{{query}}”.',
            venueCount_one: '{{count}} venue',
            venueCount_other: '{{count}} venues',
            // Venues whose organisation could not be named.
            otherVenues: 'Other venues',
            all: 'All venues and organisations',
        },
        organisation: 'Organisation',
        organisationSettings: '{{organisation}} settings',
        staffMenu: 'Signed in as {{name}}',
        staffMenuOpen: 'Account: {{name}}',
        staffMenuOpenAnonymous: 'Account',
        signOut: 'Sign out',
        // The quick theme switch in the staff menu (ADR-0041); the full choice is in Your account → Appearance.
        useLightTheme: 'Use the light theme',
        useDarkTheme: 'Use the dark theme',
        // ADR-0032: signing out while holding a drawer.
        holding: {
            title: 'You’re still holding the {{counter}} drawer',
            body: 'Hand it over to whoever takes the counter next, end the shift with a count, or sign out and leave the shift open.',
            handOver: 'Hand over',
            handOverBody: 'The drawer stays open for the next person: they count it as they take it over (Take over — count the drawer), and the shift ends on their count. Sign out now?',
            handOverConfirm: 'Leave it for the next person',
            end: 'End shift',
            signOutAnyway: 'Sign out anyway',
        },
        navigation: 'Navigation',
        openNavigation: 'Open the navigation',
        // The quiet word on a tab whose screen is not built yet (design guide, principle 6).
        soon: 'Soon',
        notifications: 'Notifications',
        notificationsEmptyTitle: 'You’re all caught up',
        notificationsEmptyBody: 'Alerts about the floor, orders and shifts will arrive here.',
        notificationsMarkAllRead: 'Mark all read',
        notificationsMarkRead: 'Mark read: {{title}}',
        // The staff menu's link to the member's own account page.
        account: 'Your account',
        // Beside it, at a venue where the member has no staff PIN yet (F-IAM-10): a gentle nudge, nothing more.
        accountPinPrompt: 'Set your staff PIN for this venue',
        notificationsError: 'Notifications could not be loaded. Try again shortly.',
        agoNow: 'now',
        agoMinutes: '{{n}}m',
        agoHours: '{{n}}h',
        // The top bar's shift status (spec/13 §2), read from the venue's open shift.
        shiftClosed: 'No shift open',
        shiftOpen: 'Shift open since {{since}}',
        // The sidebar groups (spec/13 §2, spec/14 §1), grouped by job.
        group: {
            operations: 'Operations',
            people: 'People',
            setup: 'Setup',
            commerce: 'Commerce',
            oversight: 'Oversight',
            platform: 'Platform',
        },
        // A sidebar section's label, by id. Venue and organisation share this list; a name that differs
        // between them (Staff and roles) has its own key. A section not built yet keeps its label here for
        // its placeholder page, though the sidebar does not list it (spec/13 §2).
        nav: {
            floor: 'Floor',
            sell: 'Sell',
            passes: 'Passes',
            orders: 'Orders',
            kitchen: 'Kitchen',
            stock: 'Stock',
            reservations: 'Reservations',
            players: 'Players',
            shifts: 'Shifts',
            reports: 'Reports',
            pricing: 'Pricing & promotions',
            library: 'Library',
            stations: 'Stations',
            boot: 'PCs & games',
            addons: 'Add-ons',
            staff: 'Staff',
            settings: 'Settings',
            overview: 'Overview',
            venues: 'Venues',
            payouts: 'Payouts',
            integrations: 'Integrations',
            subscription: 'Subscription',
            staffAndRoles: 'Staff and roles',
            audit: 'Audit log',
        },
    },
    // The command palette (spec/13 §2's `Ctrl+K`): navigation turned into search, plus a few actions.
    palette: {
        open: 'Search or jump to…',
        shortcut: 'Ctrl K',
        title: 'Command palette',
        hint: 'Search for a section or a setting, or run an action. Move with the arrow keys, open with Enter.',
        placeholder: 'Search or jump to…',
        results: 'Results',
        goTo: 'Go to',
        actions: 'Actions',
        empty: 'No matches for “{{query}}”.',
        footMove: 'Move',
        footOpen: 'Open',
        footClose: 'Close',
        // Settings found by name, key or description (spec/13 §13): each opens its screen at the field.
        settings: {
            title: 'Settings',
            detail: '{{group}} · {{scope}}',
            readOnly: 'Read only',
            scope: {
                venue: 'Venue: {{venue}}',
                organization: 'Organisation',
                platform: 'Platform defaults',
            },
        },
    },
    // The venue Library (spec/13 §9.1, F-CAT-02): the titles a venue offers, from the platform catalogue.
    library: {
        title: 'Library',
        intro: 'The games and apps this venue offers players, drawn from the platform catalogue.',
        forbidden: 'Your role cannot see this venue’s library.',
        readOnly: 'Read only: your role cannot change what this venue offers.',
        loading: 'Loading the library',
        loadFailed: 'The library could not be loaded.',
        empty: {
            title: 'No titles yet',
            body: 'Add a game from the platform catalogue to offer it on this venue’s stations.',
        },
        list: {
            caption: 'Titles this venue offers',
            more: 'Show more',
        },
        // Install detection (F-CAT-03): the titles × stations matrix, and what the PCs have that the library does not.
        tabs: 'Library views',
        tab: {
            titles: 'Titles',
            customApps: 'Custom apps',
            installs: 'On your PCs',
        },
        matrix: {
            caption: 'Which titles each station has installed',
            title: 'Title',
            loading: 'Loading what each PC has installed',
            loadFailed: 'Could not load what the PCs have installed.',
            class: 'Station class',
            allClasses: 'All classes',
            zone: 'Zone',
            allZones: 'All zones',
            noZone: 'No zone',
            missingOnly: 'Missing only',
            noneMissing: 'Every PC that reported has every title it should.',
            rescanAll: 'Rescan all',
            rescanSent_one: 'Asked {{count}} PC to rescan.',
            rescanSent_other: 'Asked {{count}} PCs to rescan.',
            rescanFailed: 'Could not ask the PCs to rescan.',
            neverReported: 'Not reported yet',
            scannedAt: 'Checked {{time}}',
            // A cell's detail: its version and size from the station's latest report, and when that report came.
            sizeGb: '{{size}} GB',
            reportedAt: 'Reported {{time}}',
            empty: {
                title: 'Nothing to compare yet',
                body: 'Add titles to the library and pair stations; each PC reports what it has installed.',
            },
            cell: {
                installed: 'Installed {{version}}',
                updating: 'Updating {{percent}}%',
                missing: 'Missing',
                notReported: 'Not reported',
                notOffered: 'Not offered on this class',
                unverified: 'Could not be checked on this PC; players still see it here',
            },
            // The cells of an app PCs install for players, where it is not installed: what the PC said about it.
            legend: '↓ installing for players · × could not install · ○ installed for another Windows account only',
        },
        // "Install on PCs missing it" (ADR-0056): a one-off install of one app on every PC that does not have it.
        install: {
            menu: 'Install on PCs missing it',
            title: 'Install {{app}} on PCs missing it?',
            body: 'Each PC that does not have {{app}} for players installs it as soon as it is free: on the lock screen, never during a session. A PC that is off or offline installs it when it next connects.',
            switchOn: 'Install for players stays on.',
            switchOff: 'This is a one-off: Install for players stays off.',
            confirm: 'Install',
            sent_one: 'Asked {{count}} PC to install {{app}}.',
            sent_other: 'Asked {{count}} PCs to install {{app}}.',
            none: 'No PC was asked: every PC has {{app}} already, or cannot install it.',
            failed: 'Could not ask the PCs to install {{app}}.',
        },
        // "Install for players": PCs install an app that installs per Windows account into the account players use.
        players: {
            label: 'Install for players',
            hint: 'PCs install this app into the account players use, while no one is playing.',
            on: 'PCs will install {{app}} for players.',
            off: 'PCs will no longer install {{app}} for players.',
            failed: 'The change could not be saved.',
            section: 'Install for players',
            pcs: 'PCs',
            pcsEmpty: 'No PC has reported yet.',
            notReported: 'Not reported by any PC yet',
            installed: 'Installed for players on {{n}} of {{total}} PCs',
            installing_one: 'Installing on {{count}} PC when it is free',
            installing_other: 'Installing on {{count}} PCs when they are free',
            queued_one: 'Waiting to install on {{count}} PC when it is free',
            queued_other: 'Waiting to install on {{count}} PCs when they are free',
            failedCount_one: 'Could not install on {{count}} PC',
            failedCount_other: 'Could not install on {{count}} PCs',
            otherAccount_one: 'Installed for another Windows account only on {{count}} PC, so players can’t open it there',
            otherAccount_other: 'Installed for another Windows account only on {{count}} PCs, so players can’t open it there',
            otherAccountAction: 'Turn on Install for players to install it in the account players use.',
            missingFromImage_one: 'Not in the diskless image on {{count}} PC',
            missingFromImage_other: 'Not in the diskless image on {{count}} PCs',
            missingFromImageAction: 'Install it on the super client with this switch on, then save the image.',
            notOnPc_one: 'Not on {{count}} PC',
            notOnPc_other: 'Not on {{count}} PCs',
            notOnPcAction: 'Install it once from the Microsoft Store in any Windows account on those PCs.',
            // The short status pill of an app on one PC (the station window's Games and apps, the library entry's PC list).
            pill: {
                installed: 'Installed',
                unverified: 'Unverified',
                updating: 'Updating {{percent}}%',
                updatePaused: 'Update paused at {{percent}}%',
                installing: 'Installing',
                queued: 'Waiting for the PC to be free',
                failed: 'Install failed',
                notHere: 'Not on this PC',
                otherAccount: 'Another account only',
                missingFromImage: 'Missing from image',
            },
            // Where an app stands in one PC's player account (the station sheet and the matrix).
            state: {
                installed: 'Installed for players.',
                installing: 'Installing when the PC is free.',
                failed: 'Could not install.',
                other_account_only: 'Installed for another Windows account only. Players can’t open it.',
                missing_from_image: 'Not in the diskless image. Install it on the super client and save the image.',
                not_on_this_pc: 'Not on this PC. Install it once from the Microsoft Store in any Windows account here.',
                queued: 'Waiting for the PC to be free. It installs on the lock screen, never during a session.',
            },
            failure: {
                download_failed: 'The download failed.',
                host_not_allowed: 'The download was sent somewhere it is not allowed to come from.',
                signature_invalid: 'The installer is not signed, or its signature is not valid.',
                signer_mismatch: 'The installer is signed by someone other than the app’s publisher.',
                installer_failed: 'The installer failed.',
                timed_out: 'The installer took too long.',
                not_found_after_install: 'The installer finished, but the app is not there.',
                register_failed: 'Windows could not add the app for players.',
                snapshot_failed: 'The installed app could not be saved for checking between players, so it is not offered.',
                tampered: 'The app had been changed and could not be checked, so it is not offered until it is installed again.',
                restore_failed: 'The app had been changed and could not be put back, so it is not offered until it is installed again.',
                other: 'Something went wrong.',
            },
            retry: 'Trying again after {{time}}, when the PC is free.',
            detail: 'Details: {{detail}}.',
        },
        detected: {
            title: 'Detected on your PCs, not in your library',
            hint: 'Games your stations have installed that this library does not offer yet.',
            onPcs_one: 'On {{count}} PC',
            onPcs_other: 'On {{count}} PCs',
            add: 'Add to library',
            added: '{{name}} added to the library.',
            addFailed: 'Could not add {{name}}.',
            notInCatalogue: 'Not in the catalogue yet',
            inLibrary: 'In your library under another launcher',
            // F-CAT-08: a title with no catalogue match becomes a request to HQ.
            askHq: 'Ask HQ to add',
            askHqLabel: 'Ask HQ to add {{name}} to the catalogue',
            asked: 'Asked HQ to add {{name}}.',
            askFailed: 'Could not ask HQ to add {{name}}.',
            requested: 'Requested from HQ',
        },
        category: {
            games: 'Games',
            competitive: 'Competitive',
            casual: 'Casual',
            apps: 'Apps',
            browsers: 'Browsers',
            voice: 'Voice',
            media: 'Media',
            tools: 'Tools',
        },
        badge: {
            featured: 'Featured',
            hidden: 'Hidden',
            custom: 'Custom',
            archived: 'Archived',
        },
        classes: {
            all: 'Every station class',
            count_one: '{{count}} station class',
            count_other: '{{count}} station classes',
        },
        // The Titles tab's toolbar: search, All · Games · Apps, and the two narrowing chips.
        filter: {
            search: 'Search titles',
            kind: 'Kind of title',
            all: 'All',
            games: 'Games',
            apps: 'Apps',
            featured: 'Featured',
            installed: 'Installed for players',
            clear: 'Clear filters',
            emptyTitle: 'No titles match',
            emptyBody: 'Try a different search, or clear the filters to see every title.',
        },
        // A card's one line on Install for players, and the menu entry that opens the switch.
        card: {
            allPcs: 'All PCs',
            featuredLabel: 'Featured',
            menuInstallForPlayers: 'Install for players…',
            status: {
                off: 'Not installed for players',
                notReported: 'Not reported by any PC yet',
                all_one: 'On {{count}} PC',
                all_other: 'On all {{count}} PCs',
                some_one: 'On {{n}} of {{count}} PC',
                some_other: 'On {{n}} of {{count}} PCs',
                queued_one: 'Waiting on {{count}} PC',
                queued_other: 'Waiting on {{count}} PCs',
                failed_one: 'Install failed on {{count}} PC',
                failed_other: 'Install failed on {{count}} PCs',
            },
        },
        launch: {
            none: 'No launch recipe yet',
            count_one: '{{count}} launch recipe',
            count_other: '{{count}} launch recipes',
        },
        add: {
            open: 'Add title',
            title: 'Add a title',
            intro: 'Pick a game or an app from the platform catalogue, then choose how this venue offers it.',
            stepPick: 'Pick a title',
            stepOffer: 'Offer it',
            searchLabel: 'Search the catalogue',
            searchPlaceholder: 'Search for a game or an app…',
            // The Games / Apps filter above the catalogue browse (spec/14 §6: the catalogue holds apps too).
            filter: 'Show',
            filterAll: 'All',
            filterGames: 'Games',
            filterApps: 'Apps',
            loading: 'Loading the catalogue',
            browseFailed: 'The catalogue could not be loaded.',
            noResults: 'No catalogue titles match. HQ curates the platform catalogue.',
            results: 'Catalogue titles',
            inLibrary: 'In library',
            pick: 'Add',
            offerFor: 'How {{game}} is offered',
            added: 'Added {{game}} to the library.',
            already: 'That title is already in this venue’s library.',
            failed: 'The title could not be added.',
            submit: 'Add to library',
        },
        field: {
            category: 'Kiosk category',
            classes: 'Station classes',
            classesHint: 'Where the title appears. Choose none to offer it on every class.',
            noClasses: 'This venue has no station classes yet — the title will show on every class.',
            featured: 'Featured',
            featuredHint: 'Show the title in the kiosk showcase.',
            visible: 'Visible to players',
            visibleHint: 'A hidden title stays in the library but the kiosk does not list it.',
            // spec/11 §3.5: HQ flags a title as needing administrator rights; a venue may turn that off for itself.
            elevation: 'Launch with administrator rights',
            elevationHint: 'HQ marks this title as needing administrator rights, so stations launch it through the elevated service. Turn it off to launch it as the player at this venue.',
        },
        edit: {
            title: 'Edit title',
            sortOrder: 'Sort order',
            sortOrderHint: 'Lower sorts first within the category.',
            saved: 'Saved {{game}}.',
            failed: 'The change could not be saved.',
            submit: 'Save',
        },
        remove: {
            done: 'Removed {{game}} from the library.',
            failed: 'The title could not be removed.',
        },
        // An organisation's own custom apps (spec/13 §9): not in the platform catalogue, usable at every venue.
        customApps: {
            open: 'Add custom app',
            intro: 'Apps your organisation adds by hand — a tournament client, a local tool. Every venue of the organisation can offer them.',
            caption: 'Your organisation’s custom apps',
            loading: 'Loading custom apps',
            loadFailed: 'Custom apps could not be loaded.',
            empty: {
                title: 'No custom apps yet',
                body: 'Add an app that is not in the platform catalogue: its name, how the PCs launch it and an icon.',
            },
            inLibrary: 'In this venue’s library',
            notInLibrary: 'Not offered here',
            addHere: 'Add to this venue',
            addedHere: 'Added {{app}} to this venue’s library.',
            addHereFailed: 'Could not add {{app}} to this venue.',
            archive: 'Archive',
            // Archiving a custom app is organisation-wide and cannot be undone here, so it confirms in place first.
            archiveLabel: 'Archive {{app}}',
            archiveConfirm: 'Archive {{app}} across the whole organisation? It leaves every venue’s library. Entries already offering it keep working.',
            archiveKeep: 'Keep it',
            archived: 'Archived {{app}}. It is no longer offered on any station.',
            archiveFailed: 'Could not archive {{app}}.',
            launchSummary: {
                exe: 'Program',
                lnk: 'Shortcut',
                uri: 'Link',
                store: 'Microsoft Store app',
            },
            sheet: {
                createTitle: 'Add a custom app',
                editTitle: 'Edit custom app',
                stepName: 'Name and category',
                stepLaunch: 'How to launch',
                stepDetection: 'Detection',
                stepIcon: 'Icon',
                stepConfirm: 'Confirm',
                name: 'Name',
                nameHint: 'As players see it on the kiosk.',
                nameRequired: 'Give the app a name.',
                nameTaken: 'Another custom app in your organisation already has that name.',
                category: 'Kiosk category',
                categoryHint: 'Where venues file it by default. Each venue can change it.',
                launchIntro: 'How a PC starts the app. Paths may use these variables, which each PC resolves:',
                type: 'Launch type',
                types: {
                    exe: 'Program (.exe)',
                    lnk: 'Shortcut (.lnk or .url)',
                    uri: 'Link (URI)',
                    store: 'Microsoft Store app (AUMID)',
                },
                path: 'Path',
                pathHintExe: 'For example {ProgramFiles}\\Arena\\arena.exe',
                pathHintLnk: 'For example {UserProfile}\\Desktop\\Arena.lnk',
                uri: 'URI',
                uriHint: 'For example arena://play',
                aumid: 'App user model ID',
                aumidHint: 'The app’s full AUMID, as Get-StartApps in PowerShell lists it.',
                args: 'Arguments',
                workingDirectory: 'Working directory',
                launchRequired: 'Fill in how the app launches.',
                detectionIntro: 'The process names a PC recognises the app by, so the kiosk knows it is running and whether it is installed.',
                processNames: 'Process names',
                processNamesHint: 'Separate names with commas. “.exe” is optional.',
                iconIntro: 'A square PNG or WebP, up to {{size}}. Without one the kiosk shows the first letter.',
                iconFile: 'Icon file',
                iconDrop: 'Drop an icon here, or choose a file',
                iconDropHint: 'Square PNG or WebP, up to {{size}}',
                iconWrongType: 'The icon must be a PNG or WebP.',
                iconTooLarge: 'The icon must be {{size}} or smaller.',
                iconNone: 'No icon chosen',
                iconKeep: 'Keeps its current icon',
                iconChosen: 'Chosen: {{name}}',
                iconFailed: '{{app}} was saved, but its icon could not be uploaded. Try again from Edit.',
                confirmIntro: 'Check the details, then save.',
                addToVenue: 'Add to this venue’s library',
                addToVenueHint: 'Offer it on this venue’s stations now. Other venues can add it later.',
                detectionNone: 'None',
                created: 'Added {{app}}.',
                createdAndAdded: 'Added {{app}} and offered it at this venue.',
                saved: 'Saved {{app}}.',
                failed: 'The custom app could not be saved.',
                back: 'Back',
                next: 'Next',
                cancel: 'Cancel',
                create: 'Add custom app',
                save: 'Save',
            },
        },
    },
    // The one shared placeholder page for a sidebar section that is not built yet (`shell/ComingSoon.tsx`).
    // A section's `blurb` is one sentence; its `areas` are the sub-areas it will hold. Keyed by the
    // section id the sidebar uses.
    // The venue's Reports screen (spec/13 §2, spec/17): today the Games played report (F-CAT-04, spec/17 R-04).
    reports: {
        title: 'Reports',
        intro: 'How the business did over a range of business days.',
        hero: { unknown: '-' },
        notPermitted: 'Your role cannot read this venue’s reports.',
        notPermittedFinancial: 'Your role cannot read this venue’s financial reports.',
        unavailable: 'This report could not be loaded.',
        emptyTitle: 'Nothing to report in this range',
        empty: 'No payments, sessions or orders were recorded in these business days. Pick a longer range.',
        partial: 'Some figures reached their read limit and may be low. Narrow the range for exact figures.',
        noData: 'No data in this range',
        vsPrevious: 'vs {{range}}',
        businessDays: 'Business days, in the venue’s timezone up to its day cutoff',
        tabs: 'Reports',
        toolbar: 'Range and venues',
        exportCsv: 'Export CSV',
        weekOf: 'Week of {{date}}',
        byVenue: 'Venues side by side',
        byVenueHint: 'Each venue over its own business days, against its previous period.',
        chart: {
            showTable: 'Show as a table',
            summary: 'From {{first}} on {{firstDay}} to {{last}} on {{lastDay}}; highest {{max}} on {{maxDay}}.',
            plus: 'plus',
            minus: 'minus',
        },
        tab: {
            overview: 'Overview',
            revenue: 'Revenue',
            sessions: 'Sessions & occupancy',
            products: 'Products',
            wallet: 'Passes & wallet',
            staff: 'Staff',
            games: 'Games played',
            daily: 'Daily summary',
        },
        // The owners' daily summary (N-27): the same figures as the email it goes out as, for one business day.
        daily: {
            intro: 'One business day at a glance: the summary owners get by email after each day closes.',
            day: 'Business day',
            email: 'Email it to me',
            emailed: 'On its way to your email.',
            emailFailed: 'The summary could not be sent. Try again.',
            emailUnverified: 'Verify your email address in Your account first.',
            unavailable: 'The daily summary could not be loaded.',
            notPermitted: 'Only owners see the daily summary.',
            sent: 'Sent to owners {{when}}.',
            readNow: 'Read now. It goes to owners once the day is closed.',
            triggerClosed: 'Every day in it was closed.',
            triggerDeadline: 'Sent two hours after the day ended, with a day still open.',
            kpis: 'The day’s headline figures',
            vsLastWeek: 'vs last {{weekday}}',
            hoursPlayed: 'Hours played',
            players: 'Players',
            playersHint_one: '{{count}} new',
            playersHint_other: '{{count}} new',
            grossSalesHint: 'GST collected {{tax}}',
            highlights: 'Highlights',
            highlightsHint: 'What needs a look, and how the day compares',
            allClear: 'Nothing needs your attention.',
            best: 'Best venue: {{venue}}, with {{amount}} in net takings.',
            againstAverage: 'Net takings {{change}} vs the 7-day average of {{amount}}.',
            flag: {
                day_not_closed: '{{venue}}: the day has not been closed yet. These figures may still change.',
                cash_variance_short: '{{venue}}: the cash count was {{amount}} short.',
                cash_variance_over: '{{venue}}: the cash count was {{amount}} over.',
                refunds_high: '{{venue}}: refunds of {{amount}} are well above usual.',
                shift_open_one: '{{venue}}: {{count}} shift is still open.',
                shift_open_other: '{{venue}}: {{count}} shifts are still open.',
                station_offline_one: '{{venue}}: {{count}} station was offline for 30 minutes or more during a session.',
                station_offline_other: '{{venue}}: {{count}} stations were offline for 30 minutes or more during a session.',
                stock_alerts_one: '{{venue}}: {{count}} stock item needs attention.',
                stock_alerts_other: '{{venue}}: {{count}} stock items need attention.',
            },
            venues: 'Venues',
            venuesHint: 'Each venue on its own business day, most net takings first',
            venue: 'Venue',
            netTakings: 'Net takings',
            sessions: 'Sessions',
            occupancy: 'Occupancy',
            change: 'vs last week',
            notClosed: 'Not closed yet',
            bestBadge: 'Best',
            takings: 'Takings',
            takingsHint: 'From sales to what the business took',
            grossSales: 'Gross sales',
            discounts: 'Discounts',
            refunds_one: 'Refunds ({{count}})',
            refunds_other: 'Refunds ({{count}})',
            tax: 'GST collected',
            moneyIn: 'Money in, wallet top-ups included',
            paidBy: 'How it was paid',
            paidByHint: 'Net takings by tender, and what was sold on account',
            tender: {
                cash: 'Cash',
                counter_card: 'Card',
                counter_upi: 'UPI at the counter',
                online: 'Online',
                wallet: 'From wallets',
            },
            onAccount: 'Sold on account, not paid yet',
            play: 'Play',
            playHint: 'Sessions, time and how full the floor was',
            averageSession: 'Average session',
            occupancyOpen: 'Occupancy while open',
            perStation: 'Net takings per station',
            passSales: 'Play sold as passes',
            payAsYouGo: 'Play at the hourly rate',
            food: 'Food, drink and retail',
            foodHint: 'What sold, and the best sellers',
            foodSales: 'Food and drink',
            retailSales: 'Retail',
            orders: 'Orders',
            topItem: '{{name}} × {{quantity}}',
            bestSellers: 'Best sellers',
            noItems: 'Nothing sold',
            stockAlerts: 'Stock items needing attention',
            wallet: 'Wallets and credit',
            walletHint: 'Top-ups are held for players until they spend them',
            topUps: 'Wallet top-ups',
            bonusIssued: 'Bonus credit given',
            spent: 'Spent from wallets',
            onAccountOwed: 'Owed on account across your venues',
            cash: 'Cash',
            cashHint: 'What the drawers should hold against what was counted',
            expected: 'Expected in the drawers',
            counted: 'Counted',
            variance: 'Over or short',
            drawer: '{{counter}} drawer',
            drawerUnnamed: 'Drawer',
            drawerOpen: 'Still open',
            openShifts: 'Shifts still open',
            uniquePlayers: 'Players',
            newPlayers: 'New players',
            duration: '{{hours}} h {{minutes}} min',
        },
        range: {
            label: 'Range',
            today: 'Today',
            yesterday: 'Yesterday',
            last7: 'Last 7 days',
            last30: 'Last 30 days',
            thisMonth: 'This month',
            lastMonth: 'Last month',
            custom: 'Custom range',
            from: 'From',
            to: 'To',
            invalid: 'Pick a first and a last business day, at most {{max}} days apart, the last on or after the first.',
            summary: '{{range}}, compared with {{previous}}.',
            cutoff: 'Business days end at {{cutoff}}.',
        },
        scope: {
            label: 'Venues',
            here: '{{venue}}',
            all_one: 'All venues',
            all_other: 'All {{count}} venues',
        },
        kpi: {
            revenue: 'Revenue',
            revenueHint: 'Includes {{wallet}} spent from wallets. Top-ups count when spent.',
            moneyIn: 'Money in',
            moneyInHint: 'Cash, card, UPI and online, with {{topUps}} of wallet top-ups',
            sessions: 'Sessions',
            sessionsHint: '{{players}} players · {{fresh}} new',
            occupancy: 'Occupancy',
            occupancyHint: 'Station time in session, of the open hours',
            occupancyNoHours: 'Of the whole range: no opening hours are set',
            averageSession: 'Average session',
            playTime: '{{time}} played in all',
            arpu: 'Revenue per player',
            arpuHint: 'Revenue ÷ active players',
            netSales: 'Net sales',
            netSalesHint: 'Sold, before tax, less discounts and refunds',
            discounts: 'Discounts',
            discountsHint: 'Codes, promotions and manual discounts',
            refunds: 'Refunds',
            refundRate: '{{rate}} of what was paid',
        },
        column: {
            day: 'Business day',
            week: 'Week',
            weekday: 'Weekday',
            walletSpent: 'From wallets',
            playTime: 'Play time',
            venue: 'Venue',
            change: 'Change',
            topUps: 'Wallet top-ups',
            hour: 'Hour',
        },
        tender: {
            cash: 'Cash',
            counter_card: 'Card at the counter',
            counter_upi: 'UPI at the counter',
            online: 'Online',
            wallet: 'Spent from wallets',
        },
        category: {
            play: 'Play',
            food: 'Food and drinks',
            retail: 'Retail',
            membership: 'Membership',
            other: 'Other',
        },
        weekday: {
            '0': 'Mon',
            '1': 'Tue',
            '2': 'Wed',
            '3': 'Thu',
            '4': 'Fri',
            '5': 'Sat',
            '6': 'Sun',
        },
        weekdayLong: {
            '0': 'Monday',
            '1': 'Tuesday',
            '2': 'Wednesday',
            '3': 'Thursday',
            '4': 'Friday',
            '5': 'Saturday',
            '6': 'Sunday',
        },
        revenue: {
            trend: 'Revenue each day',
            trendHint: 'Revenue and money in, each business day',
            paidBy: 'How revenue was paid',
            walletSliceHint: 'Revenue now, not money in',
            negativeNote: 'Refunds were more than payments for {{tenders}}, so it takes no slice. Its figure is in the list.',
            notSold: 'Nothing sold in',
            refundedMore: 'Refunds were more than sales in {{categories}}.',
            walletCredit: 'Wallet credit given',
            walletCreditHint: 'Bonuses, coupons and manual credits: promotional, not a sale',
            paidByHint: 'Net of refunds. Wallet money counts when it is spent, never when it is topped up.',
            bonusNote: 'Bonus credit spent ({{amount}}) is a promotional cost, not revenue.',
            definition: 'Paid and settled, less refunds. Wallet spends count, top-ups do not.',
            byDay: 'Revenue by business day',
            byWeek: 'Revenue by week',
            groupBy: 'Group by',
            days: 'Days',
            weeks: 'Weeks',
            paidDirect: 'Paid in cash, card, UPI or online',
            fromWallets: 'Spent from wallets',
            fromWalletsHint: 'Revenue now; the money came in when the wallet was topped up',
            byCategory: 'Sales by category',
            byCategoryHint: 'Before tax, net of refunds',
            tax: 'Tax collected',
            taxPaidAtTopUp: 'GST paid at top-up',
            taxPaidAtTopUpHint: 'GST on balances moved in from your previous system, already paid when they were topped up there. Not collected again.',
            fromMoneyIn: 'From money in to revenue',
            fromMoneyInHint: 'Every term is read from the same ledger',
            moneyInHint: 'Cash, card, UPI and online taken, less money handed back',
            topUpsHint: 'Held for players until they spend it',
            handedBack: 'Top-ups handed back',
        },
        sessions: {
            empty: 'No sessions ran in these business days, and the venue has no stations offered. Pick a longer range.',
            byDay: 'Sessions each day',
            peaks: 'Busiest hours',
            peaksHint: 'Highest occupancy in the range',
            noPeaks: 'No station was in session in this range.',
            peakAt: '{{day}} {{hour}}',
            heatmap: 'Occupancy by hour and weekday',
            heatmapHint: 'Weekdays are business days: the small hours count with the night before.',
            peakMeta: 'Peak {{day}} {{hour}} at {{occupancy}}',
            cell: '{{day}} {{hour}}: {{occupancy}} occupied',
            cellClosed: '{{day}} {{hour}}: closed',
            legendClosed: 'Closed',
            byHour: 'Occupancy by hour',
            byHourHint: 'Every weekday together, from the day cutoff',
            byClass: 'By station class',
            byZone: 'By zone',
            groupHint: 'Station time in session, of the open hours',
            average: 'Average {{value}}',
            noSessions: 'No sessions in this range.',
            noClasses: 'No stations are offered.',
            noZones: 'No stations are offered.',
            noZone: 'Not in a zone',
            stations_one: '{{count}} station',
            stations_other: '{{count}} stations',
        },
        products: {
            empty: 'No items or passes were sold in these business days. Pick a longer range.',
            item: 'Item',
            kind: 'Kind',
            kindName: {
                food: 'Food and drinks',
                retail: 'Retail',
                other: 'Other',
            },
            quantity: 'Sold',
            quantityShort_one: '{{count}} sold',
            quantityShort_other: '{{count}} sold',
            orders: 'Orders',
            net: 'Net sales',
            pass: 'Pass',
            sold: 'Sold',
            granted: 'Granted',
            passRevenue: 'Sold for',
            itemsNet: 'Food, drinks and retail',
            itemsNetHint_one: 'Before tax, {{count}} item',
            itemsNetHint_other: 'Before tax, {{count}} items',
            passesSold: 'Passes sold',
            passesSoldHint: 'Sold for {{amount}}',
            playPasses: 'Play sold as passes',
            playPassesHint: 'Before tax, net of pass refunds',
            playRateCard: 'Play at the rate card',
            playRateCardHint: 'Pay as you go, from wallets and on account',
            topItems: 'Top items',
            topItemsHint: 'Paid orders, before tax',
            noItems: 'No food, drinks or retail were sold.',
            play: 'Play sold',
            playHint: 'Passes, and play time at the rate card',
            playDiscounts: 'Play promotions',
            playTotal: 'Play sold',
            passesHint: 'How many of each pass were sold, and what they sold for',
            soldCount_one: '{{count}} sold',
            soldCount_other: '{{count}} sold',
            passMeta: 'Sold for {{amount}}',
            passMetaGranted_one: 'Sold for {{amount}} · {{count}} granted',
            passMetaGranted_other: 'Sold for {{amount}} · {{count}} granted',
            allItems: 'Every item sold',
            passes: 'Passes',
            noPasses: 'No passes were sold or granted.',
        },
        wallet: {
            topUps: 'Wallet top-ups',
            topUpsHint: 'Held for players: a liability until spent',
            spent: 'Spent from wallets',
            spentHint: 'Revenue, on the day it is spent',
            bonusSpent: 'Bonus spent',
            bonusSpentHint: 'A promotional cost. {{granted}} granted',
            owed: 'Owed to players now',
            owedHint: 'Wallet balances, cash and bonus',
            trend: 'Top-ups and wallet spend',
            trendHint: 'Spent from wallets (revenue) and topped up (held for players), each business day',
            handedBackHint: 'Top-up money returned to players',
            bonusGrantedHint: 'Promotional credit put into wallets',
            noFlows: 'No wallet money moved in this range.',
            nothingOwed: 'Nothing is owed to players right now.',
            owedTotal: 'Wallets and passes',
            flows: 'Wallet money in the range',
            flowsHint: 'At the venues in scope',
            notRevenue: 'Not revenue until spent',
            handedBack: 'Top-ups handed back',
            isRevenue: 'Counted in revenue',
            bonusGranted: 'Bonus granted',
            promoCost: 'Promotional cost, not revenue',
            liabilities: 'Owed now',
            liabilitiesHint: 'Prepaid value players have not used yet',
            walletCash: 'Wallet balances (cash)',
            walletBonus: 'Wallet balances (bonus)',
            passLiability: 'Unused pass time',
            passLiabilityHint: 'At what it sold for',
            receivables: 'Owed to you on account',
            receivablesHint: 'What players owe',
            organisationWide: 'Balances are the organisation’s, as of now: a wallet or a pass is used at any of its venues.',
        },
        staff: {
            empty: 'No sales, refunds, voids or comps were recorded in these business days.',
            nobody: 'No member (kiosk, wallet play)',
            former: 'A former member',
            member: 'Member',
            voids: 'Voids',
            comps: 'Comps',
            minutes: '{{minutes}} min',
            topSeller: 'Top seller',
            topSellerHint: '{{amount}} rung up',
            members: 'Members selling',
            membersHint: 'Rang up a sale, a top-up or a refund',
            noVoids: 'No sales voided',
            voidedValue: '{{amount}} voided',
            noComps: 'No comps granted',
            compTime: '{{time}} of play comped',
            noRevenue: 'No member rang up revenue in this range.',
            topUpsBy: 'Top-ups taken by member',
            topUpsByHint: 'Money in for wallets: held for players, not revenue',
            noTopUps: 'No member took a top-up in this range.',
            tableHint: 'Every member, and what no member did (kiosk orders, wallet play) on its own row',
            revenueBy: 'Revenue by member',
            revenueByHint: 'What each member rang up, wallet spends included, refunds taken off',
            table: 'Sales, refunds, voids and comps by member',
        },
        gamesPlayed: {
            title: 'Games played',
            thisVenue: 'Games played is read per venue: this is the venue you are in.',
            hint: 'Time each title ran during sessions, as the PCs detected it. A play counts on the business day it started.',
            unavailable: 'The Games played report could not be loaded.',
            emptyTitle: 'No games played in this range',
            empty: 'PCs record the titles players run during their sessions. Pick a longer range, or check back after some sessions.',
            hours: '{{hours}} h',
            minutes: '{{minutes}} min',
            topHint: '{{time}} · {{share}} of play',
            titleMeta: '{{share}} of play · {{sessions}} · {{players}}',
            sessionsCount_one: '{{count}} session',
            sessionsCount_other: '{{count}} sessions',
            playersCount_one: '{{count}} player',
            playersCount_other: '{{count}} players',
            periodTop: '{{period}} · most played: {{title}}',
            stationsHint: 'Time played on each PC, with its most played titles',
            weekOf: 'Week of {{date}}',
            stationNumbered: 'PC {{number}}',
            stationGone: 'Removed PC',
            view: {
                label: 'View',
                titles: 'By title',
                days: 'By day',
                weeks: 'By week',
                stations: 'By PC',
            },
            heading: {
                titles: 'Most played titles',
                days: 'Hours played each day',
                weeks: 'Hours played each week',
                stations: 'Hours played on each PC',
            },
            caption: {
                titles: 'Hours played per title',
                days: 'Hours played per day',
                weeks: 'Hours played per week',
                stations: 'Hours played per PC',
            },
            figure: {
                hours: 'Hours played',
                players: 'Players',
                titles: 'Titles played',
                top: 'Most played',
            },
            column: {
                title: 'Title',
                hours: 'Hours',
                share: 'Share',
                sessions: 'Sessions',
                players: 'Players',
                station: 'PC',
                topTitles: 'Most played on it',
            },
        },
    },
    soon: {
        eyebrow: 'Coming soon',
        areasLabel: 'It will hold',
        roadmap: 'On the roadmap — designed, and matching the build.',
        fallbackTitle: 'Coming soon',
        fallbackBlurb: 'This part of the console is on the roadmap.',
        floor: {
            blurb: 'The live picture of the venue — every station as a tile with the time left, who is playing, and what needs you.',
            areas: ['Station tiles', 'What needs you', 'Coming up'],
        },
        sell: {
            blurb: 'One cart for time, passes, food, drinks and retail, with a tax-split checkout to any station.',
            areas: ['Time & passes', 'Food & drinks', 'Charge'],
        },
        orders: {
            blurb: 'Every order with how it was paid, its tax documents and refunds — filter by day, channel or staff and export the exact set.',
            areas: ['Filters', 'Refunds', 'Tax invoices'],
        },
        kitchen: {
            blurb: 'The kitchen display: tickets from the counter and the kiosk, moving from new to ready as the kitchen works.',
            areas: ['Tickets', 'Prep stations', 'Recall'],
        },
        reservations: {
            blurb: 'Bookings and the waitlist — hold a station, place a walk-in, and offer the next free station.',
            areas: ['Bookings', 'Waitlist', 'Holds'],
        },
        reports: {
            blurb: 'Takings, occupancy and cohorts over any period, side by side.',
            areas: ['Takings', 'Occupancy', 'Export'],
        },
        players: {
            blurb: 'The customer across the organisation — sessions, orders, wallet, passes, notes and tags.',
            areas: ['Search', 'Wallet', 'Notes'],
        },
        shifts: {
            blurb: 'Open and close a shift with a float and a blind count, with the X and Z reports and the hand-over.',
            areas: ['Float', 'Cash movements', 'Blind count'],
        },
        library: {
            blurb: 'The games and apps on the floor — what is installed, what is featured, and what a kiosk may launch.',
            areas: ['Games', 'Apps', 'Featured'],
        },
        overview: {
            blurb: 'The organisation at a glance — venues, takings and what needs attention across the estate.',
            areas: ['Venues', 'Takings', 'Alerts'],
        },
        venues: {
            blurb: 'Every venue in the organisation — status, hours, load and public listing.',
            areas: ['Status', 'Hours', 'Listing'],
        },
        payouts: {
            blurb: 'Settlements from the payment provider — what is due, what has arrived, and the fees taken.',
            areas: ['Settlements', 'Fees', 'Statements'],
        },
        subscription: {
            blurb: 'The organisation’s plan, the entitlements in use, and its invoices from Clavian.',
            areas: ['Plan', 'Entitlements', 'Invoices'],
        },
        integrations: {
            blurb: 'Connect the tools the organisation already uses — accounting, messaging and analytics.',
            areas: ['Accounting', 'Messaging', 'Webhooks'],
        },
        staffAndRoles: {
            blurb: 'The people on the organisation’s staff and what each role may do, across every venue.',
            areas: ['Members', 'Roles', 'Invitations'],
        },
    },
    // The organisation's audit log (spec/13 §15, F-ORG-14): the screen, and a player's Audit tab.
    audit: {
        title: 'Audit log',
        intro: 'Every recorded action in the organisation: who did it, where, and when.',
        acrossOrganization: 'Across the organisation',
        notPermitted: 'Your role cannot read this organisation’s audit log.',
        trace: {
            title: 'Trace a request',
            label: 'Request id',
            description: 'Paste the req_… a caller quotes to see every row that request wrote, and nothing else.',
            submit: 'Find its rows',
            invalid: 'Write the request id: req_ followed by 26 letters and digits.',
            heading: 'Every row one request wrote',
            banner: 'Every other filter is set aside while you trace a request.',
            requestId: 'Request id',
            traceId: 'Trace id',
            open: 'Open the trace (in a new tab)',
            clear: 'Back to the whole log',
        },
        filters: {
            bar: 'Filter the audit log',
            search: 'Search the log',
            searchPlaceholder: 'Action, or a req_ or ven_ id',
            searchInvalid: 'Search by an action, such as sessions.comp or sessions, or paste a req_ or ven_ id.',
            venueId: 'Venue id',
            action: 'Action',
            actionDescription: 'An action such as sessions.comp, or every action under a prefix: sessions.*',
            actorKind: 'Kind of actor',
            anyActor: 'Anyone',
            actorId: 'Actor id',
            targetType: 'Target type',
            targetTypeDescription: 'The kind of thing the row is about, such as player or setting_value.',
            targetId: 'Target id',
            onBehalfOf: 'Support grant or impersonation',
            onBehalfOfDescription: 'Only what was done under this break-glass grant or impersonation.',
            from: 'From (UTC)',
            fromDescription: 'Rows that happened at or after this time, in UTC.',
            to: 'Before (UTC)',
            toDescription: 'Rows that happened before this time, in UTC.',
            apply: 'Apply filters',
            clear: 'Clear filters',
            clearAll: 'Clear all',
            more: 'More filters',
            moreDescription: 'Narrow the log by where, who, what and when, or trace one request.',
            groups: {
                where: 'Where',
                who: 'Who and what',
                when: 'When',
            },
            range: {
                label: 'Time range — days in {{zone}}',
                today: 'Today',
                yesterday: 'Yesterday',
                last7: '7 days',
                last30: '30 days',
                all: 'All time',
                custom: 'Custom',
            },
            inUse: 'Filters in use',
            chip: {
                venueId: 'Venue: {{value}}',
                action: 'Action: {{value}}',
                actorKind: 'Who: {{value}}',
                actorId: 'Actor: {{value}}',
                targetType: 'About: {{value}}',
                targetId: 'Target: {{value}}',
                onBehalfOf: 'Grant: {{value}}',
                from: 'From {{value}}',
                to: 'Before {{value}}',
                today: 'Today',
                yesterday: 'Yesterday',
                last7: 'Last 7 days',
                last30: 'Last 30 days',
            },
            invalidVenue: 'Write the venue’s id: ven_ followed by 26 letters and digits.',
            invalidInstant: 'Write a date and time.',
            window: 'Make this later than From.',
        },
        actorKind: {
            user: 'Person',
            member: 'Staff member',
            device: 'Device',
            api_key: 'API key',
            system: 'The system',
        },
        actorKindShort: {
            user: 'Person',
            member: 'Staff',
            device: 'Device',
            api_key: 'API key',
            system: 'System',
        },
        onBehalfOf: {
            support_grant: 'Under support grant',
            impersonation: 'Under impersonation',
        },
        onBehalfOfShort: {
            support_grant: 'Support grant',
            impersonation: 'Impersonation',
        },
        column: {
            when: 'When',
            action: 'Action',
            actor: 'Who',
            where: 'Where',
            target: 'About',
        },
        rows: {
            title: 'Rows',
            caption: 'Audit rows, newest first',
            captionTrace: 'The rows request {{id}} wrote, newest first',
            loading: 'Reading the audit log',
            shown_one: '{{count}} row, newest first',
            shown_other: '{{count}} rows, newest first',
            shownMore_one: '{{count}} row so far, newest first',
            shownMore_other: '{{count}} rows so far, newest first',
            empty: 'No rows match these filters',
            emptyDescription: 'Widen the time range or remove a filter.',
            emptyLog: 'Nothing has been recorded yet',
            emptyPlayer: 'Nothing has been recorded about this player yet',
            emptyTrace: 'That request wrote no audit rows.',
            more: 'Show older rows',
            refused: 'The filters were refused:',
            failed: 'The audit log could not be read.',
        },
        detail: {
            eyebrow: 'Audit row',
            when: 'When',
            whenLocal: 'Your time',
            whenUtc: 'UTC',
            recordedAt: 'Recorded',
            who: 'Who',
            actorId: 'Actor id',
            actorIp: 'IP address',
            onBehalfOf: 'Acting under',
            where: 'Where',
            venue: 'Venue',
            about: 'About',
            target: 'Target',
            reason: 'Reason given',
            request: 'Request',
            requestId: 'Request id',
            traceId: 'Trace id',
            rowId: 'Row id',
            noRequest: 'Written outside a request, by a job or the system.',
            showTrace: 'Show everything this request did',
            copy: 'Copy',
            copyNamed: 'Copy the {{name}}',
            copied: 'Copied.',
            names: {
                actorId: 'actor id',
                onBehalfOf: 'grant id',
                venueId: 'venue id',
                targetId: 'target id',
                requestId: 'request id',
                traceId: 'trace id',
                rowId: 'row id',
            },
        },
        changes: {
            summary: 'What changed',
            caption: 'What changed, field by field',
            field: 'Field',
            before: 'Before',
            after: 'After',
            nothing: 'No change was recorded with this row.',
        },
        export: {
            submit: 'Export as CSV',
            done: 'Saved {{file}}.',
            tooMany: 'More than {{max}} rows match. An export is never cut short: narrow the filters, by time, venue, actor or action, and export again.',
            failed: 'The export could not be made.',
        },
        // What a row's action reads as, by its code with the dots written as `__`. A code with no copy here
        // reads as its own words: `shifts.shift.closed` → "Shift closed".
        actions: {
            access__invitation__accept: 'Invitation accepted',
            access__invitation__create: 'Invitation sent',
            access__invitation__revoke: 'Invitation withdrawn',
            access__member__remove: 'Member removed',
            access__member__role__update: 'Member’s role changed',
            access__member__status__update: 'Member’s status changed',
            access__member__venues__update: 'Member’s venues changed',
            access__player__ban: 'Player banned',
            access__player__ban__update: 'Player’s ban changed',
            access__player__unban: 'Player’s ban lifted',
            access__role__create: 'Role created',
            access__role__delete: 'Role deleted',
            access__role__update: 'Role changed',
            access__role__limits__update: 'Role’s limits changed',
            access__shared_device__lock: 'Shared device locked',
            access__shared_device__open: 'Shared device opened',
            access__shared_device__unlock: 'Shared device unlocked',
            access__staff_pin__lock: 'Staff PIN locked',
            access__staff_pin__reset: 'Staff PIN reset',
            access__staff_pin__set: 'Staff PIN set',
            audit__log__export: 'Audit log exported',
            audit__log__read: 'Audit log read',
            catalog__game__launch_recipes__set: 'Launch recipes set',
            catalog__game__refresh: 'Game refreshed from IGDB',
            catalog__library__install_requested: 'App install requested',
            catalog__title__publish: 'Game published',
            commerce__credit__limit_set: 'Credit limit set',
            commerce__credit__settled: 'Account settled',
            commerce__credit__written_off: 'Balance written off',
            commerce__promotion__created: 'Promotion created',
            commerce__promotion__updated: 'Promotion changed',
            commerce__sale__cancelled: 'Sale cancelled',
            commerce__sale__discount_applied: 'Discount applied',
            commerce__sale__refunded: 'Sale refunded',
            identity__user__read: 'Person’s record read',
            orders__discount: 'Order discounted',
            orders__refund: 'Order refunded',
            org__brand__update: 'Brand changed',
            org__setting__update: 'Setting changed',
            org__settings__read: 'Settings read',
            org__venue__archive: 'Venue archived',
            org__venue__create: 'Venue created',
            org__venue__listing: 'Venue’s listing changed',
            org__venue__update: 'Venue changed',
            org__zone__create: 'Zone created',
            org__zone__update: 'Zone changed',
            passes__holding__refunded: 'Pass refunded',
            passes__holding__voided: 'Pass voided',
            passes__kiosk_purchase__applied: 'Kiosk pass added to the session',
            passes__kiosk_purchase__cancelled: 'Kiosk pass purchase cancelled',
            passes__kiosk_purchase__created: 'Pass bought at the kiosk',
            passes__kiosk_purchase__expired: 'Kiosk UPI request expired',
            players__id_check__view: 'Player’s ID check viewed',
            players__note__add: 'Note added to a player',
            players__note__edit: 'Player’s note edited',
            players__note__remove: 'Player’s note removed',
            players__tags__update: 'Player’s tags changed',
            players__update: 'Player’s record changed',
            players__ban: 'Player banned',
            pricing__rate_card__update: 'Rate card changed',
            sessions__comp: 'Time comped',
            kitchen__ticket__advanced: 'Kitchen ticket moved on',
            sessions__call__acknowledged: 'Call for help acknowledged',
            sessions__call__closed: 'Call for help closed',
            sessions__call__opened: 'Call for help raised',
            sessions__call__resolved: 'Call for help resolved',
            sessions__call__updated: 'Call for help updated',
            sessions__charged: 'Session charged',
            sessions__ended: 'Session ended',
            sessions__lock: 'Screen locked',
            sessions__unlock: 'Screen unlocked',
            sessions__message: 'Message sent to the player',
            sessions__sell_time: 'Time sold',
            sessions__session__comped: 'Time comped',
            sessions__session__ended: 'Session ended',
            sessions__session__locked: 'Screen locked',
            sessions__session__unlocked: 'Screen unlocked',
            sessions__session__messaged: 'Message sent to the player',
            sessions__session__sold_time: 'Time sold',
            sessions__pass_added: 'Pass queued onto the session',
            sessions__session__started: 'Session started',
            sessions__session__warned: 'Low-time warning sent',
            sessions__start: 'Session started',
            sessions__started: 'Session started',
            sessions__offline_settle: 'Offline session settled',
            shifts__business_day__closed: 'Business day closed',
            shifts__business_day__signed_off: 'Business day signed off',
            shifts__shift__member_joined: 'Joined a shared drawer',
            shifts__shift__member_left: 'Left a shared drawer',
            shifts__cash_movement__recorded: 'Cash movement recorded',
            shifts__shift__closed: 'Shift closed',
            shifts__shift__opened: 'Shift opened',
            stations__class__create: 'Station class created',
            stations__class__update: 'Station class changed',
            stations__floor_plan__update: 'Floor plan changed',
            stations__station__create: 'Station added',
            stations__station__maintenance: 'Station maintenance changed',
            stations__station__paired: 'Station paired',
            stations__station__power: 'Station powered',
            stations__station__relinked: 'Station re-linked',
            stations__station__setup_skipped: 'Station setup skipped',
            stations__station__update: 'Station changed',
        },
        // What a row is about, by its target type. A type with no copy here reads as its own words.
        targetType: {
            api_key: 'API key',
            audit_log: 'Audit log',
            business_day: 'Business day',
            cash_movement: 'Cash movement',
            device: 'Device',
            floor_plan: 'Floor plan',
            game: 'Game',
            invitation: 'Invitation',
            member: 'Member',
            order: 'Order',
            organization: 'Organisation',
            pass: 'Pass',
            player: 'Player',
            rate_card: 'Rate card',
            role: 'Role',
            sale: 'Sale',
            session: 'Session',
            setting_value: 'Setting',
            shift: 'Shift',
            station: 'Station',
            station_class: 'Station class',
            user: 'Person',
            venue: 'Venue',
            zone: 'Zone',
        },
    },
    chooseVenue: {
        title: 'Choose a venue',
        intro: 'You work at more than one venue. Choose the one to run.',
        noneTitle: 'No venues yet',
        none: 'You do not work at any venue yet. Ask an owner to invite you.',
        // An owner whose organisation has no venue yet (spec/13 §13.1): the way to its first.
        ownerNoneTitle: 'Set up your first venue',
        ownerNone: '{{organisation}} has no venue yet. Setup walks you through creating one and getting it ready to open.',
        ownerNoneAction: 'Start setup',
        unavailable: 'Your venues could not be loaded.',
        organisations: 'Organisations',
        venuesLabel: 'Venues',
        openOrganisation: 'Open organisation',
        venueCount_one: '{{count}} venue',
        venueCount_other: '{{count}} venues',
    },
    notFound: {
        title: 'Not found',
        body: 'There is no such page, or it is not one you can see.',
        home: 'Go to your venues',
    },
    // Floor (spec/13 §3, F-STN-14 … F-STN-18): the venue console's home — the live picture of the room.
    floor: {
        // Arrange mode (F-STN-19, spec/13 §3.4): the floor plan editor.
        arrange: {
            open: 'Arrange',
            title: 'Arrange the floor',
            intro: 'Place stations, draw zones and add fixtures so the floor looks like the room. Save when it is right.',
            notPermitted: 'Your role cannot arrange this venue’s floor.',
            unavailable: 'The floor plan could not be loaded.',
            backToFloor: 'Back to the floor',
            toolbar: 'Arrange tools',
            floorLabel: 'Floor',
            ground: 'Ground floor',
            level: 'Floor {{level}}',
            unused: '(empty)',
            discardConfirm: 'You have unsaved changes on this floor. Discard them?',
            layer: {
                label: 'Edit',
                stations: 'Stations',
                zones: 'Zones',
                fixtures: 'Fixtures',
            },
            columns: 'Columns',
            rows: 'Rows',
            fewer: 'Fewer {{what}}',
            more: 'More {{what}}',
            gridLabel: 'Floor plan, {{columns}} columns by {{rows}} rows',
            panelLabel: 'Arrange panel',
            help: {
                stations: 'Drag a station onto a square; dropping it on another station swaps them. Or select a station and use the arrow keys to move it, Delete to send it back to the tray.',
                zones: 'Pick a zone, then drag across the grid to paint its area. Arrow keys move a drawn zone; Shift and the arrow keys resize it.',
                fixtures: 'Add a fixture, then select it: arrow keys move it, Shift and the arrow keys resize it, Delete removes it.',
            },
            tray: 'Not placed yet',
            trayEmpty: 'Every station is on the floor.',
            elsewhere_one: '{{count}} station is placed on another floor.',
            elsewhere_other: '{{count}} stations are placed on other floors.',
            placeAria: 'Place {{name}} on the floor',
            toTray: 'Take {{name}} off the floor',
            autoLayout: 'Auto-layout in number order',
            autoLayoutOverflow_one: '{{count}} station did not fit and stays in the tray. Make the floor bigger.',
            autoLayoutOverflow_other: '{{count}} stations did not fit and stay in the tray. Make the floor bigger.',
            noRoom: 'There is no free square left. Make the floor bigger or move something.',
            stationAria: '{{name}}, column {{x}}, row {{y}}',
            unknownStation: 'A station that is no longer at this venue',
            zoneAria: 'Zone {{name}}, from column {{x}}, row {{y}}, {{w}} by {{h}}',
            unknownZone: 'Unknown zone',
            noZones: 'This floor has no zones yet. Add zones under Stations → Zones, then draw them here.',
            zonesLoading: 'Loading this venue’s zones…',
            zonesUnavailable: 'The zones could not be loaded.',
            zonesRetry: 'Try again',
            zoneDrawn: 'Drawn',
            zoneNotDrawn: 'Not drawn yet',
            fixtureKind: 'Fixture',
            addFixture: 'Add fixture',
            fixtureCount: '{{count}} of {{max}} fixtures',
            fixtureLabel: 'Label',
            fixtureAria: '{{kind}} {{label}}, from column {{x}}, row {{y}}, {{w}} by {{h}}',
            tooManyFixtures: 'A floor holds at most {{max}} fixtures.',
            problems: 'Fix these before saving',
            save: 'Save floor',
            cancel: 'Discard changes',
            saved: 'Floor saved. Every console now shows the new plan.',
            saveFailed: 'The floor could not be saved. Try again.',
            staleTitle: 'Someone else saved this floor',
            stale: 'Another manager saved this floor after you opened it. Load their plan to carry on from it (your changes here are discarded).',
            loadTheirs: 'Load their plan',
        },
        fixture: {
            counter: 'Counter',
            entrance: 'Entrance',
            lounge: 'Lounge',
            wall: 'Wall',
            pillar: 'Pillar',
            screen: 'Screen',
            window: 'Window',
            restroom: 'Restroom',
            stairs: 'Stairs',
        },
        // The live floor laid out as arranged (F-STN-19).
        room: {
            view: 'Room',
            label: 'The floor as arranged',
            unavailable: 'The floor plan could not be loaded.',
            emptyTitle: 'The floor is not arranged yet',
            empty: 'A manager can place the stations in Arrange so this view matches the room.',
            unplaced_one: 'Not placed: station {{numbers}}.',
            unplaced_other: 'Not placed: stations {{numbers}}.',
        },
        title: 'Floor',
        intro: '{{n}} stations · {{live}} in session',
        live: 'Live',
        liveHint: 'Kept current as stations report in.',
        liveConnecting: 'Connecting',
        liveConnectingHint: 'Reconnecting to live updates — showing the last read.',
        livePaused: 'Paused',
        livePausedHint: 'Live updates are off. The floor refreshes on a timer.',
        liveUnavailable: 'Live updates paused',
        liveUnavailableHint: 'Live updates cannot connect. The floor still refreshes every few seconds; retry, or reload the page.',
        liveRetry: 'Retry',
        liveStatus: 'Floor updates: {{state}}',
        refreshing: 'Refreshing',
        unavailable: 'The floor could not be loaded.',
        notPermitted: 'Your role cannot see this venue’s floor.',
        emptyTitle: 'No stations yet',
        emptyAction: 'Add a station',
        empty: 'Add stations under Stations, then pair each PC. They appear here as they come online.',
        open: 'Open',
        noZone: 'Unplaced',
        figures: {
            label: 'The floor at a glance',
            occupancy: 'In session',
            ofTotal: '/ {{total}}',
            inUse: '{{percent}}% of stations in use',
            takings: 'Takings today',
            businessDay: 'Business day {{day}}',
            nothingTaken: 'Nothing taken yet today',
            unreadable: 'Could not be read just now',
            orders: 'Open food orders',
            ordersHint: 'Waiting in the kitchen',
            overdue: 'Overdue',
            overdueHint: '{{count}} more ending soon',
            overdueAlone: 'Out of funded time',
            free: 'Free now',
            freeHint: 'Ready for a player',
            ending: 'Ending soon',
            endingHint: '10 minutes or less left',
        },
        filter: {
            label: 'Show stations',
            all: 'All',
            free: 'Free',
            session: 'In session',
            ending: 'Ending soon',
            overdue: 'Overdue',
            offline: 'Offline',
            update: 'Update waiting',
            showAll: 'Show all stations',
        },
        filterEmpty: {
            all: 'No stations to show.',
            free: 'No station is free right now.',
            session: 'No one is playing right now.',
            ending: 'No session ends in the next 10 minutes.',
            overdue: 'No session is overdue.',
            offline: 'Every station is online.',
            update: 'Every PC is on the latest Windows.',
        },
        // The floor's search (spec/13 §3): a station by number or name, or the player on it.
        search: {
            label: 'Find a station or player',
            placeholder: 'Station or player',
            empty: 'No station or player matches “{{query}}”.',
        },
        // The list view (spec/13 §3.1): the stations and the sessions on them.
        list: {
            caption: 'The venue’s stations and who is playing on them',
            // Under the time left: until when it plays (the Sessions table's "Plays until").
            until: 'until {{time}}',
        },
        density: {
            label: 'Tile size',
            comfortable: 'Comfortable',
            compact: 'Compact',
        },
        group: {
            count: '{{live}} playing · {{free}} free · {{total}} stations',
        },
        // The words a station's state is told in, on its tile, in the list and in its sheet.
        state: {
            session: 'In session',
            left: '{{time}} left',
            ending: 'Ending soon',
            overdue: 'Overdue',
            locked: 'Screen locked',
            free: 'Free',
            offline: 'Offline',
            maintenance: 'Maintenance',
            disabled: 'Out of order',
            updating: 'Updating',
            // The start-up setup (ADR-0055): the PC installs apps marked for players before it opens for play.
            settingUp: 'Setting up',
            pairing: 'Pairing',
        },
        tile: {
            aria: 'Station {{number}}, {{name}}: {{state}}',
            // One more thing a tile says to a screen reader: who plays on it, then what it runs now (spec/13 §3.1).
            ariaWith: '{{tile}}. {{detail}}',
            numbered: 'Station {{number}}',
            left: 'Time left',
            until: 'Until {{time}}',
            inGrace: 'In grace time',
            ready: 'Ready to play',
            staffRun: 'Run by staff',
            lastSeen: 'Last seen {{time}}',
            // A player here has an open call for help (F-SES-25): the tile's flag, and its name for a screen reader.
            called: 'Help',
            ariaCalled: '{{tile}}. The player has called for help',
            // The health badge (F-STN-11): a station with an open alert.
            healthAria: '{{state}}. Needs attention: {{alerts}}',
            healthBadge: 'Needs attention: {{alerts}}',
            // A PC setting up (ADR-0055): the app it is on of how many, as its screen says it.
            setup: '{{current}} of {{total}}',
        },
        // A station alert's kind, in the badge, "Needs attention" and the station sheet (F-STN-11, spec/10 §6.6).
        alert: {
            tamper_attempt: 'Tamper attempt',
            agent_restarted: 'Station app keeps restarting',
            policy_apply_failed: 'Lockdown did not apply',
            hook_unhealthy: 'Keyboard lock not working',
            guard_down: 'App blocker not running',
            kiosk_not_topmost: 'Lock screen not covering every monitor',
            explorer_unexpected: 'Windows desktop showing',
            disk_low: 'Disk space low',
            temp_high: 'Graphics card running hot',
            webview_missing: 'Kiosk runtime missing',
            webview_crashed: 'Kiosk keeps crashing',
            clock_jump: 'Clock jumped',
            update_rolled_back: 'Update failed',
            offline_in_session: 'Went offline during a session',
            printer_offline: 'Printer not answering',
            sign_in_browser_missing: 'No browser for Google sign-in',
            unknown: 'Health alert',
        },
        // A failed or expired station command, in "Needs attention" (F-STN-15, spec/13 §3.2 item 8).
        commandIssue: {
            failed: '{{command}} failed',
            expired: '{{command}} did not reach the station',
            // A failed command with the station's reason in plain words, by its error code (2026-10-07). The station's own
            // message is technical, so it is not shown here; a code with no words here shows just "{{command}} failed".
            failedWith: '{{command}} failed: {{reason}}',
            reason: {
                unsupported: 'the Clavian app on this PC cannot do this yet. Updating the app may fix it.',
                not_supported: 'the Clavian app on this PC cannot do this yet. Updating the app may fix it.',
                invalid_payload: 'the PC could not make sense of the request.',
                invalid_mode: 'the PC could not make sense of the request.',
                not_paired: 'the PC is not paired yet.',
                power_failed: 'Windows did not carry out the power action.',
                printer_unreachable: 'the printer did not answer.',
                send_failed: 'the PC could not send the wake-up signal to every PC.',
                lease_unavailable: 'the PC could not reach Clavian to fetch what it needed.',
                bundle_unavailable: 'the PC could not reach Clavian to fetch what it needed.',
                jwks_unavailable: 'the PC could not reach Clavian to fetch what it needed.',
                invalid_lease: 'the PC could not confirm the session’s details.',
                command_failed: 'something went wrong on the PC.',
                empty_ack: 'something went wrong on the PC.',
            },
            command: {
                power: 'Power',
                modeSet: 'Mode change',
                lock: 'Lock',
                unlock: 'Unlock',
                end: 'End session',
                message: 'Message',
                policy: 'Policy update',
                library: 'Library update',
                callBanner: 'Call banner',
                other: 'A command',
            },
        },
        // What a station is running now (F-CAT-04, spec/10 §7.3): the library title being played, else the
        // foreground program. On the tile, the station sheet and a running session.
        playing: {
            title: 'Playing {{name}}',
            titleMinutes: 'Playing {{name}} · {{minutes}} min',
            titleHours: 'Playing {{name}} · {{hours}} h {{minutes}} min',
            other: 'Other: {{exe}}',
            label: 'Now playing',
        },
        // The station's raw runtime mode (spec/10 §6.1), for the list and the sheet.
        stationMode: {
            pairing: 'Pairing',
            locked: 'Locked',
            in_session: 'In session',
            session_locked: 'Screen locked',
            maintenance: 'Maintenance',
            updating: 'Updating',
            disabled: 'Disabled',
            offline_locked: 'Locked (offline)',
            setting_up: 'Setting up',
        },
        column: {
            station: 'Station',
            numberShort: 'No. {{number}}',
            status: 'Status',
            player: 'Player',
            timeLeft: 'Time left',
            charged: 'Charged so far',
            // A booked console session (ADR-0034) is charged nothing as it runs: its figure is what is due.
            due: 'due at the end',
            class: 'Class',
            zone: 'Zone',
        },
        // "What needs you" (F-STN-15).
        needsYou: {
            title: 'What needs you',
            clear: 'All clear. Nothing needs you right now.',
            overdue: 'Overdue',
            ending: 'Ending soon',
            free: 'Free now',
            freeCount: '{{count}} free',
            offline: 'Offline',
            outOfOrder: 'Out of order',
            maintenance: 'In maintenance',
            wakeAll: 'Wake all offline',
            wakeAllConfirm: 'Wake all {{count}} offline stations? Each is woken through an online station at the venue.',
            wakingAll: 'Waking {{count}} offline stations.',
            // Section 2, "Needs attention" (spec/13 §3.2): open station alerts and failed or expired commands.
            attention: 'Needs attention',
            attentionAlert: '{{alert}}',
            attentionSince: 'since {{time}}',
            // F-OPS-07: a shift closed with a variance over the owner's alert threshold, until the day is closed.
            shiftVariance: 'Shift variance',
            shiftVarianceItem: '{{counter}}: {{amount}} {{word}}',
            // Offline play (spec/13 §3.6): sessions PCs ran offline that staff must still settle.
            offlinePlay: 'Played offline',
            offlinePlayItem_one: '{{count}} session to settle',
            offlinePlayItem_other: '{{count}} sessions to settle',
            // Stock (spec/13 §6.4): low, out and below-zero items, and dishes stock has sold out.
            stock: 'Stock',
            stockLow: '{{count}} low',
            stockOut: '{{count}} out',
            stockRecount_one: '{{count}} needs a recount',
            stockRecount_other: '{{count}} need a recount',
            stockSoldOut: '{{count}} sold out',
            stockLowState: '{{onHand}} of {{par}}',
            stockNegativeState: 'Recount: {{onHand}}',
            stockMore_one: 'and {{count}} more item',
            stockMore_other: 'and {{count}} more items',
            stockSoldOutItem: 'Sold out: {{names}}',
        },
        // Calls for help (F-SES-25, spec/13 §3.2).
        calls: {
            title: 'Calls for help',
            // Which station called: its name, and who plays on it when the floor knows.
            stationWithPlayer: '{{station}} · {{player}}',
            unknownStation: 'A station not on this floor',
            onMyWay: 'On my way',
            done: 'Done',
            onWay: '{{name}} is on the way',
            justNow: 'just now',
            ago: '{{minutes}} min ago',
            agoHours: '{{hours}} h ago',
            agoDays: '{{days}} d ago',
            category: {
                pc: 'PC problem',
                peripherals: 'Headset or controls',
                food: 'Food and drinks',
                other: 'Something else',
            },
        },
        confirm: {
            restart: 'Restart station {{number}}? Anyone in a session is warned first.',
            shutdown: 'Shut down station {{number}}? Anyone in a session is warned first.',
            yes: 'Confirm',
            cancel: 'Cancel',
        },
        power: {
            restart: 'Restart',
            shutdown: 'Shut down',
            sleep: 'Sleep',
            wake: 'Wake',
        },
        // The station sheet (spec/13 §3.3, F-STN-16).
        drawer: {
            eyebrow: 'Station',
            station: 'Station',
            // The station modal's header, action bar and tabs (spec/13 §3.3): the common actions over every tab,
            // the rest behind More, and the detail in Session · PC · Health.
            header: {
                left: 'left',
                until: 'Until {{time}}',
                played: 'played',
                grace: 'In grace',
                step: 'Other stations',
                previous: 'Previous station',
                next: 'Next station',
            },
            bar: {
                label: 'Station actions',
                // One word per action (2026-10-07): free time is a comp; paid time is "Sell time" (sessions.actions.sell).
                addTime: 'Add free time',
                message: 'Message',
                // Opens a small dialog, hence the ellipsis.
                messageOpen: 'Message…',
                more: 'More actions',
                close: 'Close',
                // ADR-0033: a staff-run station's own actions.
                lock: 'Lock',
                unlock: 'Unlock',
                screen: 'Countdown screen',
            },
            // ADR-0033: a station staff run — no app, the server keeps the time.
            staffRun: {
                fact: 'How it runs',
                value: 'Run by staff — no app',
                hint: 'Staff start, extend and end its sessions here. The timer runs on our servers; you are alerted at 10 and 5 minutes left and when time is up, then the grace period runs and the session ends.',
                locked: 'Locked — {{player}} stepped away',
                lockedToast: 'Session locked.',
                unlockedToast: 'Session unlocked.',
                lockFailed: 'The session could not be locked.',
                title: 'Playing {{title}}',
            },
            // ADR-0033: the countdown screen for a TV or tablet beside a staff-run station.
            screen: {
                title: 'Countdown screen',
                intro: 'Open this link on a TV or tablet beside the station. It shows the time left, the player’s first name and your venue’s logo — nothing else. No app or sign-in needed.',
                none: 'No screen is linked.',
                show: 'Show countdown screen',
                linkedSince: 'Linked {{time}}',
                lastSeen: 'Last opened {{time}}',
                neverSeen: 'Not opened yet',
                scan: 'Scan with the tablet’s camera, or type the link:',
                once: 'This link is shown once. To show it again, make a new one — the old link stops working.',
                copy: 'Copy link',
                copied: 'Link copied.',
                rotate: 'New link',
                revoke: 'Unlink',
                revoked: 'Screen unlinked.',
                failed: 'The countdown screen could not be changed.',
                qrLabel: 'QR code for the countdown screen link',
            },
            tabs: {
                label: 'Station details',
                overview: 'Overview',
                apps: 'Games and apps',
                health: 'Health',
                boot: 'Windows & games',
                alerts_one: '{{count}} open alert',
                alerts_other: '{{count}} open alerts',
            },
            // The tile's quick-actions menu (spec/13 §3.1): the common actions without opening the station.
            quick: {
                label: 'Quick actions for {{name}}',
                open: 'Open station',
                message: 'Message…',
                addTime: 'Add free time…',
                maintenance: 'Maintenance…',
                restart: 'Restart…',
                shutdown: 'Shut down…',
                clearAlerts: 'Clear health warnings…',
            },
            // Health (F-STN-11, F-LCK-08): open alerts, the recent history and the latest reading.
            health: {
                title: 'Alerts and readings',
                healthy: 'Healthy',
                unhealthy: 'Needs attention',
                neverReported: 'This PC has not reported its health yet.',
                open: 'Open alerts',
                recent: 'Recent alerts',
                cleared: 'Cleared {{time}}',
                openedAt: 'Since {{time}}',
                latest: 'Last reading {{time}}',
                cpu: 'CPU',
                ram: 'Memory',
                disk: 'Disk free',
                diskValue: '{{gb}} GB',
                gpu: 'GPU',
                gpuValue: '{{temp}} °C',
                gpuUnknown: 'Not readable',
                percent: '{{value}}%',
                trend: 'Disk free over the last day',
                loading: 'Checking health…',
                failed: 'Could not load this PC’s health.',
                // Clearing warnings automatically or by hand (F-STN-11, 2026-10-02).
                age: 'Open {{age}}',
                snoozedUntil: 'Snoozed until {{time}}',
                snoozedBadge: 'Snoozed',
                clearsWhen: {
                    policy_apply_failed: 'Clears by itself once the whole lockdown is in effect on the PC.',
                    hook_unhealthy: 'Clears by itself once the keyboard lock responds again.',
                    guard_down: 'Clears by itself once the app blocker is running again.',
                    kiosk_not_topmost: 'Clears by itself once the lock screen covers every monitor again.',
                    explorer_unexpected: 'Clears by itself once the Windows desktop is closed.',
                    disk_low: 'Clears by itself once the PC has 11 GB free again.',
                    temp_high: 'Clears by itself once the graphics card cools below 85 °C.',
                    webview_missing: 'Clears by itself once the kiosk runtime is installed.',
                    offline_in_session: 'Clears by itself once the PC is back online or the session ends.',
                    printer_offline: 'Clears by itself once a print job prints.',
                    sign_in_browser_missing: 'Clears by itself once Microsoft Edge or Google Chrome is installed on this PC.',
                    unknown: 'Clears by itself once the PC reports it fixed.',
                },
                // What an alert means, in plain words, under its title (2026-10-07). What the PC itself reported is technical,
                // for support, so it stays folded under "Technical details" (spec/BRAND.md §6.2).
                explain: {
                    tamper_attempt: 'Something on this PC tried to get around the lockdown, such as a blocked tool or an unknown program talking to the Clavian app.',
                    agent_restarted: 'The Clavian app on this PC stopped and restarted several times in a few minutes, which can interrupt the lock screen.',
                    policy_apply_failed: 'Part of the lockdown that keeps players out of Windows settings and tools did not take effect, so this PC is less protected than it should be.',
                    hook_unhealthy: 'The keyboard lock is not working, so shortcuts such as the Windows key or Alt+Tab may reach Windows.',
                    guard_down: 'The app blocker, which stops players opening tools such as Command Prompt or Task Manager, is not running on this PC.',
                    kiosk_not_topmost: 'The lock screen is not covering every monitor, so a player may see or reach the Windows desktop.',
                    explorer_unexpected: 'The Windows desktop and taskbar are open in the player’s account, where they should be hidden.',
                    disk_low: 'This PC is running out of disk space, which can stop games from updating or starting.',
                    temp_high: 'The graphics card is running hotter than it should, which can slow games down or switch the PC off.',
                    webview_missing: 'A Windows component the lock screen needs (Microsoft Edge WebView2) is not installed, so the lock screen cannot open.',
                    webview_crashed: 'The lock screen crashed several times in a few minutes and may not be showing.',
                    clock_jump: 'The PC’s clock jumped forward or back. Session time is not affected, but someone may have changed the clock.',
                    update_rolled_back: 'An update to the Clavian app on this PC failed, so the PC went back to the version it had before.',
                    offline_in_session: 'This PC lost its connection to Clavian while a player was in a session.',
                    printer_offline: 'The printer connected to this PC did not answer a print job. Check that it is on, connected and has paper.',
                    sign_in_browser_missing: 'Install Microsoft Edge or Google Chrome to offer Google sign-in on this PC. Until then players sign in here with their phone or a password.',
                    unknown: 'This PC reported a problem.',
                },
                technicalDetails: 'Technical details',
                technicalDetailsHint: 'What the PC reported, word for word. Clavian support may ask for it.',
                quietPeriod: 'A one-off event: clears by itself after a quiet spell with no repeat.',
                expiresAt: 'A one-off event: clears by itself after a quiet spell, at the latest {{time}} if it does not happen again.',
                clearReason: {
                    resolved: 'Fixed on the PC {{time}}',
                    expired: 'Cleared {{time}}: it did not happen again',
                    staff: 'Cleared by staff {{time}}',
                },
                note: 'Note: {{note}}',
                clear: 'Clear',
                clearOne: 'Clear {{alert}}',
                clearAll: 'Clear all',
                clearForm: {
                    titleOne: 'Clear this warning',
                    titleAll: 'Clear all warnings',
                    hint: 'If the PC still reports it, the warning comes back. Snooze hides it from the floor for a while instead.',
                    note: 'Note (optional)',
                    notePlaceholder: 'What you did, e.g. freed up disk space',
                    snoozeFor: 'Snooze for',
                    hours: '{{n}} h',
                    clear: 'Clear',
                    snooze: 'Snooze',
                    cancel: 'Cancel',
                    cleared_one: 'Warning cleared',
                    cleared_other: '{{count}} warnings cleared',
                    snoozed_one: 'Warning snoozed',
                    snoozed_other: '{{count}} warnings snoozed',
                    nothing: 'Nothing to clear: the warnings had already cleared.',
                    failed: 'Could not clear the warnings.',
                },
                // Whether the PC runs the venue's current settings (F-STN-09): the policy version it reported against the
                // version the server builds for it now.
                settings: {
                    upToDate: 'Settings up to date',
                    upToDateHint: 'This PC runs your current settings.',
                    pending: 'Settings pending',
                    pendingHint: 'A settings change is on its way to this PC. It applies within seconds while the PC is online.',
                    unknown: 'Settings not reported',
                    unknownHint: 'This PC has not reported which settings it runs. It will once it runs the latest station app.',
                },
            },
            // Install detection (F-CAT-03): what the station reports installed from the venue library.
            installed: {
                title: 'Games and apps',
                hint: 'What this PC reports installed from the venue library. Players only see these here; the rest show as on other PCs.',
                summary: '{{n}} of {{total}} installed',
                checkedToday: 'checked today {{time}}',
                checkedOn: 'checked {{when}}',
                neverReported: 'not checked yet',
                filter: 'Show',
                filters: {
                    all: 'All',
                    updating: 'Updating',
                    attention: 'Needs attention',
                },
                filterEmpty: 'Nothing here.',
                here: 'Installed',
                notHere: 'Not on this PC',
                updating: 'Updating {{percent}}%',
                launchers: 'Launchers on this PC: {{launchers}}',
                noLibrary: 'The venue library offers this station nothing yet.',
                loading: 'Checking what is installed…',
                failed: 'Could not load what is installed on this PC.',
                version: 'v{{version}}',
                rescan: 'Rescan',
                rescanSent: 'Asked the PC to rescan. The list updates when it reports.',
                rescanFailed: 'Could not ask the PC to rescan.',
                unverified: 'Unverified',
                unverifiedHint: 'The PC could not check this title, so players still see it here. Rescan, or check it on the PC.',
                // Install one app on this PC now (ADR-0056): it installs as soon as the PC is free, never during a session.
                install: 'Install',
                retry: 'Retry',
                installLabel: 'Install {{app}} on this PC',
                retryLabel: 'Try installing {{app}} on this PC again',
                installSent: '{{app}} installs on this PC as soon as it is free.',
                installFailed: 'Could not ask this PC to install {{app}}.',
            },
            // Wake-on-LAN readiness (F-STN-17): whether staff can switch this PC on from the console.
            wake: {
                title: 'Wake-on-LAN',
                hint: 'Whether Wake from the console can switch this PC on. The station app sets up Windows; the BIOS must allow it too.',
                ready: 'Ready',
                needsAttention: 'Needs attention',
                unknown: 'Not reported',
                neverReported: 'This PC has not reported yet. It reports once it runs the latest station app.',
                checkedAt: 'Checked {{time}}',
                loading: 'Checking Wake-on-LAN…',
                failed: 'Could not load this PC’s Wake-on-LAN state.',
                addresses: 'Wakes by {{macs}}',
                noAddress: 'No network address on record, so Wake cannot reach this PC.',
                adapterReady: '{{name}} ({{mac}}): ready',
                adapterNotReady: '{{name}} ({{mac}}): not armed',
                issue: {
                    fast_startup_on: 'Fast Startup is on, so a shut-down PC will not wake. The station app turns it off; if it stays on, a Windows policy may be setting it.',
                    no_wired_adapter: 'No wired network adapter. Wi-Fi cannot wake a PC.',
                    magic_packet_off: 'Wake on Magic Packet is off on the network adapter; its driver may not offer it.',
                    device_wake_off: 'Windows does not let the network adapter wake the PC.',
                    power_management_off: 'Power management is off for the network adapter, so Windows cannot arm it to wake the PC.',
                    apply_failed: 'The station app could not change a setting. See the station log.',
                },
            },
            kind: 'Kind',
            zone: 'Zone',
            clientVersion: 'Station app',
            lastSeenAgo: 'Last seen {{when}}',
            lastSeenNever: 'Never seen',
            capacity: 'Seats',
            mode: 'Reported mode',
            noMode: 'Not reported yet',
            lastSeen: 'Last seen',
            neverSeen: 'Never',
            freeHint: 'No one is playing here. Sell time or a pass at the counter, or a player signs in at the station.',
            noActions: 'Your role cannot run live-ops on this station.',
            maintenance: 'Maintenance',
            maintenanceHint: 'Takes the station off the floor for a while. It comes back by itself when the time is up.',
            startMaintenance: 'Start maintenance',
            endMaintenance: 'End maintenance',
            duration: 'For how long',
            minutes: '{{n}} min',
            power: 'Power',
            sent: '{{action}} sent.',
            failed: '{{action}} could not be sent.',
            wakeUnavailable: 'Cannot wake it now: no other station is online to relay, or no address is on record.',
            offlineHint: 'Offline. Commands run when it reconnects; Wake goes through another PC that’s online.',
            // Skip a PC's start-up setup (ADR-0055): it opens for play now; what it has not installed installs later.
            skipSetup: {
                action: 'Skip setup',
                title: 'Skip setup on station {{number}}?',
                progress: '{{done}} of {{total}} apps done.',
                body: 'It opens for play now. The apps it has not installed yet install later, while it is idle. An install already running finishes first.',
                send: 'Skip setup',
                cancel: 'Cancel',
                sent: 'Skip setup',
                notSettingUp: 'Station {{number}} is no longer setting up.',
            },
            // Message a player at a station (F-STN-20).
            message: {
                title: 'Message the player',
                hint: 'Show a short line to whoever is at this station — a toast, and an overlay in-game.',
                label: 'Message',
                placeholder: 'e.g. Your food is ready at the counter',
                needsAck: 'Needs acknowledgement',
                send: 'Send',
                required: 'Type the message to send.',
                sent: 'Message sent.',
                failed: 'The message could not be sent.',
                // A message that needs acknowledgement, followed until the player taps Got it or presses the hotkey.
                awaitingRead: 'Waiting for the player to acknowledge it.',
                read: 'The player acknowledged it at {{time}}.',
                // F-SES-20: in a session, the message goes to the session and is on its timeline.
                onTimeline: 'Recorded on the session’s timeline.',
            },
            // The offline code (F-SES-24, spec/05 §8.4).
            offlineCode: {
                title: 'Offline code',
                hint: 'When the venue loses its connection, read this code to the player. They type it at the station.',
                action: 'The code will',
                actions: {
                    start: 'Start',
                    extend: 'Extend',
                    maintenance: 'Maintenance',
                },
                minutes: 'Time it carries',
                generate: 'Generate code',
                codeLabel: 'Offline code {{code}}',
                carries: {
                    start: 'Starts a {{n}} min session',
                    extend: 'Adds {{n}} min to the session',
                    maintenance: 'Unlocks for {{n}} min of maintenance',
                },
                expires: 'Works until {{time}}',
                copy: 'Copy code',
                copied: 'Copied',
                noSecret: 'This station cannot make offline codes yet. Pair it again to give it one.',
                failed: 'The code could not be generated. Try again.',
            },
            // The lockdown test (F-STN-21, spec/13 §12).
            lockdownTest: {
                title: 'Lockdown test',
                hint: 'Asks the PC to check its own lockdown: every escape the locker blocks, and whether each layer is in force right now. Run it on one PC before the rest.',
                run: 'Run lockdown test',
                again: 'Run again',
                never: 'This PC has not been tested yet.',
                pending: 'Waiting for the PC to answer…',
                expired: 'The PC did not answer. It may be off or offline — try again when it is on.',
                passed: 'Passed — {{n}} checks held',
                failing: 'Failed — {{n}} failing',
                ranAt: 'Tested {{time}} while {{mode}}',
                allChecks: 'All checks',
                outcome: {
                    pass: 'Held',
                    fail: 'Failed',
                    not_applicable: 'Not checked here',
                },
                sendFailed: 'The test could not be sent. Try again.',
            },
            // The recovery code for station-cli undo (F-LCK-06, spec/11 §4.3).
            recoveryCode: {
                title: 'Recovery code',
                hint: 'For a PC the lockdown has left unusable. An administrator on that PC runs the undo command with this code; it turns the lockdown off and disables the station until it is repaired.',
                issue: 'Issue recovery code',
                warning: 'This code opens the PC completely. It works once, on this PC only, for 15 minutes.',
                command: 'On the PC, in an administrator command prompt:',
                expires: 'Works until {{time}}',
                copy: 'Copy command',
                copied: 'Copied',
                notPaired: 'This station has no PC paired, so there is nothing to recover.',
                failed: 'The code could not be issued. Try again.',
            },
        },
    },
    stations: {
        title: 'Stations',
        intro: 'The PCs, consoles and other stations at this venue, their classes and the zones they sit in.',
        caption: 'This venue’s stations',
        emptyTitle: 'No stations yet',
        empty: 'Add the first station, then pair its PC with the code it shows.',
        search: 'Search by number, name or kind',
        filterByClass: 'Filter by class',
        unavailable: 'The stations could not be loaded.',
        notPermitted: 'Your role cannot see this venue’s stations.',
        readOnly: 'Read only: your role cannot add or change stations.',
        more: 'Load more',
        noName: 'No name',
        noZone: 'Unplaced',
        column: {
            number: 'Number',
            name: 'Name',
            kind: 'Kind',
            class: 'Class',
            zone: 'Zone',
            capacity: 'Seats',
            pairing: 'Device',
            client: 'Client',
            actions: 'Actions',
        },
        // The station client each PC runs and its automatic update (F-STN-12, spec/10 §8, spec/13 §3): a compact
        // cell in the Stations list and a section in the floor's station drawer.
        client: {
            title: 'Station client',
            hint: 'The Clavian app this PC runs, and whether an update is on its way. Updates install while the PC is idle.',
            loading: 'Checking the station client…',
            failed: 'Could not load this PC’s station client.',
            neverReported: 'This PC has not reported its version yet. It reports once it runs a station app that updates itself.',
            version: 'Version',
            kiosk: 'Kiosk',
            offered: 'Offered',
            channel: 'Channel',
            reported: 'Reported',
            none: 'None',
            notReported: 'Not reported',
            cellLabel: 'Station client of station {{number}}: {{status}}',
            status: {
                unknown: 'Not reported',
                current: 'Up to date',
                available: 'Update available',
                image: 'Update the image',
                checking: 'Checking',
                downloading: 'Downloading',
                downloaded: 'Ready to install',
                installing: 'Installing',
                restarting: 'Restarting',
                rolled_back: 'Rolled back',
                failed: 'Update failed',
            },
            // How the PC starts (2026-10-07): from its own disk, or diskless from an image a server keeps (CCBoot).
            boot: {
                label: 'Starts from',
                local: 'Its own disk',
                diskless: 'Diskless',
                disklessNamed: 'Diskless ({{system}})',
                maintenance: 'Diskless — updating the image',
                maintenanceNamed: 'Diskless ({{system}}) — updating the image',
                playerAccountMissing: {
                    title: 'This PC’s image has no player account',
                    body: 'So the PC cannot sign the player in and shows no Clavian screen. Prepare the image again on the super client: as an administrator, run Clavian.Station.Service.exe --prepare-image, then save the image.',
                },
                recordsLost: 'This PC’s own records start empty each time it restarts.',
            },
            // A diskless PC does not install updates itself: what it installs is lost when it restarts.
            image: {
                movement: 'Update the image to {{version}}',
                movementNamed: 'Update the {{system}} image to {{version}}',
                hint: 'This PC starts from a diskless image, so an update installed on it is lost when it restarts. It does not update itself: update the image, and every diskless PC starts on the new version at its next restart.',
                maintenance: 'This PC is running as the super client that updates the image: what it installs goes into the image. When it is up to date, shut it down and save the image in CCBoot.',
            },
            // "Update the image from this PC": the PC was started as CCBoot super client, so the update goes into the image.
            updateImage: {
                action: 'Update the image',
                actionNamed: 'Update the image from {{name}}',
                confirm: {
                    title: 'Update the image from {{name}}?',
                    description: 'Do this only while {{name}} runs as CCBoot super client — then what it installs goes into the image every diskless PC starts from.',
                    step1: 'In CCBoot, enable super client for this PC and restart it.',
                    step2: 'Press Update the image. The PC downloads and installs the latest Clavian release, and its software restarts for about a minute.',
                    step3: 'When it shows up to date, shut it down and save the image in CCBoot (Disable super client, with Update the image ticked).',
                    step4: 'Restart the other diskless PCs: each starts on the new version.',
                    notSuper: 'If this PC is not running as super client, the update is lost when it restarts.',
                    cancel: 'Cancel',
                    send: 'Update the image',
                },
            },
            // Where an update is, in plain words, from the PC's own report (2026-10-07).
            phase: {
                checking: 'Checking for updates…',
                downloading: 'Downloading update {{version}} — {{percent}}%',
                // With how fast it downloads now, when the PC says (2026-10-07): "… — 45% · 12 MB/s".
                downloadingSpeed: 'Downloading update {{version}} — {{percent}}% · {{speed}}',
                downloadingNoPercent: 'Downloading update {{version}}…',
                verifying: 'Checking the download…',
                ready: 'Update {{version}} is downloaded — it installs when the PC is free',
                installing: 'Installing — the PC restarts in a moment',
                restarting: 'Restarting — the PC is ready in a moment',
                done: 'Up to date ({{version}})',
                failed: 'Update failed: {{detail}}',
                theUpdate: 'the update',
            },
            updatingTo: 'Updating to {{version}}',
            availableVersion: '{{version}} is available',
            rolledBack: 'The update did not come up healthy, so the previous version was put back.',
            failedHint: 'The update failed. The station tries again at its next check.',
            detail: 'Detail: {{detail}}',
            channels: {
                stable: 'Stable',
                beta: 'Beta',
                pinned: 'Pinned',
            },
            // "Update now" (F-STN-12, spec/10 §8.2, 2026-10-07): ask one PC to check for the latest release and install it
            // as soon as it is free — never during a session. Confirmed in a small dialog; then its progress, in words.
            updateNow: {
                action: 'Update now',
                actionNamed: 'Update {{name}} now',
                offlineHint: 'The PC is offline. It checks for updates by itself as soon as it is back.',
                confirm: {
                    title: 'Update {{name}} now?',
                    description: 'The PC installs the latest Clavian release now instead of waiting until it is idle.',
                    check: 'It checks for the latest release and installs it now.',
                    session: 'If a session is running, it installs when the session ends — never during it.',
                    restart: 'The PC’s Clavian software restarts for about a minute while it installs. Nobody can sign in meanwhile.',
                    cancel: 'Cancel',
                    send: 'Update now',
                },
                progress: {
                    sent: 'Asked {{name}} to check for the latest release…',
                    sentOffline: 'Sent. The status below follows the update as the PC reports it.',
                    upToDate: '{{name}} is up to date ({{version}}). Nothing to install.',
                    started: 'Updating {{name}} to {{version}}. Its software restarts for about a minute.',
                    startedNoVersion: 'Updating {{name}} now. Its software restarts for about a minute.',
                    deferredSession: 'A session is running on {{name}}: {{version}} installs as soon as it ends.',
                    deferredBusy: '{{name}} is not free right now: {{version}} installs as soon as it is back to its lock screen.',
                    checking: '{{name}} is checking for the latest release. The status below follows what it finds.',
                    refused: '{{name}} cannot install the update: {{detail}}',
                    checkFailed: '{{name}} could not reach Clavian to check for updates. It tries again by itself.',
                    offline: '{{name}} went offline. It checks for updates by itself as soon as it is back.',
                    unsupported: '{{name}} runs an older Clavian app that cannot update on request. It still updates by itself while it is idle.',
                    failed: 'Update now did not work on {{name}}: {{detail}}',
                    expired: '{{name}} did not answer. It may have gone offline.',
                    theUpdate: 'the update',
                },
                refused: {
                    conflict: '{{name}} cannot take an update now: it is offline or not paired.',
                    forbidden: 'Your role cannot update stations.',
                    failed: 'The update could not be sent. Try again.',
                },
            },
        },
        kind: {
            pc: 'PC',
            console: 'Console',
            sim: 'Sim rig',
            vr: 'VR',
            table: 'Table',
            other: 'Other',
        },
        status: {
            active: 'Active',
            disabled: 'Disabled',
        },
        // A paired device's role (F-STN-17, 2026-10-01): the venue's counter PC running Clavian Counter.
        role: {
            counter: 'Counter',
            counterHint: 'The counter PC running Clavian Counter: it wakes the venue’s PCs over the LAN, and is never on the floor or bookable.',
        },
        // The two installers a PC is set up with (F-STN-01, F-STN-17, 2026-10-01): downloaded and double-clicked, never a
        // command line.
        installers: {
            action: 'Download apps',
            sheetTitle: 'Set up a PC',
            sheetHint: 'Download the installer on the PC and run it. Then pair the PC here with the code it shows.',
            label: 'Installers',
            // The one branded setup, the default for a first install (2026-10-14): the person chooses the kind of PC in it.
            setup: {
                title: 'Clavian setup',
                hint: 'For a gaming PC or the front desk. You choose which during setup. It is a small download that fetches the right installer for the PC.',
                hintCounter: 'Run it on the front-desk PC and choose Front desk PC when it asks. It is a small download that fetches the installer.',
                download: 'Download Clavian setup (.exe)',
            },
            // The MSIs, for IT, imaging and silent installs.
            it: {
                summary: 'For IT: silent install packages (.msi)',
                hint: 'The full installers, for imaging and for PCs with no internet. Most venues only need the setup above, which downloads the one it needs.',
                label: 'Installer packages',
                commands: 'Silent install, from an administrator command prompt:',
            },
            station: {
                title: 'Clavian Station',
                hint: 'For a gaming PC: the kiosk players sign in on, with the PC locked down between sessions.',
                download: 'Download Clavian Station',
            },
            counter: {
                title: 'Clavian Counter',
                hint: 'For the front-desk PC: printers, the cash drawer and Wake-on-LAN. No lockdown — it stays your staff’s own desktop.',
                download: 'Download Clavian Counter',
            },
            version: 'Version {{version}} · {{size}}',
            none: 'No installer is published yet. Contact Clavian support.',
            counterNone: 'The Clavian Counter installer comes with the next Clavian release.',
            unavailable: 'The installers could not be loaded.',
        },
        pairing: {
            paired: 'Paired',
            notPaired: 'Not paired',
            pair: 'Pair device',
            sheetTitle: 'Pair a PC',
            sheetHint: 'Bind the PC at this station, so the floor knows what it reports.',
            stepOne: 'Install Clavian Station on the PC — or Clavian Counter on the front-desk PC — and start it. It shows an 8-character code.',
            stepTwo: 'Type the code here and approve it.',
            formLabel: 'Pair a device to station {{number}}',
            codeLabel: 'Pairing code',
            codeHint: 'The 8-character code shown on the PC’s screen.',
            approve: 'Approve',
            cancel: 'Cancel',
            codeInvalid: 'Enter the 8-character code shown on the PC.',
            codeNotFound: 'That code was not found or has expired. Check the code on the PC and try again.',
            conflict: 'This station is already paired, or that code was already used.',
            refused: 'The device could not be paired. Try again.',
        },
        add: {
            title: 'Add station',
            intro: 'Three short steps: what it is, where it sits, and its PC.',
            stepType: 'Type',
            stepDetails: 'Details',
            stepPair: 'Pair',
            created: 'Station {{number}} added.',
            pairIntro: 'Pair its PC now with the code the PC shows, or pair it later from the list.',
            paired: 'Station {{number}} is paired.',
            pairLater: 'Pair later',
            number: 'Number',
            numberHint: 'A number unique to this venue.',
            name: 'Name',
            nameHint: 'A label beside the number.',
            kind: 'Kind',
            class: 'Class',
            chooseClass: 'Choose a class',
            zone: 'Zone',
            zoneNone: 'Unplaced',
            submit: 'Add station',
            required: 'Fill this in.',
            noClasses: 'Add a station class before adding a station.',
            createClass: 'New class',
            refused: 'The station could not be added. Check the fields and try again.',
            numberTaken: 'Another station at this venue already has that number.',
            // ADR-0033: how the station is run — paired to the station app, or run by staff with no app.
            runBy: 'How it runs',
            runByStaff: 'Run by staff',
            runByStaffHint: 'No app to install. Staff start, extend and end sessions from the floor; the timer runs on our servers and staff are alerted as time runs out.',
            runByApp: 'Clavian Station app',
            runByAppHint: 'A Windows PC with the station app: players sign in on it, it locks itself between sessions.',
            stepReady: 'Ready',
            staffRunCreated: '{{station}} is on the floor.',
            staffRunReady: 'Nothing to pair. Start a session from its tile; open “Countdown screen” there to put a timer on a TV or tablet beside it.',
            done: 'Done',
        },
        // ADR-0033: a station staff run, in the list and the edit sheet.
        staffRun: {
            badge: 'Run by staff',
            hint: 'No app: staff run its sessions from the floor.',
            editLabel: 'Run by staff (no app)',
            editHint: 'Turn this off to pair a Windows PC with the Clavian Station app. A station with a paired device cannot be run by staff until it is unpaired.',
            pairedConflict: 'A device is paired to this station. Unpair it before staff run it.',
        },
        edit: {
            action: 'Edit',
            actionNamed: 'Edit station {{number}}',
            title: 'Edit station',
            intro: 'Change its number, name, class, zone, seats or availability. A station’s kind is fixed.',
            kindFixed: 'Kind is fixed once a station is added.',
            number: 'Number',
            numberHint: 'A number unique to this venue.',
            name: 'Name',
            nameHint: 'A label beside the number.',
            class: 'Class',
            chooseClass: 'Choose a class',
            zone: 'Zone',
            zoneNone: 'Unplaced',
            capacity: 'Seats',
            capacityHint: 'How many players this station seats (1 to {{max}}).',
            status: 'Availability',
            statusActive: 'Active',
            statusDisabled: 'Out of order',
            statusHint: 'An out-of-order station is hidden from the floor and the kiosk.',
            disabledReason: 'Reason',
            disabledReasonHint: 'Shown on the kiosk while the station is out of order.',
            submit: 'Save changes',
            required: 'Fill this in.',
            capacityRange: 'Enter a number of seats from 1 to {{max}}.',
            noChanges: 'Change something first.',
            numberTaken: 'Another station at this venue already has that number.',
            badReference: 'That class or zone is not one of this venue’s.',
            refused: 'The station could not be saved. Check the fields and try again.',
            saved: 'Station {{number}} saved.',
        },
        // Unpair a station's PC and remove a station: the row's menu, the station window's menu and the two confirms.
        removal: {
            moreFor: 'More actions for station {{number}}',
            unpairAction: 'Unpair…',
            removeAction: 'Remove station…',
            unpair: {
                title: 'Unpair {{station}}?',
                body: 'The PC paired to it goes back to its pairing screen, ready to pair again.',
                keeps: '{{station}} keeps its number, settings and history, and waits for a PC to be paired.',
                confirm: 'Unpair',
                done: '{{station}} is unpaired.',
                running: 'A session is running on {{station}}. End it before you unpair it.',
                notPaired: 'No PC is paired to {{station}}.',
                failed: 'The PC could not be unpaired. Try again.',
            },
            remove: {
                title: 'Remove {{station}}?',
                body: '{{station}} disappears from the floor and its number is free again. Its past sessions and sales stay in reports.',
                pcGoesBack: 'The PC goes back to its pairing screen.',
                confirm: 'Remove station',
                done: '{{station}} is removed.',
                running: 'A session is running on {{station}}. End it before you remove it.',
                failed: 'The station could not be removed. Try again.',
            },
        },
        tabs: {
            label: 'Stations, classes and zones',
            stations: 'Stations',
            classes: 'Classes',
            zones: 'Zones',
        },
        pricingMode: {
            per_player: 'Per player',
            per_station: 'Per station',
            per_playerHint: 'Each player on a station of this class pays.',
            per_stationHint: 'The station is priced once, however many play on it.',
        },
        colorToken: {
            // The categorical palette (#411, ADR-0025, re-spaced by ADR-0060): the eight hues the class/zone picker offers.
            'category-1': 'Pink',
            'category-2': 'Azure',
            'category-3': 'Gold',
            'category-4': 'Fern',
            'category-5': 'Teal',
            'category-6': 'Sky',
            'category-7': 'Periwinkle',
            'category-8': 'Orchid',
            // Legacy tokens: no longer offered for a class or zone, but a stored value still shows its name.
            ink: 'Ink',
            muted: 'Silver',
            subtle: 'Grey',
            'line-strong': 'Slate',
            'ok-ink': 'Green',
            'warn-ink': 'Amber',
            'danger-ink': 'Rose',
            'accent-hi': 'Crimson',
            'on-accent': 'White',
            'accent-ink': 'Coral',
            focus: 'Coral red',
            ok: 'Green',
            warn: 'Amber',
            danger: 'Rose',
            'danger-hi': 'Blush',
            'chart-5': 'Pewter',
        },
        classes: {
            intro: 'The price and permission tiers a venue sells by. A station belongs to exactly one class.',
            listLabel: 'Station classes',
            new: 'New class',
            editTitle: 'Edit class',
            tabClass: 'Class',
            edit: 'Edit',
            editNamed: 'Edit {{name}}',
            emptyTitle: 'No classes yet',
            created: 'Class {{name}} created.',
            saved: 'Class {{name}} saved.',
            create: 'Create class',
            save: 'Save class',
            cancel: 'Cancel',
            empty: 'No classes yet. Create the first one to price and sell by it.',
            unavailable: 'The classes could not be loaded.',
            drawerHint: 'How the class prices and seats, with the floor tile a station of it will wear.',
            formLabel: 'Station class details',
            previewLabel: 'Live preview',
            previewName: 'New class',
            seats: 'Seats {{count}}',
            agePreview: 'Ages {{age}}+',
            noAge: 'No age limit',
            field: {
                name: 'Name',
                color: 'Colour',
                pricing: 'Pricing',
                capacity: 'Default capacity',
                age: 'Age limit',
                description: 'Description',
                stations: 'Stations',
            },
            capacityHint: 'How many players a station of this class seats by default (1 to {{max}}).',
            ageHint: 'The youngest a player may be to start here, on top of the venue’s minimum age. Leave empty for none.',
            descriptionHint: 'Shown to players: what this class is and why it costs what it does.',
            error: {
                nameRequired: 'Give the class a name.',
                capacityRange: 'Enter a capacity from 1 to {{max}}.',
                ageRange: 'Enter an age from 1 to {{max}}, or leave it empty.',
                nameTaken: 'Another class at this venue already has that name.',
                refused: 'The class could not be saved. Check the fields and try again.',
            },
        },
        zones: {
            intro: 'The named areas of the floor, used by pricing, reservations and reports. Draw their shapes in arrange mode.',
            new: 'New zone',
            editTitle: 'Edit zone',
            edit: 'Edit',
            editNamed: 'Edit {{name}}',
            emptyTitle: 'No zones yet',
            saved: 'Zone {{name}} saved.',
            count_one: '{{count}} zone',
            count_other: '{{count}} zones',
            create: 'Create zone',
            save: 'Save zone',
            cancel: 'Cancel',
            empty: 'No zones yet. Create one to group the floor by area.',
            unavailable: 'The zones could not be loaded.',
            drawerHint: 'A zone is a name, a colour and the floor it is on.',
            formLabel: 'Zone details',
            floorGround: 'Ground floor',
            floorN: 'Floor {{floor}}',
            floorHint: 'Which level of the venue this zone is on. It is fixed once the zone is created.',
            floorFixed: 'On {{floor}}. A zone’s floor is fixed once it is created.',
            field: {
                name: 'Name',
                color: 'Colour',
                floor: 'Floor',
            },
            error: {
                nameRequired: 'Give the zone a name.',
                conflict: 'Another zone has that name, or this floor already has {{max}} zones.',
                refused: 'The zone could not be saved. Check the fields and try again.',
            },
        },
    },
    // Wallet: a player's closed-loop stored value (spec/13 §8, F-WAL-01/-02/-08).
    // A player's wallet (spec/13 §8, F-WAL-01/-02/-06/-07): run from the player's Wallet tab in Players, and the kiosk's
    // pay-at-the-desk top-ups in the Floor's "What needs you" — the Wallet screen until 2026-10-07.
    wallet: {
        tender: {
            cash: 'Cash',
            counter_card: 'Card (counter)',
            counter_upi: 'UPI (counter)',
            online: 'Online (UPI or card)',
        },
        // The organisation's wallet is off (`wallet.enabled`, off until an owner turns it on): the player's Wallet tab says so,
        // and where it is turned on, instead of offering a Top up that would be refused.
        off: {
            title: 'Wallets are off',
            body: 'Turn wallets on to top up, adjust or refund here. Balances and past movements still show.',
            turnOn: 'Turn on wallets',
            askOwner: 'Someone who manages payments can turn them on in Settings, under Money.',
        },
        // At the organisation, the tab reads only: money changes hands at a venue, which each action names.
        atVenue: 'Top up, Adjust and Refund are at a venue, where the money changes hands. Switch to one of your venues to use them.',
        // The setting a refusal is about, for someone who may change it.
        settingsLink: 'Open wallet settings',
        // Top-ups asked for at a PC, paid at this desk (F-KIO-10): in the Floor's "What needs you".
        kiosk: {
            title: 'Top-ups to take at the desk',
            // "Scan & pay (UPI)" (F-PAY-02, 2026-10-01): paid by the venue's own QR at the PC; UPI is chosen to confirm it.
            scanBadge: 'UPI scan',
            scanReference: 'Ref {{reference}}',
            unnamed: 'A player',
            at: '{{station}} · {{time}}',
            taken: '{{who}}’s top-up is taken.',
            bonus: '+ {{bonus}} bonus',
            tender: 'Paid by',
            take: 'Take payment',
            cancel: 'Cancel',
            // Cancelling closes the request with nothing credited and cannot be undone, so it confirms in place.
            cancelLabel: 'Cancel {{who}}’s top-up',
            cancelConfirm: 'Cancel this top-up? Nothing is credited to {{who}}.',
            keep: 'Keep it',
            cancelConfirmAction: 'Cancel the top-up',
            closed: 'This top-up is already settled.',
            limit: 'This top-up is now outside the wallet’s limits. Cancel it and top up another amount.',
            failed: 'That did not go through. Try again.',
        },
        topUp: {
            action: 'Top up',
            title: 'Top up {{name}}',
            subtitle: 'Add cash to {{name}}’s wallet at the counter, paid by cash, card or UPI.',
            amount: 'Amount ({{currency}})',
            amountInvalid: 'Enter an amount like 500 or 500.00.',
            tender: 'Paid by',
            submit: 'Add to wallet',
            disabled: 'Wallets are off for this organisation, so nothing was added.',
            limit: 'That amount is outside the wallet’s limits. Nothing was added.',
            needsShift: 'Start a shift to take cash, or take this top-up by card or UPI. Nothing was added.',
            taxAtSale: 'Top-ups can’t be taken while wallet money is set to be taxed when it is topped up. Set “When wallet money is taxed” to when it is spent.',
            forbidden: 'Your role can’t top up wallets.',
            refused: 'The top-up was refused.',
            doneBadge: 'Added',
            dialogTitle: 'Top up',
            done: 'Added {{amount}}. New balance {{total}}.',
            close: 'Done',
            onlineHint: 'The player pays with any UPI app. The wallet is credited when the payment provider confirms it.',
            requestOnline: 'Ask for payment',
            onlineDone: 'Paid online. {{amount}} has been added to the wallet.',
        },
        adjust: {
            action: 'Adjust',
            dialogTitle: 'Adjust wallet',
            subtitle: 'Credit or debit {{name}}’s wallet cash, with a reason. Audited.',
            title: 'Adjust {{name}}',
            direction: 'Direction',
            dir: { credit: 'Credit (add cash)', debit: 'Debit (remove cash)' },
            amount: 'Amount ({{currency}})',
            amountInvalid: 'Enter an amount like 500 or 500.00.',
            reason: 'Reason',
            reasonRequired: 'Give a reason for the adjustment.',
            submit: 'Adjust wallet',
            overLimit: 'That is more than your role may adjust in one go.',
            insufficient: 'That debit would take the cash below zero.',
            disabled: 'Wallets are off for this organisation, so nothing was adjusted.',
            forbidden: 'Your role can’t adjust wallets.',
            refused: 'The adjustment was refused.',
            doneBadge: 'Adjusted',
            done: 'Adjustment posted. New balance {{total}}.',
            close: 'Done',
        },
        refund: {
            action: 'Refund',
            dialogTitle: 'Refund wallet',
            subtitle: 'Pay {{name}}’s wallet cash back at the counter, with a reason. Cash only — never the bonus.',
            title: 'Refund {{name}}',
            amount: 'Amount ({{currency}})',
            amountInvalid: 'Enter an amount like 500 or 500.00.',
            tender: 'Refund to',
            reason: 'Reason',
            reasonRequired: 'Give a reason for the refund.',
            submit: 'Refund cash',
            insufficient: 'That is more cash than the wallet holds.',
            disabled: 'Wallets are off for this organisation, so nothing was refunded.',
            off: 'Wallet refunds are turned off for this organisation.',
            forbidden: 'Your role can’t refund wallets.',
            refused: 'The refund was refused.',
            doneBadge: 'Refunded',
            done: 'Refund posted. New balance {{total}}.',
            close: 'Done',
        },
    },
    // Players (spec/13 §8, F-ORG-05/-06, F-IAM-07, F-CMP-02): the customer as the organisation sees them.
    players: {
        title: 'Players',
        intro: 'Open a player for their history, wallet, notes and tags.',
        hero: {
            shown: 'Players listed',
            phone: 'With a phone',
            tagged: 'Tagged',
        },
        notPermitted: 'Your role cannot read this organisation’s players.',
        toolbar: 'Search players',
        unavailable: 'The players could not be loaded.',
        loading: 'Loading players',
        resultsLabel: 'Players',
        caption: 'The organisation’s players',
        more: 'Load more',
        count: '{{count}} players',
        notGiven: 'Not given',
        noTags: 'No tags',
        moreTags: '+{{count}} more',
        contactMaskedNote: 'Contact details are masked unless your role may read them in full.',
        search: {
            label: 'Search players',
            placeholder: 'Name, gamer tag, phone, email or member QR',
        },
        column: {
            player: 'Player',
            phone: 'Phone',
            email: 'Email',
            tags: 'Tags',
            known: 'Known since',
        },
        empty: {
            title: 'No players yet',
            body: 'A player becomes the organisation’s the first time they play or buy at one of its venues. They will appear here then.',
        },
        noMatch: 'No players match “{{query}}”.',
        noMatchHint: 'Try a whole phone number or email, or part of a name or gamer tag.',
        // Players moved from the café's previous software (F-HQ-40), waiting to claim their profile. Read only here.
        tabs: 'Players lists',
        tab: { players: 'Players', moved: 'Moved from {{platform}}', movedMany: 'Moved from earlier systems' },
        moved: {
            intro: 'Players brought over from {{platform}} when the café moved to Clavian. Each profile waits until its player claims it; the money and time shown are held for them until then.',
            search: { label: 'Search moved players', placeholder: 'Old username, name, phone or email' },
            caption: 'Players moved from the previous system',
            loading: 'Loading moved players',
            unavailable: 'The moved players could not be loaded.',
            empty: 'No moved players match “{{query}}”.',
            emptyHint: 'Try the old username, a whole phone number or a whole email.',
            column: { player: 'Player', contact: 'Contact', money: 'Money held', time: 'Time held', status: 'Status', joined: 'Joined', action: 'Claim' },
            gstPaid: 'GST paid',
            gstPaidHint: '{{amount}} of it had GST paid at top-up in the previous system.',
            timeUntil: 'until {{date}}',
            minor: 'Under 18',
            banned: 'Banned',
            noContact: 'No contact',
            status: { unclaimed: 'Waiting to claim', claimed: 'Claimed', undone: 'Undone', erased: 'Removed' },
            waitsUntil: 'Waits until {{date}}',
            more: 'Load more',
            claimButton: 'Claim at the counter',
            claimButtonLabel: 'Claim {{player}} at the counter',
            // Claiming a moved profile at the counter: staff check who the person is, then move the balance.
            claim: {
                title: 'Claim at the counter',
                description: 'Move what {{player}} had in {{platform}} to a Clavian account.',
                waiting: 'Waiting for them',
                money: 'Money',
                time: 'Play time',
                timeUntil: 'valid until {{date}}',
                none: 'Nothing',
                check: 'I checked who they are: their old username or phone, and photo ID if this venue asks for it.',
                minorNote: 'This player is under 18. A parent or guardian must be here, and you record who.',
                guardianName: 'Parent or guardian’s name',
                guardianPresent: 'The parent or guardian is here with them',
                bannedNote: 'The previous system banned this player. Decide whether they can play here before you move anything.',
                to: 'Add it to',
                toPlayer: 'One of your players',
                toEmail: 'An email address',
                playerSearch: 'Find the player',
                email: 'Their email address',
                emailHint: 'They sign in with it. A code goes there; they read it to you.',
                sendCode: 'Send the code',
                resendCode: 'Send it again',
                codeSent: 'Code sent to {{email}}.',
                code: 'The 6-digit code',
                consent: 'The player read and accepted the Terms of Service and the Privacy Policy',
                consentHint: 'Only needed when this email has no Clavian account yet. They accept it themselves.',
                approvalTitle: 'A manager approves this',
                approvalBody: 'This moves more money than staff can move alone. A manager enters their staff PIN.',
                approver: 'Manager',
                approverPlaceholder: 'Choose a manager',
                noApprovers: 'No manager has a staff PIN at this venue yet. A manager sets one under Your account, then approves.',
                pin: 'Their staff PIN',
                submit: 'Move the balance',
                cancel: 'Cancel',
                close: 'Done',
                done: 'Moved {{money}} and {{time}} to their account.',
                doneMoney: 'Moved {{money}} to their account.',
                doneNew: 'Their account is ready. They can sign in at any PC with {{email}}.',
                error: {
                    generic: 'The balance could not be moved. Try again.',
                    guardian: 'Record the parent or guardian who is here.',
                    taken: 'This profile is no longer waiting. Someone claimed it, or it was undone.',
                    player: 'That player could not be found here.',
                },
            },
            // The end of the claim window: the owner's sign-off on writing off what nobody claimed.
            writeOff: {
                title: 'Balances nobody claimed',
                intro: 'A moved profile waits for its player until its claim window ends. Then, the personal details are deleted and the play time lapses. The money stays owed until you write it off here.',
                ended: 'Ended, waiting for your sign-off',
                expiring: 'Ending within 30 days',
                endedTotal: 'Ended: {{amount}}',
                expiringTotal: 'Ending soon: {{amount}}',
                column: { player: 'Player', ends: 'Window ends', money: 'Money', time: 'Time' },
                removed: 'Details deleted',
                none: 'Nothing is ending soon.',
                approve: 'Write off {{amount}}',
                dialogTitle: 'Write off unclaimed balances',
                dialogBody: 'This writes off {{amount}} that {{count}} players never claimed. It is recorded as other income, not as takings or sales. It cannot be undone.',
                confirm: 'I checked these against my records and approve the write-off',
                reason: 'Note (optional)',
                submit: 'Write it off',
                cancel: 'Cancel',
                done: 'Written off: {{amount}}.',
                error: 'The write-off could not be made. Try again.',
            },
            platform: {
                ggleap: 'ggLeap',
                gizmo: 'Gizmo',
                senet: 'Senet',
                icafecloud: 'iCafeCloud',
                pancafe: 'PanCafe Pro',
                smartlaunch: 'SmartLaunch',
                antamedia: 'Antamedia',
                handycafe: 'HandyCafe',
                truecafe: 'TrueCafe',
                other: 'the previous system',
            },
        },
        facts: {
            title: 'Contact',
        },
        drawer: {
            eyebrow: 'Player',
            title: 'Player',
            unavailable: 'This player could not be loaded.',
            knownSince: 'Known to the organisation since {{date}}',
            tabs: 'Player detail',
            tab: {
                overview: 'Overview',
                sessions: 'Sessions',
                games: 'Games',
                orders: 'Orders',
                passes: 'Passes',
                wallet: 'Wallet',
                points: 'Points',
                notes: 'Notes',
                audit: 'Audit',
            },
        },
        // The Games tab (F-CAT-04): what the player played, from the titles the stations detected.
        games: {
            total: 'Played here',
            titles: 'Titles',
            lastPlayed: 'Last played',
            mostPlayed: 'Most played',
            recent: 'Recent plays',
            empty: 'No games played yet. Stations record the titles a player runs during their sessions.',
            sessions_one: '{{count}} session',
            sessions_other: '{{count}} sessions',
            titleLine: '{{sessions}} · last {{date}}',
            playAt: 'PC {{station}} · {{venue}}',
        },
        history: {
            unavailable: 'This could not be loaded.',
            more: 'Load more',
            everyVenue: 'Every venue',
            running: 'In progress',
            ended: 'Ended',
            noSessions: 'No sessions yet.',
            noOrders: 'No orders yet.',
            noPasses: 'No passes yet.',
            noPoints: 'No points yet.',
            noExpiry: 'No expiry',
            expires: 'Expires {{date}}',
            minutesLeft_one: '{{count}} min left',
            minutesLeft_other: '{{count}} min left',
            points_one: '{{count}} pt',
            points_other: '{{count}} pts',
            sessionAt: 'PC {{station}} · {{venue}}',
            channel: { kiosk: 'Kiosk', counter: 'Counter' },
            orderStatus: {
                placed: 'Awaiting payment',
                partially_paid: 'Part paid',
                paid: 'Paid',
                partially_refunded: 'Part refunded',
                refunded: 'Refunded',
                cancelled: 'Cancelled',
                other: 'Other',
            },
            passStatus: { exhausted: 'Used up', expired: 'Expired', refunded: 'Refunded', voided: 'Voided', other: 'Inactive' },
        },
        audit: {
            hint: 'Every change recorded against this player — notes, tags, bans and lifts — newest first. Personal data is masked.',
        },
        tags: {
            title: 'Tags',
            hint: 'The organisation’s own labels for grouping players into lists. Never shown to the player.',
            none: 'No tags on this player.',
            listLabel: 'Tags',
            remove: 'Remove the tag {{tag}}',
            addLabel: 'Add a tag',
            addPlaceholder: 'e.g. regular, tournament',
            add: 'Add tag',
            duplicate: 'This player already has that tag.',
            failed: 'The tags could not be changed.',
        },
        idCheck: {
            title: 'ID check',
            unavailable: 'The ID checks could not be loaded.',
            none: 'No ID check on record for this organisation.',
            verified: 'ID checked',
            method: {
                digital: 'Digital check',
                staff_document: 'Staff document check',
            },
            // Recording a staff document check (F-CMP-02).
            recordAction: 'Record a check',
            recordTitle: 'Record an ID check',
            recordHint: 'Look at the physical ID. Only its type and the last four characters of its number are kept.',
            documentType: 'Document',
            document: {
                aadhaar: 'Aadhaar',
                pan: 'PAN card',
                passport: 'Passport',
                driving_licence: 'Driving licence',
                voter_id: 'Voter ID',
                other: 'Other ID',
            },
            last4: 'Last four characters',
            last4Hint: 'The last four letters or digits of the number, never the whole number.',
            last4Invalid: 'Type exactly the last four letters or digits.',
            photo: 'Photo of the document',
            photoHint: 'Optional. Kept encrypted, and deleted after the retention period.',
            addPhoto: 'Add a photo',
            replacePhoto: 'Choose another photo',
            removePhoto: 'Remove',
            photoType: 'Use a JPEG, PNG or WebP photo.',
            photoTooLarge: 'The photo is over 5 MB. Take a smaller one.',
            photoUnreadable: 'The photo could not be read. Try another.',
            record: 'Record check',
            recorded: 'ID check recorded.',
            recordFailed: 'The check could not be recorded. Try again.',
        },
        ban: {
            title: 'Bans',
            unavailable: 'The bans could not be loaded.',
            none: 'No ban on record.',
            listLabel: 'Active bans',
            banOrg: 'Ban across the organisation',
            banVenue: 'Ban at this venue',
            liftOrg: 'Lift the organisation ban',
            liftVenue: 'Lift the ban at this venue',
            scope: {
                venue: 'This venue',
                organization: 'Organisation',
            },
            noExpiry: 'No expiry',
            until: 'Until {{date}}',
            banOrgHint: 'Stops the player at every venue of the organisation. The reason is internal — never shown to the player.',
            banVenueHint: 'Stops the player at this venue. The reason is internal — never shown to the player.',
            liftHint: 'The ban stays on record; lifting it lets the player back in.',
            reasonLabel: 'Reason',
            reasonHint: 'For the staff and the audit log. The player never sees it.',
            reasonRequired: 'Write a reason.',
            actionFailed: 'That could not be done.',
            confirmBan: 'Ban',
            confirmLift: 'Lift the ban',
        },
        wallet: {
            // Top up, Adjust and Refund, at a venue (F-WAL-02/-06/-07): the Wallet screen's, folded in (2026-10-07).
            actions: 'Wallet actions',
            unavailable: 'The balance could not be loaded.',
            balanceLabel: 'Wallet balance',
            total: 'Total',
            cash: 'Cash',
            bonus: 'Bonus',
            statement: 'Statement',
            statementUnavailable: 'The statement could not be loaded.',
            empty: 'No wallet movements yet.',
            more: 'Load more',
            // "Use my wallet for play time" (F-WAL-04): the player's own choice, made at the kiosk; staff only read it.
            useForPlay: {
                label: 'Wallet for play time',
                on: 'On',
                off: 'Off',
                onHint: 'Pays for play once the player’s paid time and passes run out.',
                offHint: 'Used for play only when the player chooses.',
                playerChanges: 'Only the player can change this, at their PC.',
            },
        },
        // On account: what the player owes the organisation (F-POS-07, spec/06 §8.6).
        credit: {
            title: 'On account',
            hint: 'What the player owes the organisation, across all its venues — from on-account sales and postpaid sessions.',
            unavailable: 'What the player owes could not be loaded.',
            figuresLabel: 'On account',
            owed: 'Owed',
            limit: 'Limit',
            available: 'Available',
            off: 'On account is off for this organisation. Turn it on in the organisation’s settings to let players pay later.',
            offOwed: 'On account is off for this organisation, so nothing new can be put on account. What is already owed can still be collected or written off.',
            collect: 'Collect',
            writeOff: 'Write off',
            setLimit: 'Set limit',
            amount: 'Amount ({{currency}})',
            amountInvalid: 'Enter an amount above zero.',
            limitInvalid: 'Enter a limit of zero or more.',
            moreThanOwed: 'That is more than the player owes.',
            notPermitted: 'Your role cannot do that.',
            failed: 'That could not be done. Try again.',
            tender: {
                label: 'Paid by',
                cash: 'Cash',
                counter_upi: 'Counter UPI',
            },
            reference: 'UPI reference',
            referenceHint: 'The transaction reference on the player’s UPI app, to match it later.',
            collectHint: 'Takes the payment into this venue’s drawer and lowers what the player owes.',
            collectSubmit: 'Collect payment',
            collected: 'Collected {{amount}}. The player now owes {{owed}}.',
            reasonLabel: 'Reason',
            reasonHint: 'For the audit log. Why is this money being forgiven?',
            reasonRequired: 'Write a reason.',
            writeOffHint: 'Forgives money the player owes. The organisation will not collect it.',
            writeOffReview: 'Review write-off',
            writeOffConfirmTitle: 'Write off {{amount}}?',
            writeOffConfirm: 'The player will no longer owe this {{amount}}. This forgives real money and is recorded in the audit log.',
            writeOffSubmit: 'Write off {{amount}}',
            writtenOff: 'Wrote off {{amount}}. The player now owes {{owed}}.',
            limitHint: 'How much this player may owe at once. Zero stops new on-account sales for them.',
            limitSubmit: 'Save limit',
            limitSaved: 'The limit is now {{amount}}.',
        },
        notes: {
            hint: 'The organisation’s own notes about this player. Never shown to the player, and invisible to every other organisation.',
            empty: 'No notes yet.',
            listLabel: 'Notes',
            unknownAuthor: 'A member',
            failed: 'The notes could not be changed.',
            addLabel: 'Add a note',
            addPlaceholder: 'What should the next member know?',
            add: 'Add note',
            readOnly: 'Your role cannot add notes.',
        },
    },
    // Staff and roles (spec/07 §6, F-IAM-08/-10/-11, F-ORG-13).
    staff: {
        title: 'Staff and roles',
        intro: 'Who is on the team, who is invited, and what each role may do.',
        hero: {
            members: 'Members',
            active: 'Active',
            twoFactor: 'With two-factor',
        },
        notPermitted: 'Your role cannot read the organisation’s staff.',
        tabs: 'Staff and roles',
        tab: {
            members: 'Members',
            invitations: 'Invitations',
            roles: 'Roles',
        },
        members: {
            listLabel: 'Members',
            caption: 'The organisation’s members',
            loading: 'Loading members',
            unavailable: 'The members could not be loaded.',
            more: 'Load more',
            allVenues: 'Every venue',
            oneVenue: 'A venue',
            active: 'Active',
            suspended: 'Suspended',
            twoFactorOn: 'On',
            twoFactorOff: 'Off',
            column: {
                name: 'Member',
                role: 'Role',
                venues: 'Venues',
                status: 'Status',
                twoFactor: 'Two-factor',
            },
            empty: {
                title: 'No members yet',
                body: 'Invite someone by phone or email to add them to the staff.',
            },
        },
        invitations: {
            listLabel: 'Invitations',
            loading: 'Loading invitations',
            unavailable: 'The invitations could not be loaded.',
            revoke: 'Revoke',
            revokeConfirm: 'Revoke this invitation?',
            revokeConfirmLabel: 'Revoke the invitation for {{destination}}?',
            revokeYes: 'Revoke',
            revoked: 'The invitation for {{destination}} was revoked.',
            revokeFailed: 'The invitation could not be revoked. Try again.',
            expires: 'Expires {{date}}',
            created: 'Sent {{date}}',
            status: {
                pending: 'Pending',
                accepted: 'Accepted',
                revoked: 'Revoked',
                expired: 'Expired',
            },
            empty: {
                title: 'No invitations',
                body: 'Invite a member to see the invitation and its link here.',
            },
        },
        roles: {
            title: 'Roles',
            intro: 'What each role may do, across every venue. Built-in roles are fixed; custom roles you define.',
            new: 'New role',
            listLabel: 'Roles',
            loading: 'Loading roles',
            unavailable: 'The roles could not be loaded.',
            builtIn: 'Built-in',
            custom: 'Custom',
            permissionCount_one: '{{count}} permission',
            permissionCount_other: '{{count}} permissions',
        },
        invite: {
            action: 'Invite',
            eyebrow: 'Staff',
            title: 'Invite a member',
            subtitle: 'By phone or email. They sign in with that account and accept.',
            // While phone sign-in is turned off.
            subtitleEmail: 'By email. They sign in with that account and accept.',
            channel: 'Invite by',
            phone: 'Phone',
            email: 'Email',
            phoneLabel: 'Phone number',
            phoneHint: 'The number on the account they will sign in with.',
            emailLabel: 'Email address',
            emailHint: 'The address on the account they will sign in with.',
            roleLabel: 'Role',
            roleHint: 'What they may do — you can only grant a role below your own.',
            rolePlaceholder: 'Choose a role',
            venuesLabel: 'Venues',
            everyVenue: 'Every venue',
            chooseVenues: 'Choose venues',
            venuesPick: 'Venues they may work at',
            noVenues: 'No venues to choose from.',
            send: 'Send the invitation',
            destinationRequired: 'Enter a phone number or email.',
            emailRequired: 'Enter an email address.',
            roleRequired: 'Choose a role.',
            venuesRequired: 'Choose at least one venue, or invite to every venue.',
            alreadyMember: 'That person is already a member of the organisation.',
            phoneOff: 'Invitations by phone aren’t available right now. Invite them by email instead.',
            failed: 'The invitation could not be sent.',
            doneTitle: 'Invitation created',
            sent: 'The invitation was sent to {{destination}}.',
            notSent: 'The invitation for {{destination}} was created, but no message went. Pass on the link below.',
            linkTitle: 'Invitation link',
            linkHint: 'The invitee opens this to accept. It works only for the contact it was made for.',
            another: 'Invite another',
            close: 'Done',
        },
        member: {
            eyebrow: 'Member',
            title: 'Member',
            facts: 'Details',
            email: 'Email',
            phone: 'Phone',
            venues: 'Venues',
            joined: 'Joined',
            twoFactor: 'Two-factor',
            role: 'Role',
            roleHint: 'Changing a role changes what this member may do, at every venue.',
            roleLabel: 'Organisation role',
            roleChanged: 'Role changed to {{role}}.',
            roleFailed: 'The role could not be changed.',
            manage: 'Manage',
            suspend: 'Suspend',
            suspendConfirmTitle: 'Suspend this member?',
            suspendConfirmBody: 'Suspend {{name}}. They cannot sign in to work until you reinstate them.',
            suspendConfirm: 'Suspend',
            suspended: 'Member suspended.',
            reinstate: 'Reinstate',
            reinstated: 'Member reinstated.',
            statusFailed: 'That could not be done.',
            resetPin: 'Reset PIN',
            pinReset: 'The PIN was reset. The member sets a new one from their account.',
            pinFailed: 'The PIN could not be reset.',
            remove: 'Remove from staff',
            removeConfirmTitle: 'Remove this member?',
            removeConfirmBody: 'Remove {{name}} from the staff. Their access ends at once. This can be undone by inviting them again.',
            removeConfirm: 'Remove',
            removed: '{{name}} was removed.',
            removeFailed: 'The member could not be removed.',
        },
        // How much a role, or one member in place of their role, may comp (spec/07 §6.4; owner decision 2026-09-30).
        comp: {
            title: 'Comp limit',
            roleHint: 'How much time the holders of this role may give away free. Each member can also have their own.',
            memberHint: 'How much time this member may give away free — their own limit, in place of their role’s.',
            modeRole: 'Follow role',
            modeSetting: 'Venue setting',
            modeCustom: 'Custom',
            modeUnlimited: 'No limit',
            hintRole: 'The {{role}} role’s comp limit applies to this member.',
            hintSetting: 'The venue’s comp settings apply: per comp and per shift.',
            hintCustom: 'The lower of these and the venue’s settings applies, unless they may comp past the settings’ limits.',
            hintUnlimited: 'No limit per comp or per shift. Every comp is still recorded and audited.',
            unlimitedOwnerOnly: 'Only an owner can give comp with no limit.',
            unlimitedBadge: 'Comps without a limit',
            perAction: 'Per comp',
            perShift: 'Per shift',
            setting: 'Setting',
            less: 'Less for {{what}}',
            more: 'More for {{what}}',
            followsRole: 'Follows the {{role}} role',
            settingApplies: 'The venue’s settings',
            customSummary: '{{action}} per comp · {{shift}} per shift',
            unlimited: 'No limit',
            save: 'Save comp limit',
            saved: 'The comp limit was saved.',
            failed: 'The comp limit could not be saved.',
        },
        role: {
            eyebrow: 'Role',
            title: 'Role',
            permissions: 'Permissions',
            permissionsHint: 'What holders of this role may do. A built-in role’s are fixed; a custom role’s are set where it is made.',
            permissionsLabel: 'Permissions',
            noPermissions: 'This role has no permissions.',
            picker: {
                label: 'Permissions to grant',
            },
            create: {
                title: 'New role',
                intro: 'Name the role and pick the permissions it combines. You can change its permissions later.',
                nameLabel: 'Name',
                nameHint: 'Lower case letters, digits and underscores, e.g. floor_lead.',
                nameInvalid: 'Use lower case letters, digits and underscores, starting with a letter.',
                nameTaken: 'A role with that name already exists.',
                permissions: 'Permissions',
                permissionsHint: 'Pick what a holder of this role may do. Grant only what the role needs.',
                selectAtLeastOne: 'Pick at least one permission.',
                submit: 'Create role',
                refused: 'The role could not be created. Check the name and permissions and try again.',
                created: 'Role {{name}} created.',
            },
            edit: {
                action: 'Edit permissions',
                hint: 'Pick the permissions this role grants. The whole set is replaced when you save.',
                selectAtLeastOne: 'A role keeps at least one permission.',
                save: 'Save permissions',
                saved: 'The role’s permissions were saved.',
                refused: 'The permissions could not be saved. Try again.',
            },
            delete: {
                title: 'Delete role',
                hint: 'Remove this custom role. A role a member still holds cannot be deleted.',
                action: 'Delete role',
                confirm: 'Delete',
                deleted: 'Role {{name}} deleted.',
                inUse: 'This role is still held by a member. Move them to another role first.',
                refused: 'The role could not be deleted. Try again.',
            },
            limits: {
                title: 'Limits',
                hint: 'The most a holder may do with a permission. Blank is no limit of the role’s own — the setting alone applies, and the lower of the two wins.',
                blankMeans: 'Leave a field blank for no limit.',
                compAction: 'Comp per action (minutes)',
                compShift: 'Comp per shift (minutes)',
                discount: 'Manual discount',
                discountPercent: 'Manual discount (%)',
                refund: 'Refund without approval',
                refundAmount: 'Refund without approval ({{currency}})',
                wallet: 'Wallet adjustment',
                walletAmount: 'Wallet adjustment ({{currency}})',
                noLimit: 'No limit',
                minutes: '{{count}} min',
                badMinutes: 'Enter whole minutes, or leave blank.',
                badPercent: 'Enter a percentage between 0 and 100, or leave blank.',
                badAmount: 'Enter an amount like 500 or 500.00, or leave blank.',
                saved: 'The role’s limits were saved.',
                failed: 'The limits could not be saved.',
                save: 'Save limits',
            },
        },
    },
    // Sell: the point of sale (spec/13 §4, F-POS-09/-01/-12).
    sell: {
        // A B2B buyer on a sale (F-CMP-07): an out-of-state GSTIN is charged IGST instead of CGST + SGST.
        buyer: {
            add: 'Bill to a business (GSTIN)',
            title: 'Business buyer',
            gstin: 'Buyer GSTIN',
            gstinHint: '15 characters. A GSTIN from another state is charged IGST; the total does not change.',
            name: 'Business name',
            save: 'Bill to this business',
            cancel: 'Cancel',
            invalid: 'Enter a valid 15-character GSTIN and the business name.',
            refused: 'The buyer could not be set. It must be set before the first payment.',
            billedTo: 'Billed to {{name}} · GSTIN {{gstin}}',
            igst: 'IGST (inter-state)',
        },
        title: 'Sell',
        intro: 'Tap items into the cart, choose a station, and charge with cash or counter UPI.',
        categories: 'Categories',
        allItems: 'All items',
        // The shelves (spec/13 §4): everything, the menu, the counter's passes. Inside the menu, categories are chips.
        shelf: {
            label: 'What to sell',
            all: 'All',
            food: 'Food & drinks',
            passes: 'Passes',
        },
        allCategories: 'All categories',
        otherCategory: 'Other',
        search: 'Search items',
        noProductsTitle: 'Nothing on the menu yet',
        soldOutNamed: '{{name}}, sold out',
        // A product the menu does not sell on the Counter channel (spec/13 §6.1): shown, not sold here.
        notAtCounter: 'Not at the counter',
        notAtCounterNamed: '{{name}}, not sold at the counter',
        // Outside its availability schedule, in the venue's time (F-FNB-02): listed, not yet orderable.
        notNow: 'Not available now',
        notNowNamed: '{{name}}, not available at this time',
        // The modifiers dialog (F-FNB-01): a product with modifier groups is chosen before it joins the cart.
        modifiers: {
            eyebrow: 'Choose options',
            label: 'Options for {{name}}',
            priced: '{{name}} (+{{price}})',
            required_one: 'Choose {{count}}',
            required_other: 'Choose {{count}}',
            upTo_one: 'Optional · up to {{count}}',
            upTo_other: 'Optional · up to {{count}}',
            total: 'This line: {{price}} each',
            choose: 'Choose from {{group}} to add it',
            add: 'Add to cart',
        },
        inCart: '{{count}} in cart',
        // The count badge on a card once it is in the cart.
        inCartShort: '×{{count}}',
        readOnly: 'Read only: your role cannot take a sale.',
        notPermitted: 'Your role cannot sell at this venue.',
        unavailable: 'The menu could not be loaded.',
        noProducts: 'Add products under Kitchen, Menu setup. Until then, sell anything as a custom item.',
        productsLabel: 'Products',
        add: 'Add {{name}}',
        kind: {
            time: 'Time',
            retail: 'Retail',
            other: 'Other',
        },
        tender: {
            cash: 'Cash',
            counter_card: 'Card',
            counter_upi: 'Counter UPI',
            wallet: 'Wallet',
            on_account: 'On account',
            online: 'Online (UPI or card)',
        },
        // The payment method as checkout's segmented control names it: short, so every method fits on one row.
        tenderShort: {
            cash: 'Cash',
            counter_card: 'Card',
            counter_upi: 'UPI',
            wallet: 'Wallet',
            on_account: 'Account',
            online: 'Online (UPI or card)',
        },
        cart: {
            label: 'Cart',
            title: 'Cart',
            empty: 'The cart is empty. Tap an item to start a sale.',
            clear: 'Clear',
            items_one: '{{count}} item',
            items_other: '{{count}} items',
            each: '{{price}} each',
            estimated: 'Estimated total',
            // The cart quoted before it is placed (F-POS-03): its subtotal, each promotion taken off, the total.
            summary: 'Cart total',
            subtotal: 'Subtotal',
            total: 'Total',
            code: 'Code {{code}}',
            deliverTo: 'Deliver to station',
            noStation: 'No station',
            toStation: 'To PC {{number}}',
            // The player the sale is for (spec/06 §8.3 step 1): optional, and needed for Wallet and On account.
            player: 'Player',
            playerHint: 'Name, username or phone',
            playerNone: 'No player: anonymous sale. Attach one to pay by wallet or on account.',
            playerChange: 'Change',
            playerRemove: 'Remove player {{name}}',
            less: 'One fewer {{name}}',
            more: 'One more {{name}}',
        },
        // Passes on the Sell screen (F-POS-01, F-PRC-01): a pass is a line on the sale, issued once it is paid.
        passes: {
            tab: 'Passes',
            label: 'Passes',
            noneTitle: 'No passes at the counter',
            none: 'No pass is sold at the counter right now. Under Pricing, Passes, a pass sold on the Counter channel shows here.',
            add: 'Add pass {{name}}, {{price}}',
            hours_one: '{{count}} h',
            hours_other: '{{count}} h',
            limitDay_one: 'max {{count}} a day',
            limitDay_other: 'max {{count}} a day',
            limitTotal_one: 'max {{count}} per player',
            limitTotal_other: 'max {{count}} per player',
            membersOnly: 'Members only',
            membersOnlyNamed: '{{name}}, members only: not sold at the counter yet',
            lineDetail: 'Pass · {{duration}}',
            // A pass card's times of day ("10 pm–6 am"), and its hint that it is sold to a player.
            window: '{{from}}–{{to}}',
            moreWindows_one: '{{span}}, +{{count}} more',
            moreWindows_other: '{{span}}, +{{count}} more',
            needsPlayerShort: 'Needs a player',
            remove: 'Remove {{name}}',
            needsPlayer: 'A pass belongs to a player. Attach the player below to charge.',
            preloadUnavailable: 'That pass is not sold at the counter right now.',
            // Guest passes: sold to a walk-in with no account, each with a ticket code to type on a PC.
            guestLineDetail: 'Guest pass · {{duration}}',
            guest: {
                label: 'Sell as guest passes',
                hint: 'For someone without an account: each pass gets a ticket code to type on a PC.',
                on: 'Each guest pass gets a ticket code once it is paid. Take cash, card or UPI.',
            },
        },
        charge: {
            button: 'Charge {{amount}}',
            unavailable: 'An item is sold out or not available at this venue. Remove it and try again.',
            refused: 'The sale could not be placed. Check the cart and try again.',
            // F-SES-17 rule 3: the price moved between the quote and the charge; nothing was placed.
            priceChanged: 'The price changed — the total is now {{amount}}. Check the cart and charge again.',
            priceChangedPlain: 'The price changed since the cart was priced. Check the cart and charge again.',
        },
        // F-SES-17: a pass sold into the attached player's running session.
        intoSession: {
            thisPc: 'their PC',
            adds: 'Adds to the current session on {{station}} — it starts funding as soon as it is paid.',
            notUsable: 'Not usable on {{station}}: {{names}}. It is sold, and waits on the player’s account for a PC it covers.',
        },
        // F-POS-10: the pending kiosk and app orders at the top of Sell.
        awaiting: {
            title: 'Awaiting payment',
            order: 'Order',
            numbered: 'Order #{{number}}',
            kiosk: 'Kiosk',
            app: 'App',
            station: 'PC {{number}}',
            take: 'Take payment',
            reject: 'Reject',
            confirmReject: 'Reject order',
            rejected: 'Order rejected.',
            rejectPaid: 'A payment has already settled on this order, so it cannot be rejected. Refund it from Orders.',
            rejectFailed: 'The order could not be rejected. Try again.',
            // "Scan & pay (UPI)" (F-PAY-02, 2026-10-01): the player paid by scanning the venue's own QR at their PC, with this
            // reference in the payment's note. Take payment opens with UPI chosen.
            scanBadge: 'UPI scan',
            scanReference: 'Ref {{reference}}',
            scanHint: 'Paid by scanning the venue’s UPI QR at the PC. Match the reference in your UPI app, then confirm with UPI.',
            // ADR-0034: the one order a staff-run session placed when it ended.
            consoleSession: 'Console session',
        },
        custom: {
            title: 'Custom item',
            hint: 'Time or anything off the menu',
            sheetHint: 'A line that is not on the menu, at the price you set.',
            kind: 'Kind',
            name: 'Name',
            price: 'Price ({{currency}})',
            submit: 'Add to cart',
            required: 'Fill this in.',
            priceInvalid: 'Enter a price like 150 or 150.50.',
        },
        // A discount code (F-POS-02) and a coupon for the sale's player (F-POS-06), at checkout.
        codes: {
            addCode: 'Add a discount code',
            redeemCoupon: 'Redeem a coupon',
            codeLabel: 'Discount code',
            couponLabel: 'Coupon code',
            apply: 'Apply',
            redeem: 'Redeem',
            required: 'Type the code first.',
            unknown: 'No such code in this organisation.',
            refused: 'That code cannot be used on this sale: it may have ended, be used up, or need a bigger order.',
            couponUnknown: 'No such coupon.',
            couponRefused: 'That coupon cannot be redeemed: it may be used up, ended, not valid here, or already redeemed by this player.',
            applied: '{{code}} applied: {{amount}} off.',
            granted: '{{amount}} added to {{name}}’s wallet.',
        },
        pay: {
            paidToast: 'Payment taken.',
            title: 'Checkout',
            summary: 'Amount due',
            stillToPay: 'Still to pay',
            breakdown: 'Subtotal {{subtotal}} · GST {{gst}}',
            discountShort: 'Discount {{amount}}',
            paidOf: 'Paid {{paid}} of {{total}}',
            takeCash: 'Take {{amount}} in cash',
            tenderedShort: 'That is less than the amount. Enter what the customer handed over.',
            split: 'Split payment',
            splitHint: 'Take part of {{amount}} now and the rest another way.',
            // The change from a cash overpayment: back in cash, or into a player's wallet (ADR-0036, ADR-0049).
            changeDue: 'Change',
            changeBackInCash: 'Give {{amount}} back in cash',
            changeToNamedWallet: 'Add {{amount}} to {{name}}’s wallet',
            changeToAWallet: 'Add {{amount}} to a player’s wallet',
            changeWalletOff: 'Adding change to a wallet needs the wallet turned on.',
            changeWalletTaxedAtSale: 'Adding change to a wallet needs top-ups taxed when they are spent.',
            changeWalletSettings: 'Turn on in Settings',
            changeGoesTo: 'The change goes to {{name}}’s wallet',
            changeFindPlayer: 'Whose wallet?',
            changeNoPlayer: 'No player matches. Try a name, phone number or username.',
            changePickPlayer: 'Pick the player whose wallet takes the change, or give it back in cash.',
            subtitle: 'Take payment — split it across cash, card or UPI if needed.',
            tenderLabel: 'Take a payment',
            subtotal: 'Subtotal',
            gst: 'GST',
            discount: 'Discount',
            total: 'Total',
            paid: 'Paid',
            outstanding: 'Outstanding',
            tender: 'Payment method',
            amount: 'Amount ({{currency}})',
            tendered: 'Cash received ({{currency}})',
            tenderedHint: 'What the player handed over, to show the change.',
            // Quick tender: the amount exactly, then round notes; and the change to hand back, as typed.
            quickLabel: 'Quick cash amounts',
            exact: 'Exact',
            giveBack: 'Give back',
            reference: 'UPI reference',
            referenceHint: 'An optional reference from the UPI app.',
            // The venue's own card machine (F-PAY-02): never a card number, only the last 4 digits or the slip's reference.
            cardReference: 'Last 4 digits or slip reference',
            cardReferenceHint: 'Optional. Never the whole card number.',
            confirmCard: 'Card approved: record it',
            // The venue's own UPI QR (F-PAY-02): the player scans it; staff record it once their app shows it approved.
            upiNote: 'Payment to {{venue}}',
            upiQrLabel: 'UPI QR for {{amount}}',
            upiTo: 'To {{vpa}}',
            upiConfirmHint: 'Record it only once the player’s UPI app shows the payment approved.',
            confirmUpi: 'Payment received: record it',
            take: 'Take payment',
            conflict: 'That amount is more than is outstanding, or the sale is already paid.',
            refused: 'The payment could not be taken. Try again.',
            amountInvalid: 'Enter an amount like 150 or 150.50.',
            paidBadge: 'Paid',
            change: 'Change due {{amount}}.',
            newSale: 'New sale',
            // The guest pass's ticket code, shown big once the sale is paid.
            guestTicket: {
                title_one: 'Guest ticket',
                title_other: 'Guest tickets',
                code: 'Ticket code for {{pass}}',
                hint: 'Type it on a PC under “I have a ticket”.',
                until: 'Works until {{time}}',
                scan: 'Scan to keep this time in an account',
                loading: 'Getting the ticket code…',
                unavailable: 'The ticket code could not be loaded. Find it under Floor, Guests.',
            },
            paidNoChange: 'No change due.',
            // The change from a cash overpayment can go into the player's wallet (F-POS-12, ADR-0036).
            changeLabel: 'The change',
            changeInCash: 'Give back in cash',
            changeToWallet: 'Add to {{name}}’s wallet',
            addedToWallet: 'Added to the wallet',
            changeToWalletDone: '{{amount}} added to {{name}}’s wallet.',
            changeToWalletNeedsPlayer: 'To put the change in a wallet, add the player to the sale first (in the cart).',
            changeToWalletUnavailable: 'The change cannot go to the wallet here. Hand it back in cash.',
            changeToWalletOverMax: 'That would take the wallet over its limit. Hand the change back in cash.',
            // The player's own tenders (F-POS-07, F-WAL-04): the wallet and on account, only with a player.
            forPlayer: 'For {{name}}',
            walletBalance: 'Wallet balance {{amount}}',
            walletBalanceLoading: 'Reading the wallet balance…',
            onAccountHint: 'Charged to what {{name}} owes. The amount defaults to the outstanding.',
            takeOnAccount: 'Charge to account',
            walletShort: 'The wallet does not hold enough for that. Take less from the wallet and the rest another way.',
            walletOff: 'The wallet is not turned on for this organisation.',
            creditOff: 'On account is not turned on for this organisation.',
            overCreditLimit: 'That would take the player over their credit limit. Charge less to the account and take the rest another way.',
            // The online tender (F-PAY-03): a payment request the player pays through the provider.
            onlineHint: 'The player pays this amount with any UPI app. The sale records it when the payment provider confirms it.',
            requestOnline: 'Ask for payment',
        },
    },
    // The live kitchen display (spec/13 §6.2/§6.3, F-FNB-04/-05/-06). Plain, staff-side voice; a control
    // says exactly what it does (Accept, Ready, Deliver, Recall).
    kitchen: {
        title: 'Kitchen',
        intro: 'Live tickets, oldest first. Bump each one on as it moves: accept, preparing, ready, delivered.',
        menuSetup: 'Menu setup',
        readOnly: 'Read only: your role cannot move tickets.',
        notPermitted: 'Your role cannot see this venue’s kitchen.',
        unavailable: 'The kitchen tickets could not be loaded.',
        boardLabel: 'Kitchen tickets by state',
        columnEmpty: 'Nothing here',
        recallHint: 'The last 10 delivered. Recall one to put it back on Ready.',
        // Whether the order is paid (spec/13 §6.2 rule 3): a counter order may be made before it is paid.
        paid: 'Paid',
        unpaid: 'Not paid',
        age: {
            fresh: '{{minutes}} min',
            waiting: 'Waiting {{minutes}} min',
            late: 'Late · {{minutes}} min',
        },
        counter: 'Counter',
        station: 'Station',
        pc: 'PC {{number}}',
        deliverTo: 'To {{destination}}',
        deliverToCounter: 'To the counter',
        deliverToPc: 'To a PC',
        minutes: '{{minutes}}m',
        waited: 'Waiting {{minutes}} minutes',
        print: 'Print',
        recall: 'Recall',
        recallLabel: 'Recently delivered, for recall',
        recallTitle: 'Recently delivered',
        state: {
            new: 'New',
            accepted: 'Accepted',
            preparing: 'Preparing',
            ready: 'Ready',
        },
        area: {
            kitchen: 'Kitchen',
            bar: 'Bar',
        },
        // The bump button names the state it moves the ticket to (spec/13 §6.2 rule 1, F-FNB-04).
        advance: {
            new: 'Accept',
            accepted: 'Preparing',
            preparing: 'Ready',
            ready: 'Delivered',
        },
        // Cancel a food order from its ticket (F-POS-15).
        cancel: {
            open: 'Cancel order',
            openFor: 'Cancel order {{order}}',
            eyebrow: 'Order',
            title: 'Cancel order {{order}}',
            lines: 'In this order',
            total: 'Total',
            channel: 'Placed at',
            channels: {
                counter: 'The counter',
                kiosk: 'A station',
                app: 'The app',
                web: 'The web',
                api: 'An integration',
            },
            action: 'Cancel order',
            keep: 'Keep it',
            confirm: 'Cancel this order? Every ticket for it leaves the kitchen, and it cannot be undone.',
            done: 'Order cancelled.',
            settled: 'This order is paid, so it cannot be cancelled here. Refund it from Orders.',
            alreadyCancelled: 'This order is already cancelled.',
            conflict: 'This order was paid or cancelled in the meantime.',
            failed: 'The order could not be cancelled. Try again.',
            unavailable: 'The order could not be loaded.',
        },
    },
    // The Orders surface (spec/13 §5, F-POS-15): the sales list, the order drawer, and its actions —
    // take payment, apply a code, refund, approve a refund, and print or resend the receipt.
    // An online payment in progress (F-PAY-03, spec/06 §9.3): what the player pays, how, and what the
    // payment provider has said so far. Shared by Sell's checkout and the wallet's top-up.
    // Stock (spec/13 §6.4): the Stock section, its dialogs, the counting page, the product dialog's Stock used tab and
    // the stock reports. Plain words for a café: "Food & drink", "Supplies", "Recount needed".
    stock: {
        title: 'Stock',
        intro: 'What is on your shelves: food and drink, retail items and supplies. Sales take stock away by recipe.',
        introOff: 'Stock tracking is off at this venue.',
        loading: 'Loading stock…',
        unavailable: 'Stock could not be loaded.',
        notPermitted: 'Your role cannot see stock at this venue.',
        tabs: {
            label: 'Stock',
            items: 'Items',
            counts: 'Counts',
            deliveries: 'Deliveries',
            suppliers: 'Suppliers',
            reports: 'Reports',
        },
        actions: {
            receive: 'Receive delivery',
            count: 'Count stock',
            usage: 'Record usage',
        },
        off: {
            title: 'Track stock at {{venue}}',
            lead: 'Know what is on the shelf, what each dish costs to make, and what is running low, without a spreadsheet.',
            points: {
                recipes: 'Each sale takes its ingredients away by recipe, and retail items one for one.',
                alerts: 'Low and out-of-stock items show in What needs you, and a dish you cannot make is marked sold out.',
                deliveries: 'Deliveries record what you paid, so every item has an average cost.',
                margins: 'See what your stock is worth, what was wasted, and the margin on every dish.',
            },
            firstStep: 'Turning it on starts every item at zero. The first thing to do after is a full count of what is on the shelf.',
            turnOn: 'Turn on in Settings',
            askManager: 'An owner or a manager can turn it on in Settings, under Food & orders.',
        },
        firstCount: {
            title: 'Count everything once to start',
            body: 'Until the first full count, stock reads zero and every sale asks for a recount.',
            action: 'Start a full count',
        },
        kpi: {
            label: 'Stock at a glance',
            value: 'Stock value',
            valueHint: 'At each item’s average cost',
            valueUncosted_one: '{{count}} item has no cost yet',
            valueUncosted_other: '{{count}} items have no cost yet',
            low: 'Low',
            lowHint: 'Below their par level',
            attention: 'Out or below zero',
            attentionHint: '{{out}} out, {{negative}} to recount',
            soldOut: 'Dishes sold out',
            soldOutHint: 'Not enough stock to make one',
            soldOutOff: 'Stock does not mark dishes sold out here',
            lastCount: 'Last full count',
            lastCountHint: 'Count monthly to keep stock honest',
            neverCounted: 'Not counted yet',
        },
        kind: {
            ingredient: 'Food & drink',
            retail: 'Retail',
            supply: 'Supply',
        },
        kindPlural: {
            ingredient: 'Food & drink',
            retail: 'Retail',
            supply: 'Supplies',
        },
        level: {
            ok: 'In stock',
            low: 'Low',
            out: 'Out',
            negative: 'Recount needed',
        },
        baseUnit: {
            each: 'Pieces',
            g: 'Weight (g)',
            ml: 'Volume (ml)',
        },
        unit: {
            each: '{{value}} each',
            named: '{{value}} {{name}}',
            g: '{{value}} g',
            kg: '{{value}} kg',
            ml: '{{value}} ml',
            l: '{{value}} L',
        },
        unitName: {
            each: 'each',
            g: 'g',
            kg: 'kg',
            ml: 'ml',
            l: 'L',
        },
        unitCost: {
            each: '{{price}} each',
            named: '{{price}} per {{name}}',
            g: '{{price}}/g',
            kg: '{{price}}/kg',
            ml: '{{price}}/ml',
            l: '{{price}}/L',
        },
        movement: {
            sale: 'Sold',
            refund: 'Refunded',
            receive: 'Received',
            waste: 'Wasted',
            staff_meal: 'Staff meal',
            issue: 'Issued',
            count: 'Counted',
            adjust: 'Corrected',
            transfer: 'Moved',
        },
        wasteReason: {
            expired: 'Expired',
            damaged: 'Damaged',
            spilled: 'Spilled',
            quality: 'Not good enough to serve',
            other: 'Other',
        },
        column: {
            item: 'Item',
            onHand: 'On hand',
            status: 'Status',
            par: 'Par level',
            averageCost: 'Average cost',
            value: 'Value',
            lastMovement: 'Last movement',
        },
        filter: {
            level: 'Show',
            all: 'All',
            low: 'Low',
            out: 'Out',
            negative: 'Recount needed',
            kind: 'Kind',
            allKinds: 'All kinds',
            archived: 'Show archived',
        },
        items: {
            toolbar: 'Find items',
            search: 'Search by name, SKU or barcode',
            searchPlaceholder: 'Search or scan a barcode…',
            caption: 'Stock items',
            listName: 'the stock list',
            new: 'New item',
            open: 'Open',
            edit: 'Edit item',
            adjust: 'Correct on hand',
            countThis: 'Count this item',
            archived: 'Archived',
            neverMoved: 'No movement yet',
            emptyTitle: 'No stock items yet',
            empty: 'Add the ingredients and supplies you keep, or copy them from another venue. Retail items appear by themselves for each retail product.',
            filterEmpty: 'No item matches this filter.',
        },
        picker: {
            placeholder: 'Type a name, or scan a barcode…',
            results: 'Matching items',
            noMatch: 'No item matches. Add it on the Items tab first.',
            unknownBarcode: 'No item here has the barcode {{barcode}}.',
            lookupFailed: 'That barcode could not be looked up. Try again.',
            already: '{{name}} is already on the list.',
            notAllowed: '{{name}} is not a supply, so it cannot be picked here.',
        },
        quantity: {
            groupLabel: 'Quantity of {{name}}',
            packSize: '({{size}})',
            packsOf: 'Number of {{pack}} of {{name}}',
            amount: 'Amount',
            loose: 'Loose',
            amountOf: 'Amount of {{name}} in {{unit}}',
            looseOf: 'Loose {{name}} in {{unit}}',
            unitOf: 'Unit for {{name}}',
            total: 'Comes to {{total}}',
            invalid: 'Enter a whole number of packs, and an amount in {{unit}}.',
        },
        editor: {
            newTitle: 'New stock item',
            newHint: 'An ingredient or a supply you keep. It starts at zero: receive a delivery or count it to set what is there.',
            editTitle: 'Edit {{name}}',
            editHint: 'Changes apply from now on. History stays as it was recorded.',
            name: 'Name',
            namePlaceholder: 'Frozen fries',
            nameRequired: 'Give the item a name.',
            kind: 'Kind',
            kindHint: {
                ingredient: 'Used in recipes or sold as part of a dish or drink.',
                supply: 'Used by the venue itself: cleaning products, spare keyboards, cables. Never sold.',
            },
            baseUnit: 'Counted in',
            unitHint: 'Weight for anything weighed, volume for liquids, pieces for anything counted one by one.',
            unitLocked: 'Fixed now that the item has moved: its history is in this unit.',
            unitName: 'One is called',
            unitNamePlaceholder: 'can',
            unitNameHint: 'So it reads “12 cans” everywhere, not “12 each”.',
            unitNamePlural: 'More than one are called',
            unitNamePluralPlaceholder: 'cans',
            supplier: 'Usual supplier',
            supplierHint: 'Chosen for you when a delivery of it is received.',
            par: 'Par level',
            parHint: 'Below this it shows as Low. Leave empty for no par level.',
            averageCost: 'Average cost',
            costHint: 'From the deliveries received, weighted by quantity.',
            notCosted: 'Not costed yet: receive a delivery with its cost.',
            sku: 'SKU',
            barcode: 'Barcode',
            barcodeHint: 'The item’s own barcode, for scanning when receiving and counting.',
            barcodeRetail: 'The product’s barcode finds it too.',
            note: 'Note',
            packs: 'Pack sizes',
            packsHint: 'How it arrives and how you count it: a 5 kg bag, a case of 24. Each can have its own barcode.',
            packName: 'Pack {{n}} name',
            packNamePlaceholder: 'Case of 24',
            packHolds: 'What pack {{n}} holds',
            packUnit: 'Unit for pack {{n}}',
            packBarcode: 'Barcode for pack {{n}}',
            packBarcodePlaceholder: 'Barcode',
            packRemove: 'Remove pack {{n}}',
            packAdd: 'Add a pack size',
            packEquals: '{{name}} holds {{size}}',
            packInvalid: 'Each pack size needs a name and what it holds.',
            archive: 'Archived',
            archiveHint: 'An archived item leaves the list and the counts. Its history is kept.',
            retailTitle: 'Tracked for a retail product',
            retailBody: 'Each sale of the product takes one away. Its name follows the product on the menu.',
            create: 'Add item',
            save: 'Save changes',
            created: '{{name}} added',
            saved: '{{name}} saved',
            archived: '{{name}} archived',
            noChanges: 'Change something first.',
            refused: 'That could not be saved. Check the details and try again.',
        },
        detail: {
            eyebrow: 'Stock item',
            loading: 'Loading…',
            description: 'What is on hand now, and every change to it.',
            unavailable: 'This item could not be loaded.',
            onHand: 'On hand',
            noPar: 'None',
            notCosted: 'Not costed yet',
            lastCounted: 'Last counted',
            neverCounted: 'Not counted yet',
            negativeAdvice: 'Below zero means more was sold or used than was recorded coming in. Count it to put it right.',
            actions: 'Item actions',
            tabs: 'Item',
            history: 'History',
            details: 'Details',
            product: 'Retail product',
            productLinked: 'Sold one for one from the menu',
            noPacks: 'No pack sizes: it is received and counted loose.',
            packHolds: 'holds {{size}}',
        },
        history: {
            filters: 'Filter the history',
            kindFilter: 'Kind of change',
            allKinds: 'Every change',
            spanFilter: 'How far back',
            allTime: 'All time',
            caption: 'History of {{name}}',
            when: 'When',
            what: 'What',
            change: 'Change',
            after: 'On hand after',
            value: 'Value',
            who: 'Who',
            system: 'Clavian',
            openSale: 'Open the order',
            openDelivery: 'Open the delivery',
            openCount: 'Open the count',
            more: 'Show more',
            empty: 'Nothing has moved yet.',
            unavailable: 'The history could not be loaded.',
        },
        receive: {
            title: 'Receive a delivery',
            hint: 'What arrived, from whom, and what it cost. Saving adds it to stock and updates the average costs.',
            supplier: 'Supplier',
            noSupplier: 'No supplier',
            reference: 'Invoice number',
            referencePlaceholder: 'INV-20419',
            noReference: 'No invoice number',
            addItem: 'Add an item',
            empty: 'Search for an item or scan its barcode to add it to the delivery.',
            linesLabel: 'Items in this delivery',
            onHandNow: 'On hand now {{quantity}}',
            removeLine: 'Remove {{name}}',
            costMode: 'How the cost of {{name}} is entered',
            costTotal: 'Line total',
            costPerPack: 'Per pack',
            costPer: 'Per {{pack}}',
            costOf: 'Cost of {{name}} in {{currency}}',
            perPackTotal: '{{count}} packs come to {{total}}',
            quantityRequired: 'Enter how much arrived.',
            costRequired: 'Enter what it cost, or 0 for a free sample.',
            perPackNeedsPacks: 'A cost per pack needs a number of one pack size and nothing loose.',
            noLines: 'Add at least one item.',
            summary_one: '{{count}} item · {{total}}',
            summary_other: '{{count}} items · {{total}}',
            review: 'Check delivery',
            reviewTitle: 'Check the delivery',
            reviewCaption: 'Delivery lines',
            reviewNote: 'Once saved, a delivery is not edited. A mistake is put right with a correction.',
            quantity: 'Quantity',
            packs: 'As entered',
            lineCost: 'Cost',
            total: 'Total',
            packTimes: '{{count}} × {{pack}}',
            looseWords: '{{amount}} {{unit}} loose',
            looseOf: '{{quantity}} loose',
            confirm: 'Receive for {{amount}}',
            done_one: 'Received {{count}} item',
            done_other: 'Received {{count}} items',
            doneFrom_one: 'Received {{count}} item from {{supplier}}',
            doneFrom_other: 'Received {{count}} items from {{supplier}}',
            refused: 'The delivery could not be saved. Nothing was added. Try again.',
        },
        deliveries: {
            caption: 'Deliveries received',
            received: 'Received',
            items: 'Items',
            cost: 'Cost',
            by: 'Recorded by',
            eyebrow: 'Delivery',
            receivedBy: 'Received {{when}}, recorded by {{who}}',
            immutable: 'A delivery is kept as it was received. A mistake is put right with a correction on the item.',
            emptyTitle: 'No deliveries yet',
            empty: 'Each delivery you receive is listed here with its supplier and cost.',
            unavailable: 'Deliveries could not be loaded.',
        },
        usage: {
            title: 'Record usage',
            hint: 'Stock that left the shelf without being sold.',
            kind: 'What happened',
            mode: {
                waste: 'Wasted',
                staff_meal: 'Staff meal',
                issue: 'Issued',
                adjust: 'Correction',
            },
            modeHint: {
                waste: 'Thrown away: expired, damaged, spilled or not good enough to serve.',
                staff_meal: 'Eaten or drunk by staff.',
                issue: 'A supply put to use, such as a keyboard swapped onto a PC.',
                adjust: 'Put a figure right by hand. A count is usually better: use this for a known mistake.',
            },
            reason: 'Reason',
            reasonPlaceholder: 'Choose a reason',
            reasonRequired: 'Choose why it was wasted.',
            station: 'Issued to',
            noStation: 'No PC in particular',
            stationNumber: 'PC {{number}}',
            addItem: 'Add an item',
            addDish: 'Or add a dish',
            addDishPlaceholder: 'Choose a dish with a recipe',
            pickItem: 'Item to correct',
            change: 'Change',
            direction: 'Correction',
            remove: 'Take away',
            add: 'Add',
            after: 'On hand after: {{quantity}}',
            byRecipe: 'Takes its recipe away',
            size: 'Size',
            extras: 'Extras',
            servings: 'Servings',
            empty: 'Add the items, or the dishes, that were used.',
            linesLabel: 'Used',
            note: 'Note',
            why: 'Why',
            whyPlaceholder: 'Found a miscount from Tuesday',
            noteRequired: 'Say why, so the history makes sense later.',
            quantityRequired: 'Enter how much.',
            servingsRequired: 'Enter a whole number of servings.',
            noLines: 'Add at least one item or dish.',
            submit: {
                waste: 'Record waste',
                staff_meal: 'Record staff meal',
                issue: 'Record issue',
                adjust: 'Save correction',
            },
            done: {
                waste_one: 'Waste recorded for {{count}} item',
                waste_other: 'Waste recorded for {{count}} items',
                staff_meal_one: 'Staff meal recorded for {{count}} item',
                staff_meal_other: 'Staff meal recorded for {{count}} items',
                issue_one: 'Issue recorded for {{count}} item',
                issue_other: 'Issue recorded for {{count}} items',
            },
            adjusted: '{{name}} corrected',
            refused: 'That could not be recorded. Nothing changed. Try again.',
        },
        start: {
            title: 'Count stock',
            hint: 'Count what is on the shelf. You can stop and come back: the count is kept as you go.',
            kind: 'What to count',
            full: {
                title: 'Full count',
                body: 'Every active item ({{count}}). The first thing to do after turning stock on, and a good monthly habit.',
            },
            spot: {
                title: 'Spot count',
                body: 'Only the items you choose: the low ones, or a fridge after a power cut.',
            },
            items: 'Items to count ({{count}})',
            search: 'Find an item',
            pickAttention: 'Choose low and out',
            clear: 'Clear',
            note: 'Note',
            notePlaceholder: 'Month-end count',
            chooseItems: 'Choose at least one item to count.',
            openTitle: 'A count is already open',
            openBody: '{{kind}} started {{when}}: {{counted}} of {{total}} counted.',
            startFull_one: 'Start counting {{count}} item',
            startFull_other: 'Start counting {{count}} items',
            startSpot_one: 'Count {{count}} item',
            startSpot_other: 'Count {{count}} items',
            started_one: 'Count started: {{count}} item to count',
            started_other: 'Count started: {{count}} items to count',
            refused: 'The count could not be started. Try again.',
        },
        counts: {
            caption: 'Counts',
            started: 'Count',
            startedBy: 'Started {{when}} by {{who}}',
            status: 'Status',
            progress: 'Counted',
            progressOf: '{{counted}} of {{total}} counted',
            variance: 'Difference',
            continue: 'Continue',
            view: 'View',
            kind: {
                full: 'Full count',
                spot: 'Spot count',
            },
            statusWord: {
                open: 'Open',
                submitted: 'Posted',
                cancelled: 'Cancelled',
            },
            emptyTitle: 'No counts yet',
            empty: 'A full count sets every item to what is on the shelf. Start with one.',
            unavailable: 'Counts could not be loaded.',
        },
        count: {
            back: 'Counts',
            loadingTitle: 'Count',
            unavailable: 'This count could not be loaded.',
            postedBy: 'Posted {{when}} by {{who}}',
            cancelledAt: 'Cancelled {{when}}',
            toolbar: 'Find items on the count',
            search: 'Find an item',
            show: 'Show',
            showAll: 'All',
            showTodo: 'To count',
            showCounted: 'Counted',
            blindHint: 'Count what is there. What the system expects is shown in the review, after you have counted. Enter saves an item and moves to the next.',
            groupCount_one: '{{count}} item',
            groupCount_other: '{{count}} items',
            nothingHere: 'Nothing left to show here.',
            toCount: 'To count',
            saving: 'Saving…',
            notSaved: 'Not saved: try again',
            left_one: '{{count}} item left to count',
            left_other: '{{count}} items left to count',
            review: 'Review count',
            summary: 'Count summary',
            countedFigure: 'Counted',
            ofItems_one: 'of {{count}} item',
            ofItems_other: 'of {{count}} items',
            differentFigure: 'Different',
            matching_one: '{{count}} item matches',
            matching_other: '{{count}} items match',
            net: 'Net difference',
            netPreview: 'Net difference if posted now',
            lossGain: '{{loss}} short, {{gain}} over',
            differences: 'Differences',
            differencesHint: 'Biggest first. Expected is what was on hand when each item was counted.',
            noDifferences: 'Everything counted matches what was expected.',
            reviewCaption: 'Items that differ from what was expected',
            expected: 'Expected',
            counted: 'Counted',
            difference: 'Difference',
            differenceValue: 'Difference value',
            uncountedTitle_one: '{{count}} item still to count',
            uncountedTitle_other: '{{count}} items still to count',
            uncountedBody: 'Still to count: {{names}}. A full count sets every item, so count these or enter 0 for any that are not there.',
            andMore_one: 'and {{count}} more',
            andMore_other: 'and {{count}} more',
            backToCounting: 'Back to counting',
            cancel: 'Cancel count',
            post: 'Post count',
            postTitle: 'Post this count?',
            postBody_one: 'On hand becomes what you counted for {{count}} item.',
            postBody_other: 'On hand becomes what you counted for {{count}} items.',
            postNet: 'Net difference: {{amount}}.',
            postFinal: 'A posted count cannot be undone. A later count or a correction puts a mistake right.',
            note: 'Note',
            notePlaceholder: 'Two cases found behind the fridge',
            postedNote: 'Noted when it was posted',
            keepReviewing: 'Keep reviewing',
            posted_one: 'Count posted for {{count}} item',
            posted_other: 'Count posted for {{count}} items',
            incomplete_one: '{{count}} item is still to count.',
            incomplete_other: '{{count}} items are still to count.',
            postRefused: 'The count could not be posted. Nothing changed. Try again.',
            cancelTitle: 'Cancel this count?',
            cancelBody_one: 'The {{count}} figure entered is thrown away and nothing in stock changes.',
            cancelBody_other: 'The {{count}} figures entered are thrown away and nothing in stock changes.',
            keepCount: 'Keep the count',
            cancelled: 'Count cancelled',
            cancelRefused: 'The count could not be cancelled. Try again.',
        },
        suppliers: {
            toolbar: 'Suppliers',
            caption: 'Suppliers',
            name: 'Name',
            phone: 'Phone',
            email: 'Email',
            note: 'Note',
            notePlaceholder: 'Delivers Tuesday and Friday mornings',
            showArchived: 'Show archived',
            add: 'Add supplier',
            addSubmit: 'Add supplier',
            addTitle: 'Add a supplier',
            editTitle: 'Edit {{name}}',
            hint: 'Who you buy from. You pick them when you receive a delivery.',
            archive: 'Archived',
            nameRequired: 'Give the supplier a name.',
            phoneInvalid: 'That phone number is too short.',
            emailInvalid: 'That email address does not look right.',
            added: '{{name}} added',
            saved: '{{name}} saved',
            refused: 'That could not be saved. Try again.',
            emptyTitle: 'No suppliers yet',
            empty: 'Add the people you buy from, so each delivery says who it came from.',
            unavailable: 'Suppliers could not be loaded.',
        },
        copy: {
            action: 'Copy items and recipes from another venue',
            title: 'Copy from another venue',
            hint: 'Start this venue’s stock from another venue’s set-up.',
            from: 'Copy from',
            fromPlaceholder: 'Choose a venue',
            chooseVenue: 'Choose the venue to copy from.',
            copiesTitle: 'What is copied',
            copies: {
                items: 'Its food & drink items and supplies',
                packs: 'Their pack sizes and barcodes',
                par: 'Their par levels',
                recipes: 'Its recipes, for dishes that have none here',
            },
            neverTitle: 'What is never copied',
            never: {
                quantities: 'Quantities: every copied item starts at zero',
                costs: 'Costs: they come from your own deliveries',
                history: 'Any history, delivery or count',
                retail: 'Retail items: each venue tracks its own from the menu',
            },
            previewTitle: 'At {{venue}}',
            preview: 'The copy would add {{items}} items and {{recipes}} recipes here.',
            previewNothing: 'There is nothing new to copy: everything there is already here, or is left out.',
            wouldMatch_one: '{{count}} item has the same name and unit as an item here, so recipes use the item here.',
            wouldMatch_other: '{{count}} items have the same name and unit as items here, so recipes use the items here.',
            wouldSkip_one: '{{count}} item is left out: a retail item, or an item counted in another unit here.',
            wouldSkip_other: '{{count}} items are left out: retail items, or items counted in another unit here.',
            outcomesLabel: 'What happens to each item and recipe',
            itemOutcome: {
                copy: 'New items',
                match: 'Matched to an item here',
                already_copied: 'Copied before, left as they are',
                skip_unit: 'Left out: counted in another unit here',
                skip_retail: 'Left out: retail items',
            },
            recipeOutcome: {
                copy: 'New recipes',
                already_here: 'Already have a recipe here',
                skip: 'Left out: none of their items come across',
            },
            idempotent: 'Running the copy again copies nothing twice.',
            previewUnavailable: 'What a copy from {{venue}} would do could not be worked out. Try again, or copy anyway: it says what it did.',
            noOther: 'You work at no other venue of this organisation to copy from.',
            submit: 'Copy',
            toast: 'Items and recipes copied',
            matched_one: '{{count}} item was matched to an item here.',
            matched_other: '{{count}} items were matched to items here.',
            doneTitle: 'Copied from {{venue}}',
            doneBody: 'Items copied: {{items}}. Recipes copied: {{recipes}}.',
            already: 'Already here and left as they are: {{items}} items, {{recipes}} recipes.',
            next: 'Copied items start at zero. Count them before you rely on the figures.',
            refused: 'The copy could not be made. Nothing changed. Try again.',
        },
        recipes: {
            title: 'Stock used',
            intro: 'What one of each takes from stock at {{venue}}. Recipes are this venue’s own.',
            unavailable: 'The recipes could not be loaded.',
            base: 'Every size',
            baseHint: 'Used for any size without a recipe of its own',
            perUnit: 'Per one sold',
            size: 'Size, per one sold',
            extra: '{{group}}, added per one sold',
            none: 'Nothing yet.',
            usesBase: 'Uses the recipe for every size.',
            add: 'Add an item',
            pick: 'Item for {{name}}',
            quantityOf: 'Quantity of {{name}}',
            remove: 'Remove {{name}}',
            unknownItem: 'An archived item',
            makeable: 'Can be made now',
            notMakeable: 'Not enough stock',
            cost: 'Costs',
            costIncomplete: 'so far: an item has no cost yet',
            costOnSave: 'The cost and margin update when you save.',
            noCost: 'No cost yet',
            margin: 'Margin',
            marginPercent: '({{percent}})',
            onPrice: 'on {{price}} before tax',
            costPerExtra: 'Adds to the dish’s cost each time it is chosen:',
            soldOutTitle: 'Sold out here: not enough stock',
            soldOutBody: 'None of its recipes can be made from what is on hand. It comes back by itself when stock arrives.',
            retail: 'Sold off the shelf: each sale takes one {{name}} away. Add a recipe only if it is made from other items.',
            invalid: 'Every item in a recipe needs a quantity.',
            save: 'Save recipes',
            saved: 'Recipes for {{name}} saved',
            refused: 'The recipes could not be saved. Try again.',
            readOnly: 'Recipes are kept by a manager.',
        },
        reports: {
            toolbar: 'Stock reports',
            which: 'Report',
            range: 'Range',
            tab: {
                value: 'Stock value',
                usage: 'Usage',
                variance: 'Count variance',
                margins: 'Margins',
            },
            kind: 'Kind',
            total: 'Total',
            unavailable: 'This report could not be loaded.',
            notPermitted: 'Your role cannot see stock costs.',
            value: {
                total: 'Stock value',
                asOf: 'As of {{when}}',
                items: 'Items',
                itemsHint: 'Active items on the list',
                uncosted: 'Without a cost',
                uncostedHint: 'Valued at zero until a delivery costs them',
                allCosted: 'Every item has a cost',
                top: 'Where the value is',
                topHint: 'The items worth the most on the shelf',
                none: 'Nothing on the shelf has a value yet.',
                byItem: 'By item',
                byItemHint: 'On hand at each item’s average cost',
                uncostedNote: 'No cost yet: {{names}}. Receive them with their cost to value them.',
            },
            usage: {
                sold: 'Sold',
                soldHint: 'Stock taken away by sales, at cost',
                wasted: 'Wasted and staff meals',
                wastedHint: 'Of which staff meals {{meals}}',
                received: 'Received',
                receivedHint: 'What deliveries cost',
                corrections: 'Counts and corrections',
                correctionsHint: 'What counts and corrections changed',
                byItem: 'By item',
                byItemHint: 'Every change from {{from}} to {{to}}, in each item’s unit',
                usedValue: 'Used value',
                usedTotal: 'Used in total',
                none: 'Nothing moved in this range.',
            },
            variance: {
                net: 'Net difference',
                netHint: 'What counts found, against what was expected',
                losses: 'Short',
                lossesHint: 'Items that came up short, at cost',
                counts: 'Counts posted',
                countsHint: 'Full and spot counts in this range',
                byCount: 'By count',
                byCountHint: 'Each count posted and what it found',
                posted: 'Posted',
                items: 'Items',
                byItem: 'By item',
                byItemHint: 'Biggest shortfall first',
                none: 'No count was posted in this range.',
                noItems: 'Every count matched what was expected.',
            },
            margins: {
                average: 'Average margin',
                averageHint: 'Across the dishes below, before tax',
                costed: 'Dishes with a recipe',
                costedHint: 'Each size counts on its own',
                incompleteCount: 'Missing a cost',
                incompleteHint: 'An item in the recipe has no cost yet',
                chart: 'Margin by dish',
                chartHint: 'Lowest at the bottom',
                meta: 'Costs {{cost}} on {{price}}',
                table: 'Every dish',
                tableHint: 'Price, price before tax, what the recipe costs and the margin',
                product: 'Dish',
                price: 'Price',
                priceExclTax: 'Before tax',
                cost: 'Recipe cost',
                margin: 'Margin',
                marginPercent: 'Margin %',
                incomplete: 'Cost incomplete',
                incompleteNote: 'Where a cost is incomplete, the cost is at least what is shown and the margin at most.',
                none: 'No dish has a recipe here yet. Add recipes in the menu, on a product’s Stock used tab.',
            },
        },
    },
    onlinePayment: {
        label: 'Online payment',
        amount: 'Amount to pay',
        scan: 'Ask the player to scan this with their phone’s camera and pay online with UPI, a card or netbanking.',
        qrLabel: 'Payment QR code for {{amount}}',
        upiLink: 'UPI payment link',
        expires: 'Expires at {{time}}',
        waiting: 'Waiting for payment',
        status: {
            pending: 'Waiting for the player to pay…',
            succeeded: 'Paid. The payment provider confirmed it.',
            failed: 'The payment failed: {{reason}}',
            failedNoReason: 'The payment failed.',
            cancelled: 'Cancelled. Nothing was taken.',
            expired: 'The payment request expired. Nothing was taken.',
        },
        cancel: 'Cancel request',
        again: 'Try again',
        refused: 'The payment request could not be made. Try again or take payment another way.',
        unavailable: 'Online payments are not available at this venue.',
    },
    orders: {
        title: 'Orders',
        intro: 'Every sale at this venue, with how it was paid, its discounts, refunds and receipt.',
        caption: 'This venue’s sales',
        emptyAction: 'Open Sell',
        emptyTitle: 'No orders yet',
        empty: 'A sale from the Sell screen, the kiosk or the app shows here.',
        filterEmpty: 'No order matches this filter.',
        search: 'Search by reference or item',
        unavailable: 'The orders could not be loaded.',
        notPermitted: 'Your role cannot see this venue’s orders.',
        open: 'Open',
        column: {
            placed: 'Placed',
            id: 'Order',
            status: 'Status',
            total: 'Total',
            outstanding: 'Outstanding',
            refunded: 'Refunded',
            items: 'Items',
        },
        status: {
            placed: 'Placed',
            awaiting_payment: 'Awaiting payment',
            partially_paid: 'Part paid',
            paid: 'Paid',
            partially_refunded: 'Part refunded',
            refunded: 'Refunded',
            cancelled: 'Cancelled',
        },
        filter: {
            status: 'Show',
            all: 'All',
            awaiting: 'Awaiting payment',
            open: 'Open',
            paid: 'Paid',
            refunds: 'Refunded',
            cancelled: 'Cancelled',
        },
        channel: {
            counter: 'Counter',
            kiosk: 'Kiosk',
            app: 'App',
            web: 'Web',
            api: 'API',
        },
        tender: {
            cash: 'Cash',
            counter_card: 'Card',
            counter_upi: 'Counter UPI',
            wallet: 'Wallet',
            on_account: 'On account',
            online: 'Online (UPI or card)',
        },
        source: {
            station: 'A PC',
        },
        kiosk: {
            awaiting: 'The player bought this at the kiosk and chose to pay at the desk. Take payment and the pass is added to their session; cancel it and nothing is added.',
        },
        field: {
            id: 'Order',
            channel: 'Channel',
            subtotal: 'Subtotal',
            discount: 'Discount',
            tax: 'GST',
            total: 'Total',
            paid: 'Paid',
            refunded: 'Refunded',
            outstanding: 'Outstanding',
        },
        lines: {
            title: 'Lines',
        },
        discount: {
            code: 'Code {{code}}',
            manual: 'Staff discount',
        },
        payments: {
            title: 'Payments',
            cashReceived: 'Cash received',
            change: 'Change',
            changeToWallet: 'Change added to {{player}}’s wallet',
            changeToWalletUnnamed: 'Change added to the player’s wallet',
        },
        refunds: {
            title: 'Refunds',
            approve: 'Approve',
        },
        refundStatus: {
            pending_approval: 'Awaiting approval',
            succeeded: 'Refunded',
            declined: 'Declined',
        },
        // Printing the receipt (F-OPS-08): to the receipt printer, or the browser when the venue has none.
        receipt: {
            windowTitle: 'Receipt',
        },
        document: {
            title: 'Receipt',
            print: 'Print',
            // The server's printable document (F-CMP-07): the A4 page the print dialog saves as PDF, or the tax invoice. The 80 mm
            // receipt prints from the order's action bar (to the receipt printer, or the browser where there is none).
            printInvoice: 'Tax invoice (A4 / PDF)',
            printA4: 'A4 / PDF',
            printFailed: 'The document could not be opened for printing. Try again.',
            buyer: 'Billed to {{name}} · GSTIN {{gstin}}',
            igst: 'IGST (inter-state)',
            creditNotes: 'Credit notes',
            creditNote: 'Credit note',
            taxPaidAtTopUp: 'Of the GST on this invoice, {{amount}} was already paid when this money was topped up in the previous system. It is not charged again.',
            // The last email of the receipt and how it went (F-POS-13). `{{to}}` is the address, masked.
            delivery: {
                queued: 'Sending to {{to}}…',
                queuedPlayer: 'Sending to the player’s email…',
                sent: 'Sent to {{to}} at {{time}}',
                sentAuto: 'Emailed automatically to {{to}} at {{time}}',
                failed: 'Not sent to {{to}}: {{reason}}',
                failedNoAddress: 'Not sent: {{reason}}',
                reason: {
                    'no-address': 'the player has no email address',
                    'provider-unavailable': 'email is not set up on this platform yet',
                    timeout: 'the mail server did not answer in time',
                    connection: 'the mail server could not be reached',
                    tls: 'a secure connection to the mail server failed',
                    auth: 'the mail server refused our sign-in',
                    envelope: 'the address was refused',
                    message: 'the message was refused',
                    other: 'something went wrong',
                },
            },
        },
        drawer: {
            eyebrow: 'Order',
            title: 'Order details',
            loading: 'Loading the order',
            unavailable: 'The order could not be loaded.',
        },
        actions: {
            title: 'Actions',
            takePayment: 'Take payment',
            applyCode: 'Apply a code',
            refund: 'Refund',
            resend: 'Send receipt',
            printReceipt: 'Print on receipt printer',
            cancel: 'Cancel sale',
            dismiss: 'Cancel',
        },
        payment: {
            done: 'Payment taken.',
            tender: 'Payment method',
            amount: 'Amount',
            submit: 'Take payment',
            badAmount: 'Enter an amount like 150 or 150.50.',
            conflict: 'That amount is more than is outstanding, or the sale is already paid.',
            refused: 'The payment could not be taken. Try again.',
            tendered: 'Cash received ({{currency}})',
            tenderedHint: 'What the player handed over, to show the change.',
            quickLabel: 'Quick cash amounts',
            exact: 'Exact',
            giveBack: 'Give back',
            changeLabel: 'The change',
            toWallet: 'Add to wallet',
            addedToWallet: 'Added to wallet',
            changeToWalletUnavailable: 'The change cannot go to the wallet here. Hand it back in cash.',
            changeToWalletOverMax: 'That would take the wallet over its limit. Hand the change back in cash.',
        },
        code: {
            label: 'Discount code',
            required: 'Enter a code.',
            submit: 'Apply',
            refused: 'That code could not be applied. Check it and try again.',
        },
        refund: {
            done: 'Refund done.',
            amount: 'Amount',
            reason: 'Reason',
            hint: 'Refund part or all of what was paid. Above the threshold, a second member approves it.',
            submit: 'Refund',
            badAmount: 'Enter an amount like 150 or 150.50.',
            reasonRequired: 'Give a reason.',
            conflict: 'The sale is not paid, or the amount is more than remains refundable.',
            refused: 'The refund could not be recorded. Try again.',
            destination: 'Refund to',
            toTender: 'How they paid',
            toWallet: 'Player’s wallet',
            toOnline: 'Online payment',
            tenderOnline: 'Online',
            onlineHelp: 'Back to the player’s UPI, card or bank, through the payment provider. It can take a few days to reach them.',
            onlineCap: 'Up to {{amount}} can go back online',
            onlineOver: 'That is more than can go back online. The most is {{amount}}.',
            // Refund to the player's wallet (spec/06 §8.7, 2026-10-02).
            wallet: {
                player: 'Whose wallet',
                playerHint: 'Name, phone or email',
                playerChange: 'Change',
                playerRequired: 'Choose the player whose wallet gets the refund.',
                noPlayer: 'This sale has no player. Ask a member who can look up players to refund it to a wallet.',
                balance: 'Wallet',
                note: 'Credited as wallet cash: the player can spend it or have it refunded later. Nothing leaves the drawer.',
                submit: 'Refund to wallet',
            },
        },
        send: {
            to: 'Send to',
            hint: 'Send the receipt by email. Leave the address blank to use the player’s email address.',
            submit: 'Send',
            noContact: 'There is no email address to send it to. Enter one.',
            badAddress: 'Enter an email address, like name@example.com.',
            refused: 'The receipt could not be sent. Try again.',
        },
        cancel: {
            title: 'Cancel this sale',
            body: 'This cancels a sale that has taken no payment. It cannot be undone.',
            confirm: 'Cancel sale',
            keep: 'Keep the sale',
            refused: 'The sale could not be cancelled. It may have taken a payment.',
        },
    },
    // The F&B menu setup (spec/13 §6.1, F-FNB-01/-02/-03).
    menu: {
        title: 'Menu',
        intro: 'The venue’s food and drink — its categories and products, their prices, and the sold-out switch.',
        readOnly: 'Read only: your role cannot change the menu.',
        notPermitted: 'Your role cannot see this venue’s menu.',
        unavailable: 'The menu could not be loaded.',
        emptyTitle: 'No products yet',
        empty: 'Add a product to sell it at the counter, the kiosk and the app.',
        noCategories: 'Start with a category, like Snacks or Drinks, then add products to it.',
        search: 'Search products',
        categoriesLabel: 'Category',
        all: 'All',
        groupCount_one: '{{count}} product',
        groupCount_other: '{{count}} products',
        soldOut: 'Sold out',
        // A product switched off here because none of its recipes can be made from what is on hand (spec/13 §6.4).
        soldOutForStock: 'Sold out: not enough stock',
        available: 'Available',
        availableNamed: '{{name}} available',
        nowAvailable: '{{name}} is available again.',
        nowSoldOut: '{{name}} is sold out.',
        availabilityRefused: 'The change could not be saved. Try again.',
        // When a product or a category may be sold, in the venue's own time (F-FNB-02).
        schedule: {
            label: 'When it is sold',
            anyTime: 'Any time',
            anyTimeHint: 'Any time the venue is open. Add a time window to sell it only then, like breakfast or after 18:00.',
            hint: 'In the venue’s own time. Outside these windows it stays on the menu, greyed out, and cannot be ordered. An end before the start runs past midnight.',
            window: 'Time window {{number}}',
            from: 'From',
            to: 'Until',
            add: 'Add a time window',
            remove: 'Remove time window {{number}}',
            span: '{{from}}–{{to}}',
            more_one: '{{span}} +{{count}} more',
            more_other: '{{span}} +{{count}} more',
            notNow: 'Not now',
            invalid: 'Each time window needs at least one day and two different times.',
            days: {
                mon: 'Mon',
                tue: 'Tue',
                wed: 'Wed',
                thu: 'Thu',
                fri: 'Fri',
                sat: 'Sat',
                sun: 'Sun',
            },
        },
        diet: {
            none: 'Not marked',
            veg: 'Veg',
            egg: 'Egg',
            non_veg: 'Non-veg',
            vegan: 'Vegan',
        },
        area: {
            none: 'No preparation',
            kitchen: 'Kitchen',
            bar: 'Bar',
        },
        category: {
            title: 'Add category',
            hint: 'A group on the menu: Snacks, Drinks, Meals.',
            done: 'Category {{name}} added.',
            name: 'Name',
            submit: 'Add category',
            required: 'Fill this in.',
            refused: 'The category could not be added. Try again.',
            editNamed: 'Edit {{name}}',
            edit: {
                action: 'Edit',
                title: 'Edit category',
                hint: 'Rename this category, or hide it from the menu without deleting it.',
                visible: 'Shown on the menu',
                save: 'Save changes',
                done: 'Category {{name}} saved.',
                noChanges: 'Change something first.',
                refused: 'The category could not be saved. Try again.',
            },
        },
        product: {
            // Editing a product where stock is tracked: its details, and what it uses from stock (spec/13 §6.1, §6.4).
            tabs: 'Product',
            tabDetails: 'Details',
            tabStock: 'Stock used',
            // A product's picture (F-FNB-01): Sell's cards and the kiosk's Food screen show it.
            image: {
                label: 'Picture',
                hint: 'Shown on Sell and on the kiosk’s Food screen, cropped to fill a 4:3 frame. A well-lit photo of the dish, centred, at least 800 × 600, works best. JPEG, PNG or WebP, up to 5 MB.',
                upload: 'Upload a picture',
                replace: 'Replace the picture',
                remove: 'Remove the picture',
                uploading: 'Uploading {{name}}.',
                uploaded: 'Uploaded. Save the product to use it.',
                tooLarge: 'That file is over 5 MB.',
                wrongType: 'Use a JPEG, PNG or WebP image.',
                failed: 'The upload failed. Try again.',
            },
            title: 'Add product',
            hint: 'Its price, how it is marked, and where it is made.',
            done: '{{name}} added to the menu.',
            taxRate: '{{rate}}%',
            name: 'Name',
            category: 'Category',
            chooseCategory: 'Choose a category',
            price: 'Price ({{currency}})',
            diet: 'Diet marker',
            preparationArea: 'Prepared at',
            tax: 'Tax rate',
            // Where it may be sold (F-FNB-01 rule 4) and whether it may be returned.
            channels: 'Sold on',
            channelsHint: 'Left off a channel, it does not appear there and cannot be ordered there.',
            channel: {
                counter: 'The counter',
                kiosk: 'The kiosk',
                app: 'The app',
            },
            returnable: 'Can be returned for a refund',
            submit: 'Add product',
            required: 'Fill this in.',
            priceInvalid: 'Enter a price like 150 or 150.50.',
            refused: 'The product could not be added. Check the fields and try again.',
            noCategories: 'Add a category first.',
            editNamed: 'Edit {{name}}',
            edit: {
                title: 'Edit product',
                hint: 'Change its name, category, price, diet marker, where it is made, its tax rate, where and when it is sold.',
                submit: 'Save changes',
                done: '{{name}} saved.',
                noChanges: 'Change something first.',
                refused: 'The product could not be saved. Check the fields and try again.',
            },
        },
    },
    // Pricing & promotions (spec/13 §18; F-PRC-01, F-PRC-10, F-POS-02, F-POS-03, F-POS-06, F-MEM-01):
    // passes, the pay-as-you-go rate, promotions, codes and coupons, and memberships — one sidebar item,
    // renamed from "Catalogue" so it is not taken for HQ's game catalogue (the product owner, 2026-09-29).
    pricing: {
        title: 'Pricing & promotions',
        intro: 'What this venue charges for play and what it takes off.',
        introOrganization: 'What your venues charge and what they take off, set once for the organisation.',
        hero: {
            passes: 'Passes on sale',
            promotions: 'Promotions running',
            hour: 'An hour, pay as you go',
            of: 'of {{total}}',
        },
        tabs: {
            label: 'Pricing & promotions',
            passes: 'Passes',
            payg: 'Pricing',
            promotions: 'Promotions & codes',
            memberships: 'Memberships',
        },
        noEnd: 'No end date',
        status: {
            active: 'Active',
            paused: 'Paused',
            scheduled: 'Scheduled',
            ended: 'Ended',
            usedUp: 'Used up',
        },
        value: {
            percent: '{{percent}}% off',
            amount: '{{amount}} off',
            capped: '{{value}}, up to {{cap}}',
            cappedSession: '{{value}}, up to {{cap}} a session',
        },
        kind: {
            // ADR-0034: a console offer discounts a staff-run session's one order.
            console: 'Console sessions',
            time: 'Passes and counter time',
            food: 'Food and drinks',
            retail: 'Retail',
            other: 'Other',
            play: 'Play time (pay-as-you-go & wallet)',
        },
        channel: {
            counter: 'Counter',
            kiosk: 'Kiosk',
            app: 'Player app',
            web: 'Website',
            api: 'Integrations',
        },
        rules: {
            everything: 'Everything in the cart',
            products_one: '{{count}} product',
            products_other: '{{count}} products',
            window: '{{from}}–{{to}}',
            allVenues: 'All venues',
            venues_one: '{{count}} venue',
            venues_other: '{{count}} venues',
            minOrder: 'Orders of {{amount}} or more',
            firstOrder: 'First order only',
            until: 'Until {{date}}',
        },
        // Console offers (ADR-0034): conditions on a staff-run session's controllers and booked time, and a reward.
        console: {
            hint: 'Discounts a console session’s one order — its controllers and booked time. Set the conditions here and the reward next.',
            value: {
                extraPercent: '{{percent}}% off extra controllers',
                extraAmount: '{{amount}} off each extra controller',
                bundle: '{{controllers}} controllers × {{time}} for {{amount}}',
                freeTime: 'Play {{paid}}, get {{free}} free',
                sessionPercent: '{{percent}}% off the session',
            },
            rules: {
                minControllers_one: '{{count}}+ controller',
                minControllers_other: '{{count}}+ controllers',
                minDuration: '{{time}} or more',
                classes_one: '{{count}} class',
                classes_other: '{{count}} classes',
                playersOnly: 'Registered players only',
            },
            reward: {
                extra_controllers_percent: '% off extra controllers',
                extra_controllers_amount: 'Amount off each extra controller',
                bundle: 'Bundle price',
                free_time: 'Free time',
                session_percent: '% off the session',
            },
            rewardHint: {
                extra_controllers_percent: 'Every controller after the first, for the whole session: “2nd controller 50% off”.',
                extra_controllers_amount: 'A set amount off each controller after the first, once per session.',
                bundle: 'A set price for that many controllers for that long: “4 controllers × 2 h for ₹500”.',
                free_time: 'Book the paid minutes plus the free ones, and the free ones cost nothing: “play 2 h, get 30 min free”.',
                session_percent: 'A share off the whole session’s order.',
            },
            form: {
                conditions: 'Conditions',
                minControllers: 'At least this many controllers',
                minDuration: 'At least this many minutes booked',
                classes: 'Station classes',
                classesHint: 'None picked: every staff-run class.',
                playersOnly: 'Registered players only (not walk-ins)',
                reward: 'Reward',
                perController: 'Off each extra controller ({{currency}})',
                controllers: 'Controllers',
                minutes: 'Minutes',
                bundlePrice: 'Bundle price ({{currency}})',
                paidMinutes: 'Minutes paid for',
                freeMinutes: 'Minutes free',
                capHint: 'The most it takes off one session’s order.',
                controllersRange: 'Use 1 to 16 controllers.',
                minutesRange: 'Use 1 to 1440 minutes.',
                bundleIncomplete: 'A bundle needs the controllers, the minutes and a price.',
                freeIncomplete: 'Free time needs the minutes paid for and the minutes free.',
            },
        },
        form: {
            required: 'Fill this in.',
            type: 'Discount type',
            typePercent: 'Percentage',
            typeAmount: 'Amount ({{currency}})',
            percent: 'Percentage off',
            percentInvalid: 'Enter a percentage from 0.01 to 100, like 10 or 12.5.',
            amount: 'Amount off ({{currency}})',
            amountInvalid: 'Enter an amount in rupees, like 50 or 49.50.',
            cap: 'Take off at most ({{currency}})',
            capHint: 'A ceiling on one sale’s discount. Leave empty for none.',
            minOrder: 'Smallest order ({{currency}})',
            minOrderShort: 'Smallest order',
            validFrom: 'Starts on',
            validUntil: 'Ends on',
            datesOrder: 'The end date is before the start date.',
            noChanges: 'Change something first.',
            save: 'Save changes',
        },
        // The Pricing tab: the pay-as-you-go rate and its session money settings, as one card.
        payg: {
            title: 'Pay as you go',
            lede: 'What a player pays for play at this venue when no pass covers it.',
            ledeOrganization: 'What a player pays for play when no pass covers it. Each venue can set its own.',
            perHour: 'per hour',
            edit: 'Edit pricing',
            editTitle: 'Pay-as-you-go pricing',
            editHint: 'Each value says where it comes from. Reset one to go back to the organisation’s or the default.',
            billingStep: 'Billed in steps of',
            step_one: '{{count}} minute',
            step_other: '{{count}} minutes',
            stepHint: 'A started step is charged in full.',
            budget: 'Kiosk sign-in allowance',
            budgetNone: 'None',
            budgetNoneHint: 'Players choose how to pay before they play.',
            budgetHint: 'What a player may spend before choosing how to pay.',
            wallet: 'Wallets pay for play',
            walletOn: 'On',
            walletOff: 'Off',
            walletOnHint: 'After passes, play draws on the player’s wallet at this rate.',
            walletOffHint: 'Play never draws on a wallet.',
            readOnly: 'Read only: your role cannot change pricing.',
            unavailable: 'Pricing could not be loaded.',
            notHere: 'Pay-as-you-go pricing is not set at this level.',
            // Pay-as-you-go's own switch (`sessions.payAsYouGoEnabled`): its one home is here (spec/13 §18, 2026-10-07).
            switchLabel: 'Pay-as-you-go',
            switchOnHint: 'Players pay for play by the hour at the price below.',
            switchOffHint: 'Players play on passes and time staff add. The wallet still buys passes and food.',
            offNote: 'Pay-as-you-go is off: the price below is kept but not charged.',
            confirmOffTitle: 'Turn off pay-as-you-go?',
            confirmOffBody: 'Players will need a pass, or time staff add, to play. A pass that runs out goes to grace and then ends, and wallets no longer pay for play. You can turn it back on at any time.',
            keepOn: 'Keep it on',
            turnOff: 'Turn it off',
            turnedOn: 'Pay-as-you-go is on.',
            turnedOff: 'Pay-as-you-go is off.',
            switchStale: 'Someone changed pricing just now. Check it, then try again.',
            switchFailed: 'Pay-as-you-go could not be changed. Try again.',
            perClassTitle: 'Rate cards are set per venue',
            perClass: 'Open a venue’s Pricing to price each station class on its own, with time-of-day prices and a minimum charge. A class no card prices pays the rate above.',
        },
        // A venue's rate card (spec/06 §4; F-PRC-10 … F-PRC-12): per class, with bands, the billing step and a minimum.
        rateCard: {
            // ADR-0034: a staff-run class priced by controllers — booked in blocks and paid at the end.
            controllers: {
                title: 'Controllers (consoles, sims, tables)',
                hint: 'Staff-run stations are booked in blocks for a number of controllers and paid once, at the counter, when the session ends.',
                on: 'Price {{class}} by controllers',
                first: 'First controller, per hour ({{currency}})',
                extra: 'Each extra controller, per hour ({{currency}})',
                blocks: 'Block prices for the first controller',
                block: '{{block}} ({{currency}})',
                blocksHint: 'Blank: the first-controller rate for the block’s length. Extra controllers always pay their hourly rate.',
                openUnit: 'Open sessions round the time played up to',
                summary: '+{{extra}}/h each extra controller',
                problem: 'Check {{class}}’s controller prices: whole amounts of 0 or more.',
            },
            title: 'Rate card',
            none: 'No rate card yet: every station class pays the flat rate of {{rate}} an hour.',
            inForce: 'Version {{version}} · {{name}} · pricing play since {{since}}',
            create: 'Create rate card',
            edit: 'Edit rate card',
            paygOff: 'Pay as you go is off at this venue, so the card prices nothing until it is turned on above.',
            unavailable: 'The rate card could not be loaded.',
            caption: 'What each station class pays for play',
            class: 'Class',
            now: 'Now',
            rate: 'Rate',
            step: 'Billed per',
            minimum: 'Minimum',
            bands: 'Time-of-day prices',
            perHour: '{{rate}}/h',
            nextChange: '{{rate}}/h from {{time}}',
            flat: 'Not on the card: pays the flat rate of {{rate}} an hour',
            flatShort: 'The flat rate',
            stepMinutes_one: '{{count}} minute',
            stepMinutes_other: '{{count}} minutes',
            noMinimum: 'None',
            minimumMinutes_one: 'At least {{count}} minute',
            minimumMinutes_other: 'At least {{count}} minutes',
            noBands: 'None',
            band: '{{days}} {{start}}–{{end}} at {{rate}}/h',
            example: 'Example: 90 minutes on {{class}} starting now costs {{amount}} at {{rate}}/h.',
            scheduled: 'Scheduled',
            scheduledFrom: 'Version {{version}} · {{name}} starts pricing play {{from}}.',
            editTitle: 'Rate card',
            editHint: 'Publishing makes a new version. Sessions already running keep the rate they started at.',
            defaultName: 'Rate card',
            stepRates: 'Rates',
            stepBands: 'Time bands',
            stepReview: 'Review',
            priced: 'Priced by this card (otherwise the flat {{rate}} an hour)',
            hourly: '{{class}} — price per hour',
            minimumLabel: '{{class}} — minimum charge, in minutes',
            minimumHint: 'The least a session on this class pays, as minutes of play at the rate. 0 for none; at most 60.',
            noClasses: 'No class is priced by this card: go back and choose at least one, or publish it to use the flat rate everywhere.',
            bandsHint: 'A different price at certain times, in the venue’s timezone. A band that ends earlier than it starts runs past midnight. Bands may not overlap.',
            bandStart: '{{class}} — band starts',
            bandEnd: '{{class}} — band ends',
            bandRate: '{{class}} — band price per hour',
            addBand: 'Add a time band',
            removeBand: 'Remove band',
            name: 'Name',
            startLater: 'Start at a later time',
            startsAt: 'Starts at',
            reviewLine: '{{rate}}/h, billed per {{step}}, {{bands}} time bands — 90 minutes cost {{example}}',
            reviewHint: 'Players see the rate now, and the next time-of-day price, on the kiosk.',
            back: 'Back',
            next: 'Next',
            publish: 'Publish rate card',
            published: 'Rate card published',
            day: { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun', holiday: 'Holidays' },
            problem: {
                rate: 'Enter a price per hour for {{class}}.',
                minimum: 'The minimum for {{class}} is a whole number of minutes, from 0 to 60.',
                band: 'Finish {{class}}’s time bands: pick days, a start and an end that differ, and a price.',
                name: 'Give the card a name.',
                publish: 'The rate card was not published.',
            },
            // Inline, per-field messages shown under the field itself (the field already names its class).
            fieldError: {
                rate: 'Enter a price per hour, as a whole amount of 0 or more.',
                minimum: 'Use a whole number of minutes, from 0 to 60.',
                controller: 'Enter a whole amount of 0 or more.',
                bandDays: 'Pick at least one day for this band.',
                bandTime: 'Set a start and a different end, as HH:MM.',
                bandRate: 'Enter this band’s price per hour.',
                name: 'Give the card a name.',
            },
        },
        promotions: {
            title: 'Automatic promotions',
            intro: 'Applied at checkout by rule, with no code to type: happy hours, weekend deals, a first-order welcome.',
            new: 'New promotion',
            listLabel: 'Automatic promotions',
            rulesLabel: 'When {{name}} applies',
            emptyTitle: 'No promotions yet',
            empty: 'A promotion takes money off a sale when its rules match: a day, a time, what is in the cart, where it is sold.',
            unavailable: 'The promotions could not be loaded.',
            notPermitted: 'Your role cannot see promotions, codes or coupons.',
            readOnly: 'Read only: your role can see discount codes but cannot make or change offers.',
            needsManage: 'Only roles that manage discounts can see promotions.',
            stackable: 'Stacks',
            edit: 'Edit',
            editNamed: 'Edit {{name}}',
            pause: 'Pause',
            resume: 'Resume',
            pauseNamed: 'Pause {{name}}',
            resumeNamed: 'Resume {{name}}',
            paused: '{{name}} is paused.',
            resumed: '{{name}} is running again.',
            toggleFailed: 'The promotion could not be changed. Try again.',
        },
        promotionFlow: {
            title: 'New promotion',
            hint: 'Four short steps: what it applies to, the discount, when, and a last look.',
            editTitle: 'Edit promotion',
            editHint: 'Only what you change is saved.',
            stepWhat: 'Applies to',
            stepDiscount: 'Discount',
            stepWhen: 'When and limits',
            stepConfirm: 'Confirm',
            name: 'Name',
            nameHint: 'Staff see it on the cart, like “Weekday happy hour”.',
            kinds: 'What it discounts',
            kindsHint: 'Choose none to discount everything in the cart.',
            // Play time (`play`): a session's pay-as-you-go and wallet play, discounted unit by unit as it is played.
            playHint: 'Play time is discounted per billing unit as it is played: the days and the time of day decide which minutes are cheaper, and the cap limits the discount per session. It stands alone, so choosing it clears the other kinds.',
            playNotApplicable: 'Products, sales channels, a minimum order and the first-order rule do not apply to play time.',
            playPercentOnly: 'Play time is always discounted by a percentage.',
            playNeedsPercent: 'This promotion takes an amount off, so it cannot discount play time. Make a new percentage promotion for play.',
            capSession: 'Most off per session ({{currency}})',
            capSessionHint: 'A ceiling on what one session saves in total. Leave empty for none.',
            products: 'Products',
            someProducts: 'Only some products',
            productsLoading: 'Loading the menu…',
            productsUnavailable: 'The menu could not be loaded, so products cannot be chosen here.',
            noProducts: 'There are no products on the menu yet.',
            productRequired: 'Choose at least one product, or turn this off.',
            where: 'Where',
            everywhere: 'Every venue',
            venueRequired: 'Choose at least one venue, or apply it everywhere.',
            channels: 'Sold through',
            channelsHint: 'Choose none to apply it everywhere a sale is made.',
            firstOrder: 'Only a player’s first order',
            typeFixed: 'The discount type is fixed once a promotion is made.',
            stackable: 'Combines with a code and other stacking promotions',
            stackableHint: 'Otherwise the best single offer wins.',
            priority: 'Priority',
            priorityHint: 'When two promotions that do not stack both apply, the higher one wins. 0 to 1000.',
            priorityRange: 'Enter a whole number from 0 to 1000.',
            days: 'Days',
            daysHint: 'Choose none to run every day.',
            everyDay: 'Every day',
            window: 'Time of day',
            happyHour: 'Only between set times',
            start: 'From',
            end: 'Until',
            windowInvalid: 'Enter both times as HH:MM.',
            windowHint: 'In the venue’s local time. A window may run past midnight.',
            active: 'Running',
            onNow: 'Running as soon as it is saved',
            offNow: 'Saved paused',
            noMinimum: 'No minimum',
            summary: 'Summary',
            create: 'Create promotion',
            created: '{{name}} is saved.',
            saved: '{{name}} is updated.',
            refused: 'The promotion could not be saved. Check the steps and try again.',
        },
        codes: {
            title: 'Discount codes',
            intro: 'Codes staff type at checkout. Each is unique in the organisation and works at every venue.',
            new: 'New code',
            caption: 'Discount codes',
            empty: 'No discount codes yet.',
            unavailable: 'The discount codes could not be loaded.',
            usedOf: '{{used}} of {{limit}}',
            // Switching a code off (`updateDiscountCode`, `active`): checkout refuses it; its history stays.
            inactive: 'Inactive',
            deactivate: 'Deactivate',
            reactivate: 'Reactivate',
            deactivateNamed: 'Deactivate {{code}}',
            reactivateNamed: 'Reactivate {{code}}',
            deactivated: '{{code}} is off. Checkout will refuse it.',
            reactivated: '{{code}} is back on.',
            toggleFailed: 'The code could not be changed. Try again.',
            column: {
                code: 'Code',
                value: 'Discount',
                minOrder: 'Smallest order',
                used: 'Used',
                until: 'Ends',
                status: 'Status',
                actions: 'Actions',
            },
        },
        codeFlow: {
            title: 'New discount code',
            hint: 'The code and what it takes off, then its limits.',
            stepCode: 'Code and discount',
            stepLimits: 'Limits',
            code: 'Code',
            codeHint: '4 to 24 letters, numbers or hyphens, like DIWALI-25.',
            codeInvalid: 'Use 4 to 24 letters, numbers or hyphens.',
            taken: 'That code already exists in this organisation.',
            usageLimit: 'Uses in total',
            usageHint: 'Leave empty for no limit.',
            usageInvalid: 'Enter a whole number from 1 to 1,000,000.',
            perPlayer: 'Uses per player',
            perPlayerInvalid: 'Enter a whole number from 1 to 1,000.',
            create: 'Create code',
            created: '{{code}} is ready to use at checkout.',
            refused: 'The code could not be saved. Check the steps and try again.',
        },
        coupons: {
            title: 'Coupon batches',
            intro: 'Codes to hand out or send that add a bonus to a player’s wallet when redeemed at the counter.',
            new: 'New coupon batch',
            caption: 'Coupon batches',
            empty: 'No coupon batches yet.',
            unavailable: 'The coupon batches could not be loaded.',
            needsManage: 'Only roles that manage discounts can see coupon batches.',
            walletBonus: '{{amount}} wallet bonus',
            singleUse: 'Single use',
            multiUse_one: '{{count}} use per code',
            multiUse_other: '{{count}} uses per code',
            column: {
                name: 'Batch',
                grant: 'Grants',
                codes: 'Codes',
                redeemed: 'Redeemed',
                uses: 'Uses',
                until: 'Ends',
                status: 'Status',
                where: 'Where',
            },
            whereAll: 'Every venue',
            whereVenues: '{{venues}} only',
        },
        // Organisation-wide or one venue — passes and coupon batches (owner 2026-09-30, #709).
        scopeChoice: {
            organization: 'Every venue in the organisation',
            organizationHint: 'Players can use it at any of your venues.',
            venue: 'One venue only',
            venueHint: 'Refused at your other venues, and not shown on their kiosks.',
            pickVenue: 'Venue',
            venueRequired: 'Choose the venue.',
            keep: 'Keep as it is: {{what}}',
            network: 'the whole network',
            severalVenues_one: '{{count}} venue',
            severalVenues_other: '{{count}} venues',
        },
        couponFlow: {
            title: 'New coupon batch',
            hint: 'Name the batch and its bonus, set its rules, then copy or download the codes.',
            stepBatch: 'Batch',
            stepRules: 'Rules',
            stepCodes: 'Codes',
            name: 'Batch name',
            nameHint: 'For you, not players: “Diwali flyers”, “Tournament winners”.',
            grant: 'Wallet bonus per code ({{currency}})',
            grantHint: 'Added to the player’s wallet as bonus money when they redeem a code.',
            quantity: 'How many codes',
            quantityHint: 'Up to 2,000 in one batch.',
            quantityRange: 'Enter a whole number from 1 to 2,000.',
            uses: 'Uses per code',
            usesHint: '1 makes each code single use.',
            usesRange: 'Enter a whole number from 1 to 100,000.',
            perPlayer: 'Codes per player',
            perPlayerHint: 'How many codes of this batch one player may redeem.',
            perPlayerRange: 'Enter a whole number from 1 to 100.',
            where: 'Where they can be redeemed',
            create: 'Create codes',
            created_one: '{{count}} code made for {{name}}.',
            created_other: '{{count}} codes made for {{name}}.',
            refused: 'The batch could not be made. Check the steps and try again.',
            readyTitle_one: '{{count}} code is ready',
            readyTitle_other: '{{count}} codes are ready',
            readyBody: 'Each adds {{amount}} to a player’s wallet.',
            codesLabel: 'Codes in {{name}}',
            onlyOnce: 'The codes are shown only now. Copy or download them before you close this.',
            guardTitle: 'Save the codes before you close',
            guardBody: 'These codes are shown only now and cannot be fetched again. Copy or download them first.',
            copy: 'Copy all',
            copied_one: '{{count}} code copied.',
            copied_other: '{{count}} codes copied.',
            copyFailed: 'The codes could not be copied. Download them instead.',
            download: 'Download CSV',
        },
        memberships: {
            title: 'Memberships are on the way',
            body: 'Monthly and yearly plans with included play time, member prices and priority booking. They arrive with the membership service.',
            areas: ['Plans and perks', 'Included time', 'Member prices', 'Renewals'],
        },
    },
    // The Passes screen (spec/13, DESIGN.md "Pass row"; F-PRC-01/-02/-06): the pass catalogue as rows
    // with a "when it works" band, price, validity and today's count, and a Sell / Grant action.
    passes: {
        title: 'Passes',
        intro: 'The venue’s passes — play time on your station classes, sold as tickets. Sell or grant one to a player.',
        new: 'New pass',
        readOnly: 'Read only: your role cannot change the pass catalogue.',
        notPermitted: 'Your role cannot see this venue’s passes.',
        unavailable: 'The passes could not be loaded.',
        emptyTitle: 'No passes yet',
        empty: 'A pass sells play time on your station classes as a ticket: a day pass, a night pack, a weekend bundle.',
        listLabel: 'Pass products',
        inactive: 'Off sale',
        sellGrant: 'Sell / grant',
        edit: {
            action: 'Edit',
            actionNamed: 'Edit {{name}}',
            title: 'Edit pass',
            hint: 'Change its terms, availability or when it works. Changing a term makes a new version; holdings already sold keep theirs.',
            save: 'Save changes',
            done: '{{name}} saved.',
            noChanges: 'Change something first.',
            refused: 'The pass could not be saved. Check the fields and try again.',
        },
        anytime: 'Any time',
        whenAria: 'When this pass works',
        minutes: '{{count}} min',
        field: {
            price: 'Price',
            duration: 'Play time',
            validity: 'Valid for',
            today: 'Today',
        },
        label: {
            standard: 'Standard',
            day: 'Day',
            night: 'Night',
            weekend: 'Weekend',
            special: 'Special',
            limited: 'Limited',
        },
        day: {
            mon: 'Mon',
            tue: 'Tue',
            wed: 'Wed',
            thu: 'Thu',
            fri: 'Fri',
            sat: 'Sat',
            sun: 'Sun',
            holiday: 'Holidays',
        },
        validity: {
            fixed: 'Until a fixed date',
            afterPurchase: '{{count}} days from purchase',
            afterFirstUse: '{{count}} days from first use',
        },
        validityType: {
            afterPurchase: 'Days from purchase',
            afterFirstUse: 'Days from first use',
        },
        tender: {
            cash: 'Cash',
            counter_card: 'Card',
            counter_upi: 'UPI',
        },
        sell: {
            title: 'Sell or grant · {{name}}',
            hint: 'Find the player, then sell the pass or grant it free with a reason.',
            pickPlayer: 'Pick a player first.',
            refused: 'That could not be done. Check the details and try again.',
            reasonRequired: 'A grant needs a reason.',
            player: 'Player',
            playerHint: 'Search by name, phone or email',
            searching: 'Searching',
            change: 'Change',
            mode: 'Sell or grant',
            sell: 'Sell',
            grant: 'Grant',
            tender: 'Paid by',
            reason: 'Reason for the grant',
            confirmSell: 'Sell the pass',
            // Sell goes through the Sell screen (F-POS-01): a sale in Orders, paid at checkout.
            toCheckout: 'Continue to checkout',
            checkoutHint: 'Opens Sell with this pass in the cart and the player attached. Take payment there: cash, UPI, wallet, on account or split, with any discount code.',
            notAtCounter: 'This pass is not sold at the counter right now (off sale, outside its sale window, members only, or not a Counter pass). You can still grant it.',
            confirmGrant: 'Grant the pass',
            cancel: 'Cancel',
            sold: '{{name}} sold to {{player}}.',
            granted: '{{name}} granted to {{player}}.',
        },
        holdings: {
            action: 'Holdings',
            title: 'Pass holdings',
            hint: 'Find a player, then refund or void one of their passes.',
            listLabel: 'The player’s pass holdings',
            player: 'Player',
            playerHint: 'Search by name, phone or email',
            change: 'Change',
            none: 'This player has no pass holdings here.',
            unavailable: 'The holdings could not be loaded.',
            remaining: '{{time}} left · {{price}}',
            status: {
                active: 'Active',
                exhausted: 'Used up',
                expired: 'Expired',
                refunded: 'Refunded',
                voided: 'Voided',
            },
            refund: 'Refund',
            void: 'Void',
            tender: 'Refund to',
            reason: 'Reason',
            reasonOptional: 'Reason',
            reasonRequired: 'A void needs a reason.',
            refundConfirm: 'Refund the pass',
            voidConfirm: 'Void the pass',
            refunded: 'Pass refunded.',
            voided: 'Pass voided.',
            notRefundable: 'This pass cannot be refunded under its rules.',
            notVoidable: 'This pass cannot be voided — refund it instead.',
            refundFailed: 'The refund could not be done. Try again.',
            voidFailed: 'The void could not be done. Try again.',
            onlyAt: 'Only at {{venue}}',
            anotherVenue: 'another venue',
            notHere: 'Not valid at this venue',
        },
        channel: {
            counter: 'Counter',
            kiosk: 'Kiosk',
        },
        create: {
            title: 'New pass',
            hint: 'Set the price, the play time and when it works. Editing a pass later keeps sold passes on their terms.',
            required: 'Fill this in.',
            priceInvalid: 'Enter a price like 500 or 500.50.',
            durationRange: 'Between 15 minutes and 30 days.',
            daysRange: 'Between 1 and 730 days.',
            classRequired: 'Choose at least one station class.',
            channelRequired: 'Choose where it is sold.',
            channels: 'Sold at',
            channelsHint: 'On the kiosk, players buy it from their PC.',
            daysNeeded: 'Choose at least one day.',
            refused: 'The pass could not be created. Check the fields and try again.',
            name: 'Name',
            shortName: 'Short name',
            shortNameHint: 'For the kiosk ticket, up to 12 characters.',
            price: 'Price ({{currency}})',
            duration: 'Play time (minutes)',
            classes: 'Station classes',
            noClasses: 'Add a station class first.',
            scope: 'Where it works',
            validity: 'Validity',
            days: 'Days',
            label: 'Label',
            when: 'When it works',
            setHours: 'Set hours',
            start: 'From',
            end: 'Until',
            anyTimeHint: 'Works any time of day.',
            description: 'Description',
            active: 'On sale',
            create: 'Create pass',
            cancel: 'Cancel',
            preview: 'Preview',
            namePlaceholder: 'New pass',
            stepBasics: 'Basics',
            stepWhen: 'Where and when',
            stepValidity: 'Validity',
            timeHint: '24-hour time, like 17:00.',
            done: 'Pass {{name}} created.',
        },
    },
    // Console sessions (ADR-0034): a staff-run station is booked — controllers and a block of time — and paid once at the
    // counter when the session ends.
    // Guest passes (walk-ins without an account): the venue's live ones, under Floor.
    guests: {
        link: 'Guests',
        title: 'Guests',
        intro: 'Guest passes sold at the counter to walk-ins without an account. A guest types the ticket code on a PC to play.',
        off: 'This venue does not sell guest passes. Turn guests on in Settings, Sessions.',
        forbidden: 'Your role cannot see this venue’s guest passes.',
        unavailable: 'The guest passes could not be loaded.',
        emptyTitle: 'No guest passes in use',
        emptyAction: 'Open Sell',
        empty: 'Guest passes sold at the counter show here until their time is used or their ticket expires.',
        label: 'Guest · {{code}}',
        column: { code: 'Ticket code', pass: 'Pass', left: 'Time left', until: 'Works until', status: 'Status' },
        status: {
            awaiting_payment: 'Waiting for payment',
            active: 'Ready',
            playing: 'Playing on {{station}}',
            used_up: 'Used up',
            expired: 'Expired',
            refunded: 'Refunded',
            claimed: 'Claimed',
            cancelled: 'Cancelled',
        },
        unknownStation: 'a PC',
        void: {
            action: 'Void',
            named: 'Void guest pass {{code}}',
            title: 'Void guest pass {{code}}',
            intro: 'Refunds the pass and stops the code working. The refund follows the venue’s pass refund rules.',
            tender: 'Refund to',
            reason: 'Reason',
            confirm: 'Refund and void',
            done: 'Guest pass {{code}} voided.',
            notRefundable: 'This pass can’t be refunded under the refund rules. A manager can refund it with a reason.',
            failed: 'That could not be done. Try again.',
        },
    },
    consoleSession: {
        walkIn: 'Walk-in',
        guest: 'Guest · {{code}}',
        minutes: '{{n}} min',
        hours: '{{n}} h',
        hoursMinutes: '{{h}} h {{m}} min',
        start: {
            title: 'Start a console session',
            player: 'Player (optional)',
            walkIn: 'Walk-in — no account',
            walkInHint: 'No player: the order is paid at the counter without one, and players-only offers do not apply.',
            pickPlayer: 'Pick a player, or tick Walk-in.',
            controllers: 'Controllers',
            time: 'Time',
            custom: 'Custom',
            open: 'Open',
            customMinutes: 'Minutes',
            customHint: 'Any length, 1 to 1440 minutes.',
            openHint: 'No end and no alerts: charged for the time played, rounded up to the class’s billing unit, when you end it.',
            minutesRequired: 'Enter the minutes, 1 to 1440.',
            submit: 'Start session',
            done: 'Session started on {{station}} for {{player}}.',
            refused: 'The session could not be started.',
        },
        preview: {
            title: 'Price',
            due: 'Due at the end',
            firstController: '{{block}} · 1st controller',
            extraControllers_one: '{{block}} · 1 extra controller ({{time}})',
            extraControllers_other: '{{block}} · {{count}} extra controllers ({{time}})',
            openBlock: 'Open ({{time}} played)',
            blockPrice: 'block price',
            openRate: '{{amount}} an hour at these controllers, charged for the time played.',
            nothing: 'Nothing to charge yet.',
            failed: 'The price could not be worked out. You can still start the session; the order is priced when it ends.',
            paidAtCounter: 'Paid at the counter when the session ends: cash, UPI, card, on account, or the player’s wallet.',
        },
        // One more paid block on a booked session: "Sell time", as paid time is everywhere (2026-10-07).
        addTime: {
            title: 'Sell time',
            submit: 'Sell {{block}}',
            adds: 'Adds to what is due',
            dueAfter: 'Due at the end after this: {{amount}}.',
            done: 'Sold {{block}}: added to what is due.',
            failed: 'The time could not be added.',
            open: 'An Open session has no end to move: it is charged for the time played.',
        },
        controllers: {
            title: 'Controllers',
            fromNow: 'From this moment on: the time already played keeps its controllers.',
            changes: 'Changes what is due by',
            submit_one: 'Set 1 controller',
            submit_other: 'Set {{count}} controllers',
            done_one: 'Controllers set to 1.',
            done_other: 'Controllers set to {{count}}.',
            failed: 'The controllers could not be changed.',
        },
        due: {
            title: 'Due at the end',
            booked: 'Booked {{time}}',
            open: 'Open — no end',
            controllers_one: '1 controller',
            controllers_other: '{{count}} controllers',
            ordered: 'Order placed: take payment at the counter.',
        },
    },
    // A station's session on the Floor (spec/13 §3.3, F-SES-04/-14/-16/-17/-18/-21): the station modal's Session tab,
    // its funding, timeline and actions, and the forms its action bar opens. (The Sessions screen folded into the
    // Floor on 2026-10-07.)
    sessions: {
        // Offline play (spec/13 §3.6; spec/05 §8): staff codes and the sessions PCs ran offline.
        offline: {
            link: 'Offline play',
            title: 'Offline play',
            forbidden: 'Your role cannot see this venue’s offline play.',
            intro: 'When a PC can’t reach Clavian, it follows this venue’s Offline play settings. Print staff codes ahead of an outage, and settle what PCs ran offline.',
            settingsLink: 'Offline play settings',
            unknownStation: 'Unknown station',
            unavailable: 'The offline sessions could not be loaded.',
            sessionsTitle: 'Played offline',
            sessionsIntro: 'Sessions PCs ran while offline, as they reported when they reconnected.',
            sessionsPending: '{{count}} need settling: record each as paid at the desk (take the payment in Sell) or comp.',
            sessionsEmptyTitle: 'Nothing played offline',
            sessionsEmpty: 'When a PC runs a session while it can’t reach Clavian, it shows up here once it reconnects.',
            resolveFailed: 'That could not be saved. Try again.',
            resolved: { paid: 'Recorded as paid at the desk.', comp: 'Recorded as comp.' },
            column: { station: 'Station', who: 'Player', when: 'When', played: 'Played', charged: 'Charged', status: 'Status' },
            kind: { staffCode: 'Staff code' },
            action: { paid: 'Paid at the desk', comp: 'Comp' },
            resolution: { charged: 'Charged to player', paid_at_desk: 'Paid at the desk', comp: 'Comp' },
            attention: {
                staff_code: 'Needs settling',
                code_reused: 'Code used twice',
                code_unknown: 'Unknown code',
                player_not_known_here: 'Player not known on this PC',
                policy_off: 'Offline sign-in was off',
            },
            codes: {
                title: 'Staff codes',
                intro: 'One-time codes that start a session on any PC here while it is offline. PCs pick up new codes within five minutes while online, so print a sheet before you need it.',
                unavailable: 'The staff codes could not be loaded.',
                empty: 'No staff codes yet.',
                minutes: '{{minutes}} min',
                rowName: '{{minutes}}-minute code',
                revoke: 'Revoke',
                revoked: 'Code revoked. PCs drop it within five minutes.',
                usedOn: 'Used on {{station}}',
                status: { live: 'Live', used: 'Used', revoked: 'Revoked', expired: 'Expired' },
                column: { minutes: 'Minutes', issued: 'Issued', expires: 'Expires', status: 'Status', note: 'Note' },
            },
            issue: {
                count: 'How many codes',
                minutes: 'Minutes each',
                validHours: 'Valid for (hours)',
                note: 'Note (optional)',
                submit: 'Issue codes',
                countRange: 'Between 1 and 50.',
                minutesRange: 'Between 5 and 480 minutes.',
                hoursRange: 'Between 1 and 720 hours.',
                // F-LCK-09: a code either starts play or opens a PC for staff maintenance while offline.
                kind: 'Each code',
                kinds: { session: 'Starts a session', maintenance: 'Opens a PC for maintenance' },
                maintenanceRange: 'A maintenance code opens a PC for at most 240 minutes.',
                failed: 'The codes could not be issued. A venue can hold at most 200 live codes.',
                done: '{{count}} codes issued',
                onceOnly: 'These digits are shown only now. Print them before you close this.',
                print: 'Print sheet',
                close: 'Done',
                listLabel: 'The codes just issued',
            },
            sheet: {
                title: 'Offline staff codes',
                minutes: '{{minutes}} minutes of play',
                how: 'While the PC is offline: tap the lock screen, choose Staff code, and type these digits.',
                expires: 'Use by {{when}}. Works once.',
            },
        },
        drawer: {
            loading: 'Reading the session…',
            freeTitle: 'Free',
            free: 'No one is playing on this station right now.',
            unavailable: 'The session could not be read.',
        },
        field: {
            player: 'Player',
            playerShort: 'Member #{{id}}',
            payingNow: 'Paid from {{source}}',
            charged: 'Charged so far',
            started: 'Started',
            played: 'Played',
            offline: 'Played offline',
            playedOffline: '{{duration}} while the PC was offline',
        },
        quickMinutes: '{{n}} min',
        source: {
            comp: 'Free time',
            reward: 'Reward',
            allowance: 'Allowance',
            pass: 'Pass',
            wallet: 'Wallet',
            postpaid: 'Pay-as-you-go',
            grace: 'Grace',
            offline: 'Offline',
        },
        plan: {
            title: 'Funding',
            empty: 'Nothing funds this session.',
            queued: '+{{duration}} queued',
            now: 'now',
            // The funding breakdown (spec/05 §4.4): each source of the time left, in the order it is used.
            kind: {
                comp: 'Free time',
                wallet: 'Wallet',
                postpaid: 'Added time (on account)',
            },
            passUntil: 'until {{time}}',
            passExpires: 'expires {{when}}',
            costAtRate: '{{amount}} at {{rate}}/h',
            thenGrace: 'Then {{minutes}} min grace',
            thenEnd: 'Then the session ends',
        },
        timeline: {
            title: 'Timeline',
            empty: 'Nothing has happened yet.',
            segmentsTitle: 'Billed segments',
            // A play promotion on a billed segment (F-POS-03): its name and rate, and what it took off.
            segmentPromotion: '{{name}} −{{percent}}% · −{{amount}}',
            // F-SES-21: the rate a segment is charged at, snapshot when it opened.
            segmentRate: '{{amount}}/h, billed per {{unit}}',
            playsTitle: 'Titles played',
            detectedBy: {
                launch: 'launched from the library',
                steam_app: 'Steam',
                install: 'install folder',
                catalogue: 'process match',
                recipe: 'process match',
                default: 'process match',
                process: 'process match',
            },
            offlineTitle: 'Offline',
            offlineGap: 'Ran {{minutes}} min offline (measured by the PC) — {{overrun}} min past the funded end',
            offlineCode: 'Offline code ({{action}}) — {{minutes}} min played',
            offlineSession: 'Offline session — {{minutes}} min played',
            policy: {
                continue_and_settle: 'kept running, settled on reconnect',
                continue_to_lease_end: 'ran to the end of its time',
                lock_immediately: 'locked at once',
                unknown: 'policy not recorded',
            },
            ledgerTitle: 'Ledger',
            ledgerNewestFirst: 'newest first',
            // A run of identical charges, folded: "0:01 each × 27" or "₹5.00 each × 8".
            ledgerEach: '{{each}} each × {{count}}',
            // Between the first and last time of a run: "10:02 to 10:29".
            ledgerRangeTo: ' to ',
            ledgerShowOlder: 'Show older',
            ledgerLoadingOlder: 'Loading older…',
            ledgerShowLatest: 'Back to latest',
            ledgerError: 'The ledger could not be loaded.',
            ledgerRetry: 'Try again',
            reversal: 'reversed',
            entry: {
                segment_charge: 'Charge',
                segment_charge_reversal: 'Charge reversed',
                comp_grant: 'Free time added',
                comp_expire: 'Unused free time expired',
            },
            actorSystem: 'System',
            actorStaff: 'Staff',
            actorStation: 'Station',
            actorPlayer: 'Player',
        },
        event: {
            started: 'Session started',
            comped: '{{minutes}} min of free time added — {{reason}}',
            sold_time: '{{minutes}} min sold',
            pass_added: '{{name}} added from the kiosk — {{minutes}} min queued',
            message_sent: 'Message sent — “{{text}}”',
            warned: '{{minutes}}-minute warning sent',
            locked: 'Screen locked{{reason}}',
            lockedPaused: 'Screen locked{{reason}} — billing paused',
            unlocked: 'Screen unlocked',
            unlockedPaused: 'Screen unlocked — {{minutes}} min locked, not billed',
            grace_started: 'Ran out of funded time — grace began',
            wallet_enabled: 'Player chose to keep playing with their wallet',
            // The same event when the player flips "Use my wallet for play time" on at the kiosk (detail `via: 'kiosk'`).
            wallet_enabled_kiosk: 'Player turned their wallet on for play time',
            wallet_disabled: 'Player turned their wallet off for play time',
            ended: 'Session ended — {{reason}}',
            offline_settled: 'Played {{minutes}} min offline — {{charged}} min charged on reconnect',
            offline_settled_free: 'Played {{minutes}} min offline — nothing extra to charge',
        },
        lockReason: {
            step_away: ' — player stepped away',
            idle: ' — idle auto-lock',
            remote: ' — locked by staff',
        },
        actions: {
            title: 'Actions',
            end: 'End session',
            // One word per action (2026-10-07): paid time is "Sell time" everywhere; a comp is "Add free time".
            sell: 'Sell time',
            lock: 'Lock screen',
            unlock: 'Unlock screen',
            move: 'Move station',
            lockSent: 'Lock sent.',
            unlockSent: 'Unlock sent.',
            lockFailed: 'The lock could not be sent. Try again.',
            cancel: 'Cancel',
        },
        comp: {
            title: 'Add free time',
            minutes: 'Minutes',
            minutesHint: 'Free minutes to add to this session.',
            reason: 'Reason',
            submit: 'Add free time',
            required: 'Fill this in.',
            refused: 'The free time could not be added. Check the fields and try again.',
            limit: 'That is more free time than you may give — in one go, or in this shift.',
            done: 'Added {{minutes}} min of free time.',
            note: 'Note',
            noteHint: 'Say why, for the audit log.',
            noteRequired: 'Add a note saying why.',
        },
        reason: {
            compensation: 'Compensation',
            promo: 'Promotion',
            staff_play: 'Staff play',
            other: 'Other',
        },
        sell: {
            title: 'Sell time',
            minutes: 'Minutes',
            minutesHint: 'Paid minutes to add, charged at the pay-as-you-go rate on account.',
            submit: 'Sell time',
            required: 'Fill this in.',
            refused: 'The time could not be sold. Check the fields and try again.',
            done: 'Sold {{minutes}} min.',
            // F-SES-17: open Sell with this player and session, so a pass goes straight into it.
            pass: 'Sell a pass',
        },
        end: {
            title: 'End this session?',
            body: 'The games close and the final charge is settled to the ledger. This cannot be undone.',
            // ADR-0034: a booked console session places its one order as it ends.
            bodyBooked: 'The session ends and one order is placed for what was booked — take payment at the counter. This cannot be undone.',
            confirm: 'End session',
            refused: 'The session could not be ended. Try again.',
            done: 'Session ended.',
        },
        move: {
            title: 'Move to another station',
            pick: 'Move to',
            choose: 'Choose a station…',
            loading: 'Reading the floor…',
            noFree: 'No other station is free right now.',
            required: 'Choose a station to move to.',
            stationNumber: 'PC {{number}}',
            preview: 'Move here',
            warningsTitle: 'Moving to a different class:',
            confirm: 'Confirm move',
            moved: 'Session moved.',
            failed: 'The session could not be moved. Try again.',
        },
        start: {
            open: 'Start session',
            title: 'Start a session',
            player: 'Player',
            playerHint: 'Name, username or phone',
            noMatch: 'No player matches.',
            change: 'Change',
            pickPlayer: 'Choose a player first.',
            funding: 'How it is paid',
            postpaid: 'Pay as you go',
            comp: 'Free time',
            budget: 'Budget ({{currency}})',
            budgetHint: 'The most the player may spend on account this session. Their passes are used first.',
            // F-SES-09 (spec/05 §4.2): draw the player's wallet before their passes, for a player who holds both.
            walletFirst: 'Use their wallet first and save their pass (when the wallet pays for play here)',
            budgetInvalid: 'Enter an amount, like 150 or 150.50.',
            noCurrency: 'This venue has no currency set, so pay as you go is not available here.',
            minutes: 'Minutes',
            minutesHint: 'Free minutes to start the session with.',
            minutesRequired: 'Fill this in.',
            reason: 'Reason',
            submit: 'Start session',
            // ADR-0033: on a staff-run station, staff may say what is played, so Games played counts it.
            game: 'Game',
            gameHint: 'Optional. Counted in Games played and the player’s history.',
            gameNone: 'Console play (not tracked)',
            done: 'Session started for {{player}}.',
            noFunding: 'Nothing pays for this session. Add a budget, or sell the player a pass.',
            ageRestricted: 'This player is too young for this station’s class.',
            stationUnavailable: 'This station cannot take a session right now.',
            alreadyInSession: 'This player is already playing on another station.',
            notPermitted: 'Your role cannot start sessions.',
            refused: 'The session could not be started. Check the fields and try again.',
        },
    },
    shifts: {
        title: 'Shifts',
        intro: 'A shift is one person’s turn with a cash drawer, from counting the float in to counting the cash out.',
        // The redesigned page (2026-10-08): Now, Movements, History. Plain words, one short explainer where a term needs it.
        now: {
            title: 'Now',
            label: '{{counter}}, open shift',
            since: '{{names}} · since {{time}} ({{duration}})',
            minutes: '{{minutes}} min',
            hoursMinutes: '{{hours}} h {{minutes}} min',
            daysHours: '{{days}} d {{hours}} h',
            expected: 'Expected cash',
            expectedHint: 'What the drawer should hold now',
            cashReceived: 'Cash received',
            salesCount_one: '{{count}} sale',
            salesCount_other: '{{count}} sales',
            changeGiven: 'Change given',
            cashInOut: 'Cash in / out',
            salesLabel: 'Sales',
            countAndClose: 'Count & close',
            handOver: 'Hand over',
            takeOver: 'Take over',
            start: 'Open a shift',
            startAnother: 'Open a shift at another counter',
            allCounters: 'All counters',
            untracked: 'Cash drawers are not counted per shift here.',
        },
        movementsSection: {
            title: 'Movements',
            hint: 'Everything that moved cash in this drawer, oldest first, and what it should hold after each.',
        },
        history: {
            title: 'History',
            hint: 'Closed shifts, newest first. Open one for its count and movements.',
            download: 'Download history',
            empty: 'No closed shifts yet. They appear here once a shift is counted and closed.',
            open: '{{counter}}, {{day}}: open the shift',
            col: { day: 'Day', staff: 'Staff', duration: 'Length', card: 'Card', upi: 'UPI' },
        },
        noShiftReadOnly: 'Nobody holds a cash drawer right now.',
        movementHint: {
            pay_in: 'Cash put into the drawer, such as topping up the float.',
            pay_out: 'Cash paid out of the drawer, such as paying a supplier.',
            drop: 'Cash moved from the drawer to the safe.',
        },
        closeFlow: {
            stepCount: 'Count',
            stepConfirm: 'Confirm',
            review: 'Review',
            difference: 'Difference',
            noteNeeded: 'The count is off by more than {{amount}}. Add a note to close.',
            endHintOpen: 'Count what is in the drawer. The difference from what is expected shows as you count.',
            confirmHint: 'Check the count before it is recorded. Nothing changes until you confirm.',
            countedCash: 'Cash counted',
            notEntered: 'Not entered',
            noNote: 'No note',
            blindAfter: 'The expected amount shows once the shift is closed.',
        },
        openSince: 'Shift open since {{time}}',
        // ADR-0029: the business day runs by itself; a shift is drawer custody. Plain verbs on every button.
        start: 'Start shift — count the float',
        handOver: 'Hand over — count the drawer',
        takeOver: 'Take over — count the drawer',
        end: 'End shift',
        startShort: 'Start shift',
        // The running counters panel (ADR-0032) on Sell and Shifts.
        counters: {
            title: 'Counters',
            introAll: 'Every counter now: who holds its drawer, the cash taken and what the drawer should hold.',
            introOwn: 'Your drawer now: the cash taken and what it should hold.',
            free: 'Free',
            open: 'Open',
            nobody: 'No drawer open.',
            since: '{{names}} · since {{time}}',
            float: 'Float',
            cashSoFar: 'Cash so far',
            expected: 'Expected now',
            byPerson: 'Who took what at {{counter}}',
            person: 'Person',
            cash: 'Cash',
            sales: 'Sales',
            timeline: '{{counter}} — cash timeline',
            timelineTitle: '{{counter}}: cash timeline',
        },
        // "Start your shift" (ADR-0031): the prompt right after sign-in, and the locked Cash tender.
        prompt: {
            title: 'Start your shift',
            intro: {
                per_drawer: 'Pick your counter and count the float into its drawer. Card, UPI and wallet work without a shift — only cash needs one.',
                shared_drawer: 'This venue shares one drawer. Join the shift if it is open, or start it by counting the float. Only cash needs a shift.',
                floor_without_drawer: 'Working the floor? You can take card, UPI and wallet without a shift. To take cash, pick a counter and count its float.',
            },
            counter: 'Counter',
            free: 'Free',
            heldSince: '{{names}} since {{time}}',
            thisDevice: 'This device',
            otherCounter: 'Another counter',
            newCounter: 'New counter',
            counterNameRequired: 'Name the counter you are starting at.',
            countFloat: 'Next — count the float',
            atCounter: 'At {{counter}}: count the float into the drawer.',
            notHandling: 'Not handling cash today',
            floorHint: 'Not taking cash today? Card, UPI and wallet work without a shift.',
            sharedOpen: 'The shared drawer is open',
            sharedSince: 'Since {{time}} · float {{float}}',
            joinHint: 'Join it and the cash you take goes into this drawer; its count belongs to the shift.',
            join: 'Join the shift',
            joined: 'You joined the shift. Cash you take goes into the shared drawer.',
            alreadyHolding: 'You already hold a drawer. Cash you take goes into it.',
            cannotStart: 'Your role cannot start a shift. You can take card, UPI and wallet; ask a colleague with a drawer to take cash.',
            cashLocked: 'Start a shift to take cash. Card, UPI and wallet work without one.',
        },
        noShiftTitle: 'No shift open',
        noShiftBody: 'Open a shift by counting the float into the drawer. Only cash needs a shift; card and UPI work without one.',
        notTracked: 'This venue does not count a cash drawer per shift. Cash sales are still recorded, and the day’s report shows them.',
        notTrackedSettings: 'Change in Settings',
        takeOverHint: '{{name}} holds the drawer. To take over, count it: their shift ends on your count and the next shift is yours.',
        holder: 'Held by {{name}}',
        holderUnknown: 'a colleague',
        // Several drawers, and a shared one (ADR-0029, `shifts.cashHandling`).
        drawers: {
            title: 'Drawers open',
            hint: 'One per counter. Each person’s cash goes into their own drawer.',
            counter: 'Counter',
            on: 'On the drawer',
            since: 'Since',
            float: 'Float',
            actions: 'Actions',
            takeOver: 'Take over',
            end: 'End',
            join: 'Join',
            none: 'You hold no drawer. Start one at your counter, or take one over.',
            counterRequired: 'Another drawer is open: name your counter.',
        },
        join: 'Join the drawer',
        joinHint: '{{names}} {{verb}} the shared drawer. Join it to ring sales into it; the count stays the shift’s.',
        joinVerbOne: 'holds',
        joinVerbMany: 'hold',
        joined: 'You are on the drawer.',
        leave: 'Leave the drawer',
        left: 'You left the drawer.',
        sharedWith: 'Shared with {{names}}',
        spans: 'Started {{day}} · runs across the cutoff',
        // The status strip on Sell and Shifts (ADR-0029).
        strip: {
            label: 'Business day and shift',
            day: 'Business day: {{day}}',
            rolls: 'rolls over at {{time}}',
            auto: 'automatic',
            manual: 'a manager closes it',
            shift: 'Shift: {{name}} · since {{time}} · float {{float}}',
            shiftStarted: 'since {{day}} {{time}}',
            noShift: 'No shift — Start shift to take cash',
            notTracked: 'Cash drawer not tracked',
            help: 'How days and shifts work',
            drawer: '{{counter}}: {{names}} since {{time}}',
            shared: 'Shift: {{names}} · since {{time}} · float {{float}}',
            signOff_one: '{{count}} day to sign off',
            signOff_other: '{{count}} days to sign off',
            previousOpen: 'Yesterday is not closed yet',
        },
        // "How days and shifts work" (ADR-0029): the explainer the strip and the settings open.
        help: {
            title: 'How days and shifts work',
            intro: 'Two separate things: the business day decides which day the money counts on; a shift decides who is responsible for the cash drawer.',
            dayTitle: 'The business day runs by itself',
            day: 'A business day runs from cutoff to cutoff — {{cutoff}} to {{cutoff}} here — so a late night counts on the day it started. Nobody opens a day: it starts when the last one ends.',
            closeManualTitle: 'Closing the day: by a manager',
            closeManual: 'At the end of the day a manager reads the end-of-day report and closes the day, once every shift is counted. After that, sales from the day can no longer be voided; a correction is a refund.',
            closeAutoTitle: 'Closing the day: automatic',
            closeAuto: 'The day rolls over at {{cutoff}} by itself and its end-of-day report is ready. A manager signs it off later. From the rollover, sales from the day can no longer be voided.',
            shiftTitle: 'A shift is the cash drawer',
            shiftStart: 'Start shift: count the float into the drawer. From then on the drawer is yours.',
            shiftHandOver: 'Hand over: the person taking over counts the drawer. Your shift ends on that count and the next shift is theirs.',
            shiftEnd: 'End shift: count what is in the drawer. Clavian shows the difference from what it expected.',
            peopleTitle: 'Two or more people at once',
            perDrawer: 'A drawer per counter: each counter has its own drawer and its own shift. Two counters, two shifts at once; each person’s cash goes into their own drawer.',
            sharedDrawer: 'One shared drawer: one shift, and colleagues join it. Everyone rings into the same drawer; the count and the difference belong to the shift, and each sale still says who rang it.',
            floorWithoutDrawer: 'Floor staff without a drawer: people without a drawer take card, UPI and wallet. Cash goes to whoever holds an open drawer.',
            allDayTitle: 'Open 24×7?',
            allDay: 'Choose automatic close and put the cutoff at your quietest hour. Shifts can run across the cutoff: each sale counts on the day it was paid, and the shift report covers the whole shift.',
            settings: 'Open Days and shifts settings',
            done: 'Got it',
        },
        // Counting the drawer: by notes and coins, or one total.
        count: {
            mode: 'How to count',
            byNotes: 'Notes and coins',
            byTotal: 'One total',
            total: 'Total',
            denomination: '{{amount}}',
            fewer: 'One fewer {{amount}}',
            more: 'One more {{amount}}',
            countOf: 'Number of {{amount}}',
            lineTotal: '= {{amount}}',
        },
        flow: {
            startTitle: 'Start shift',
            startHint: 'Count the cash you put in the drawer. That float is what the drawer starts with.',
            stepFloat: 'Count the float',
            stepConfirm: 'Confirm',
            confirmTitle: 'You will hold the drawer',
            confirmBody: 'Float {{float}} · from now · business day {{day}}',
            started: 'Shift started. The drawer is yours.',
            handOverTitle: 'Hand over',
            handOverHint: 'Count the drawer. The shift ends on this count and the next shift starts with the same cash.',
            handOverOwn: 'Best done signed in as the person taking over: they count, and the drawer becomes theirs. Done from your sign-in, the next shift is yours.',
            takeOverTitle: 'Take over the drawer',
            takeOverHint: 'Count the drawer you are taking. {{name}}’s shift ends on your count and the next shift is yours.',
            endTitle: 'Count & close',
            endHint: 'Count what is in the drawer. The expected amount stays hidden until you finish.',
            confirmTakeOver: 'I counted the drawer and take it over',
            handedOver: 'Handed over. The next shift holds {{amount}}.',
            submitHandOver: 'Hand over',
            submitTakeOver: 'Take over',
            submitEnd: 'Close shift',
            submitStart: 'Start shift',
        },
        // An automatic close and its sign-off (ADR-0029).
        signOff: {
            action: 'Sign off',
            closesAt: 'Closes by itself at {{time}}',
            autoOpen: 'This day rolls over at {{time}} by itself and its report will be ready to sign off. Shifts carry on across the cutoff.',
            autoClosed: 'Closed automatically {{at}}',
            awaiting: 'Waiting for a manager to sign it off. These figures are the day’s record; sales from this day can no longer be voided.',
            signedOff: 'Signed off {{at}}',
            signedBadge: 'Signed off',
            title: 'Sign off {{day}}?',
            body: 'Signing off says you have read this day’s report. It changes no figure.',
            note: 'Note',
            noteHint: 'Anything the owner should know about this day.',
            done: '{{day}} signed off.',
            error: 'The day could not be signed off. Try again.',
            cancel: 'Not yet',
            viewing: 'Showing {{day}}',
            today: 'Back to today',
            review: 'Review {{day}}',
        },
        noCounter: 'Counter',
        movementCount_one: '{{count}} movement',
        movementCount_other: '{{count}} movements',
        opened: 'Shift opened.',
        moved: '{{kind}} recorded.',
        dayClosedToast: '{{day}} closed.',
        counted: 'Counted',
        expected: 'Expected',
        closedTitle: 'Shift closed.',
        closedBody: 'The count is recorded against what the ledger expected. Nothing was adjusted.',
        varianceWord: { even: 'even', over: 'over', short: 'short' },
        step: {
            cash: 'Count cash',
            counter: 'Card and UPI',
            note: 'Note',
            result: 'Result',
            cashHint: 'Count the notes and coins in the drawer. The expected amount stays hidden until you close.',
            counterHint: 'The totals from the card reader and the UPI app, if the counter took any.',
        },
        open: 'Open shift',
        close: 'Close shift',
        current: 'Current shift',
        noShift: 'No shift open',
        noShiftHint: 'Open a till session with an opening float to start taking cash.',
        openedBy: 'Counter',
        openedAt: 'Opened',
        closedAt: 'Closed',
        openingFloat: 'Opening float',
        cashSales: 'Cash sales',
        netMovements: 'Net cash movements',
        figuresLabel: 'The open drawer',
        recordMovement: 'Record cash movement',
        movements: 'Cash movements',
        noMovements: 'No cash movements yet.',
        counter: 'Counter',
        businessDay: 'Business day',
        figure: 'Figure',
        amount: 'Amount',
        reason: 'Reason',
        at: 'Time',
        statusLabel: 'Status',
        status: { open: 'Open', closed: 'Closed' },
        movementKind: { pay_in: 'Cash in', pay_out: 'Payout', drop: 'Drop to safe' },
        dayClose: 'Day close',
        takings: 'Takings',
        tenderLabel: 'Payment method',
        tender: { cash: 'Cash', counter_card: 'Counter card', counter_upi: 'Counter UPI', wallet: 'Wallet' },
        category: { play: 'Play', food: 'Food and drinks', retail: 'Retail', other: 'Other' },
        review: {
            tenders: 'Takings by payment method',
            tendersHint: 'Everything the tills, the card reader, UPI and wallets took today, wallet top-ups included.',
            categories: 'Sales by category',
            categoriesHint: 'What was sold, before tax and discounts. Refunds are shown separately.',
            corrections: 'Voids, refunds, discounts and comps',
            correctionsHint: 'Money given back or never taken.',
            money: 'Beside revenue',
            moneyHint: 'Held for players, owed on account or given away today, and pass time sold ahead of play.',
            shifts: 'Shifts of the day',
            shiftsHint: 'Each drawer: who counted it, and how it came out.',
            taken: {
                title: 'Money taken',
                hint: 'Money in by payment method: cash, UPI and card at the counter, and online. Wallet top-ups included.',
                online: 'Online and other',
                onlineHint: 'Money in not taken at a till',
                total: 'Money in',
                wallets: 'Paid from wallets',
                walletsHint: 'Revenue, not money taken: it came in when the wallet was topped up',
            },
            unusedLabel: 'Not used today',
            notSold: 'Nothing sold in',
            noneToday: 'None today',
            noAdjustments: 'No voids, refunds, discounts or comps today.',
            compsMinutes: '{{minutes}} min',
            compsSessions_one: '{{count}} session',
            compsSessions_other: '{{count}} sessions',
            revenue: 'Revenue',
            refunds: 'Refunds',
            voids: 'Voids',
            countedAmount: '{{amount}} · {{count}}',
            discounts: 'Discounts',
            comps: 'Free time given',
            compsValue_one: '{{minutes}} min · {{count}} session',
            compsValue_other: '{{minutes}} min · {{count}} sessions',
            receivables: 'Receivables raised',
            passSales: 'Pass sales',
            walletTopUps: 'Wallet top-ups',
            cashier: 'Cashier',
            alerted: 'Alerted',
            noShifts: 'No shifts this business day.',
            noShiftsHint: 'A shift opens when a cashier counts a float into a till.',
            closeRefused: 'The day could not be closed.',
            kpi: {
                moneyIn: 'Money in',
                moneyInHint: 'Cash, card, UPI and online, wallet top-ups included',
                revenueHint: 'Payments less refunds. Top-ups count when spent, tabs when paid',
                revenueWalletHint: 'Includes {{wallet}} spent from wallets. Top-ups count when spent',
                noRevenue: 'No payments today',
                variance: 'Cash variance',
                varianceHint_one: 'Across {{count}} closed shift',
                varianceHint_other: 'Across {{count}} closed shifts',
                varianceNone: 'No shift closed yet',
                sessions: 'Sessions',
                sessionsHint: 'Occupancy {{occupancy}}',
                sessionsNoOccupancy: 'Set opening hours to see occupancy',
                walletSpent: 'Paid from wallets',
                walletSpentHint: 'Revenue, not money taken today',
            },
            share: '{{share}} of the day',
            timesCount_one: '{{count}} time',
            timesCount_other: '{{count}} times',
            tenderTotal: 'Total taken',
            unused: 'Not used today: {{tenders}}',
            noTakings: 'Nothing taken today yet.',
            noSales: 'Nothing sold today yet.',
            bridge: {
                title: 'From sales to revenue',
                hint: 'Today’s sales, term by term, to the revenue. Every term is read from the ledger.',
                sales: 'Sales by category',
                discounts: 'Discounts',
                tax: 'Tax collected',
                taxPaidAtTopUp: 'GST paid at top-up',
                refunds: 'Refunds',
                onAccount: 'On account, not paid yet',
                onAccountSettled: 'Earlier tabs paid today',
                walletBonus: 'Paid with wallet bonus',
                walletSettling: 'Wallet payments still settling',
                other: 'Other adjustments',
                revenue: 'Revenue',
                passes: 'Pass sales ({{amount}}) are in Play. A pass counts as revenue when it is sold, not when it is played.',
                walletSpent: '{{amount}} of this revenue was spent from wallets: the money came in when they were topped up.',
                noRevenue: 'There is no revenue to reconcile with today.',
            },
            adjustmentsNone: 'None today: {{items}}',
            notRevenue: {
                walletTopUps: 'In the drawer, but it is the player’s money until they spend it.',
                receivables: 'Owed by players on account. It becomes revenue when they pay.',
                walletCredit: 'Wallet credit given',
                walletCreditHint: 'Bonuses, coupons and manual credits. Not money; it costs revenue only when spent.',
                passSales: 'Already revenue. The time is played later.',
                alreadyRevenue: 'Already counted in revenue',
            },
            confirm: {
                title: 'Close {{day}}?',
                body: 'Closing the day makes today’s review the day’s record.',
                record: 'The figures on this page are kept as they are now.',
                voids: 'Sales from this day can no longer be voided. A correction becomes a refund on the day it is made.',
                summary: 'The owner and managers get the day summary.',
                cancel: 'Keep open',
            },
            closedBanner: 'Closed {{at}}',
            closedBannerHint: 'These figures are the day’s record. Sales from this day can no longer be voided; corrections are refunds on a later day.',
        },
        detail: {
            title: 'Shift',
            tenders: 'Expected and counted',
            note: 'Cashier’s note',
            noNote: 'No note.',
            closedBy: 'Closed by',
            openedByMember: 'Opened by',
            alerted: 'Variance over the alert threshold',
            open: 'View',
            figures: 'The shift’s cash',
        },
        timeline: {
            title: 'Cash timeline',
            hint: 'Every event on this drawer, oldest first, with what it should hold after each.',
            filter: 'Show',
            cashOnly: 'Cash',
            allTenders: 'All payment methods',
            search: 'Order number',
            download: 'Download CSV',
            downloadFailed: 'The timeline could not be downloaded. Try again.',
            kindFilter: 'Kind',
            kinds: { all: 'All', sales: 'Sales', cash: 'Cash in / out', refunds: 'Refunds' },
            noKind: 'Nothing of that kind on this drawer.',
            handOver: 'Received {{received}} · change {{change}}',
            more: 'Show more',
            empty: 'Nothing on this drawer yet.',
            noMatch: 'No events for that order on this drawer.',
            notPermitted: 'This drawer is another member’s. Its cash timeline needs the Read all shifts permission.',
            col: { at: 'Time', event: 'Event', detail: 'Detail', by: 'By', received: 'Cash received', change: 'Change given', amount: 'Amount', balance: 'In drawer' },
            kind: {
                opening_float: 'Opening float',
                handedIn: 'Handed over in',
                sale: 'Sale',
                refund: 'Refund',
                wallet_top_up: 'Wallet top-up',
                wallet_refund: 'Wallet refund',
                pass_sale: 'Pass sale',
                pass_refund: 'Pass refund',
                pay_in: 'Cash in',
                pay_out: 'Payout',
                drop: 'Drop to safe',
                member_joined: 'Joined the drawer',
                member_left: 'Left the drawer',
                closing_count: 'Closing count',
                handed_over: 'Handed over',
                other: 'Other',
            },
            order: 'Order {{number}}',
            notInDrawer: 'Not in drawer',
            counted: 'Counted {{counted}} · expected {{expected}}',
            expectedNow: 'Expected in the drawer now',
            expectedAtClose: 'Expected in the drawer at the count',
            unknown: 'Unknown member',
        },
        closeOthers: 'This shift was opened by another member. Closing it needs the Close any shift permission.',
        variance: 'Variance',
        closeDay: 'Close day',
        dayClosed: 'Day closed',
        openShiftsBlock: 'Close every shift before closing the day ({{count}} still open).',
        recent: 'Shifts',
        emptyShifts: 'No shifts yet.',
        notPermitted: 'You do not have access to shifts at this venue.',
        dialog: {
            open: 'Open shift',
            openHint: 'The float is the cash the drawer starts with.',
            move: 'Cash in / out',
            moveHint: 'Cash put in, paid out or moved to the safe, with the reason.',
            close: 'Close shift',
            closeHint: 'Count the drawer, then the variance against what the ledger expected is shown.',
        },
        field: {
            openingFloat: 'Opening float ({{currency}})',
            counterName: 'Counter',
            kind: 'Movement',
            amount: 'Amount ({{currency}})',
            reason: 'Reason',
            countedCash: 'Counted cash ({{currency}})',
            counterCard: 'Counter card total ({{currency}})',
            counterUpi: 'Counter UPI total ({{currency}})',
            note: 'Note',
            noteHint: 'Required when the variance is over the venue’s threshold.',
            handOver: 'Open the next shift with this cash (hand-over)',
        },
        submit: { open: 'Open shift', move: 'Record', close: 'Close shift' },
        error: {
            amount: 'Enter a valid amount.',
            reason: 'Enter a reason.',
            open: 'The shift could not be opened. Try again.',
            move: 'The cash movement could not be recorded. Try again.',
            close: 'The shift could not be closed. Try again.',
            dayClose: 'The day could not be closed. Try again.',
        },
        // Open drawer (F-OPS-09): a no-sale kick through the desk printer the cash drawer is wired to.
        drawer: {
            open: 'Open drawer',
            title: 'Open the cash drawer',
            hint: 'Opens the drawer with no sale behind it. It is recorded with your name, the shift and the time.',
            reason: 'Reason',
            reasonHint: 'For example: change for a ₹500 note.',
            submit: 'Open drawer',
            done: 'Done',
            noDrawerTitle: 'No cash drawer is set up',
            noDrawer: 'Connect the drawer to a desk printer on the Clavian Counter PC, then turn on its cash drawer in Settings → Printers and hardware.',
            setUp: 'Set up printers',
            forbidden: 'Your role cannot open the drawer.',
            failed: 'The drawer could not be opened. Try again.',
            // How the kick went, as the desk device reports it (`command.updated`, spec/09 §5.1).
            status: {
                queued: 'Drawer kick sent. Waiting for the Counter PC…',
                sent: 'Drawer kick sent to the Counter PC.',
                acked: 'The drawer opened.',
                failed: 'The drawer did not open. Check the printer is on and the drawer cable is connected.',
                expired: 'The Counter PC did not answer in time. Check it is on and online.',
            },
        },
    },
    // Where a print job went (spec/13 §11; F-OPS-08, F-FNB-05): shared by the kitchen board, Orders and Printers.
    printing: {
        scanForInvoice: 'Scan for the invoice',
        sentTo: 'Sent to {{printer}}.',
        kitchenPrinter: 'the kitchen printer',
        receiptPrinter: 'the receipt printer',
        onDisplay: 'The printer is offline — the ticket stays on the display. Staff have been alerted.',
        popupBlocked: 'The print window was blocked. Allow pop-ups for this site, then print again.',
        failed: 'It could not be printed. Try again.',
    },
    // The venue's printers, in Settings → Printers and hardware (spec/13 §11, F-OPS-08/-09, F-STN-13).
    printers: {
        title: 'Printers',
        hint: 'Receipt, kitchen and bar printers, what each prints, and the cash drawer.',
        notPermitted: 'Your role cannot manage this venue’s printers.',
        unavailable: 'The printers could not be loaded.',
        empty: 'Receipt, kitchen and bar printers, and the cash drawer on a receipt printer. Until you add one, receipts and kitchen tickets print from the browser.',
        listLabel: 'This venue’s printers',
        summary_one: '{{count}} printer',
        summary_other: '{{count}} printers',
        add: 'Add printer',
        pc: 'PC {{number}}',
        counterPc: 'a Counter PC',
        width: '{{width}} mm',
        noPurposes: 'Prints nothing yet',
        drawerOn: 'Cash drawer on pin {{pin}}',
        fallbackTo: 'Falls back to {{name}}',
        deskVia: 'Through {{pc}} at {{where}}',
        lastError: 'Last error: {{error}}',
        lastSeen: 'Last heard from {{time}}',
        actionsFor: 'Actions for {{name}}',
        test: 'Test print',
        testFor: 'Test print on {{name}}',
        testTitle: 'Test page · {{name}}',
        edit: 'Edit',
        editFor: 'Edit {{name}}',
        archive: 'Archive',
        archiveFor: 'Archive {{name}}',
        archiveConfirm: 'Archive {{name}}? It stops receiving jobs.',
        keep: 'Keep it',
        kind: {
            desk: 'Desk printer',
            cloud: 'Cloud printer',
            browser: 'Browser printing',
        },
        kindHint: {
            desk: 'Connected to a Clavian Counter PC, over the network (port 9100) or USB.',
            cloud: 'A Star CloudPRNT or Epson printer that collects its jobs over the internet.',
            browser: 'The console opens a page sized for the roll, and you print it.',
        },
        status: {
            online: 'Online',
            offline: 'Offline',
            unknown: 'Not heard from yet',
        },
        purpose: {
            receipt: 'Receipts',
            kitchen: 'Kitchen tickets',
            bar: 'Bar tickets',
            day_close: 'Day close',
        },
        protocol: {
            star_cloudprnt: 'Star CloudPRNT',
            epson_sdp: 'Epson Server Direct Print',
        },
        transport: {
            tcp: 'Network (IP address)',
            spooler: 'USB (Windows printer)',
        },
        content: {
            kitchen_ticket: 'Kitchen ticket',
            receipt: 'Receipt',
            test: 'Test page',
            day_close: 'Day close',
        },
        target: {
            browser: 'Browser',
            cloud: 'Cloud printer',
            device: 'Counter PC',
            display: 'Kitchen display',
        },
        jobStatus: {
            queued: 'Queued',
            sent: 'Sent',
            printed: 'Printed',
            failed: 'Failed',
            expired: 'Expired',
        },
        // A cloud printer's polling URL: shown once, when it is added or its key is rotated.
        pollUrl: {
            title: 'Polling URL for {{name}}',
            hint: 'Enter this URL in the printer’s cloud printing settings.',
            label: 'Polling URL',
            copy: 'Copy',
            copied: 'Polling URL copied.',
            once: 'It will not be shown again. If it is lost, issue a new one from the printer’s settings.',
            dismiss: 'I have saved it',
        },
        jobs: {
            title: 'Recent print jobs',
            hint: 'Where each job went and how it ended, newest first.',
            caption: 'Recent print jobs at this venue',
            empty: 'Nothing has been printed yet.',
            unavailable: 'The print jobs could not be loaded.',
            where: '{{printer}} · {{target}}',
            archived: 'An archived printer',
        },
        sheet: {
            addTitle: 'Add a printer',
            editTitle: 'Edit {{name}}',
            intro: 'How it connects, then what it prints.',
            stepType: 'Type',
            stepConnection: 'Connection',
            stepRouting: 'Routing',
        },
        form: {
            kind: 'What kind of printer is it?',
            station: 'Clavian Counter PC',
            stationHint: 'The desk PC the printer is connected to.',
            stationRequired: 'Choose the Counter PC the printer is connected to.',
            noCounterTitle: 'Pair a Clavian Counter PC first',
            noCounter: 'A desk printer is driven by the front-desk PC running Clavian Counter — it gets no lockdown. Download Clavian setup on that PC, run it and choose Front desk PC, pair it in Stations → Add station → Pair device with the code it shows, and come back.',
            toStations: 'Go to Stations',
            transport: 'Connected by',
            host: 'IP address or host name',
            hostHint: 'The printer’s address on the venue network, for example 192.168.1.50.',
            hostRequired: 'Enter the printer’s IP address.',
            port: 'Port',
            badPort: 'Enter a port from 1 to 65535.',
            queueName: 'Windows printer name',
            queueHint: 'Exactly as the Counter PC lists it under Printers.',
            queueRequired: 'Enter the Windows printer name.',
            protocol: 'Cloud protocol',
            cloudHint: 'Once it is added, you get a polling URL to enter on the printer.',
            cloudEditHint: 'The polling URL was shown when the printer was added.',
            rotate: 'Issue a new polling URL',
            rotateHint: 'The old URL stops working at once.',
            browserHint: 'Nothing to connect: the console opens a page sized for the roll and your browser prints it.',
            name: 'Name',
            nameHint: 'For example: Counter receipts, Kitchen.',
            nameRequired: 'Enter a name.',
            purposes: 'What it prints',
            purposesHint: 'Each job goes to the printer for its purpose.',
            paperWidth: 'Paper roll',
            fallback: 'When it is offline, send jobs to',
            fallbackHint: 'A kitchen ticket with no printer left stays on the kitchen display.',
            noFallback: 'No other printer',
            cashDrawer: 'A cash drawer is connected to this printer',
            cashDrawerHint: 'Open drawer and cash payments kick it through the printer.',
            drawerPin: 'Drawer kick pin',
            pin: 'Pin {{pin}}',
            create: 'Add printer',
            save: 'Save changes',
            added: '{{name}} added.',
            saved: '{{name}} saved.',
            conflict: 'That Counter PC or fallback printer cannot be used for this printer.',
            failed: 'The printer could not be saved. Try again.',
        },
    },
    settings: {
        title: 'Settings',
        // The page intro and every row's source line use the same words (spec/13 §13, 2026-10-07).
        intro: 'Each setting says where its value comes from: the default, head office, your organisation or this venue. Change one here, or reset it to follow the level above.',
        groupsNav: 'Settings groups',
        // The landing: a starting point, the search and the groups.
        landing: {
            groupsTitle: 'All settings',
            count_one: '{{count}} setting',
            count_other: '{{count}} settings',
        },
        // The search at the top of every settings screen (spec/13 §13): labels, help, choices and the usual synonyms.
        search: {
            label: 'Search settings',
            placeholder: 'Search settings: grace, GST, USB, receipts…',
            results: 'Matching settings',
            none: 'No setting matches “{{query}}”.',
            inGroup: '{{group}} · {{section}}',
            hint: 'Type two or more letters.',
        },
        // A first-time checklist on a venue's settings (spec/13 §13): the settings a new café sets first.
        // The job groups (spec/13 §13): grouped by what a café owner is doing, not by the code behind them.
        group: {
            venue: 'Venue',
            timeAndPricing: 'Time & pricing',
            sessionsAndPcs: 'Sessions & PCs',
            money: 'Money',
            cashAndShifts: 'Cash & shifts',
            foodAndOrders: 'Food & orders',
            playersAndSignIn: 'Players & sign-in',
            staffLimits: 'Staff & limits',
            hardware: 'Hardware',
            security: 'Security',
            notifications: 'Notifications',
            privacyAndRecords: 'Privacy & records',
            clavianBoot: 'Clavian Boot',
        },
        groupHint: {
            venue: 'Status, details, opening hours, holidays and closing time.',
            timeAndPricing: 'Pay-as-you-go, grace and time warnings.',
            sessionsAndPcs: 'Stepping away, clearing PCs, offline play and the kiosk screen.',
            money: 'Payments, wallets, pay later, refunds and tax.',
            cashAndShifts: 'The business day, cash drawers and counters.',
            foodAndOrders: 'Kitchen and ordering from the PCs.',
            playersAndSignIn: 'Accounts, sign-in codes, age and ID checks.',
            staffLimits: 'Discounts, refunds and free time staff can give, and staff sign-in.',
            hardware: 'Printers, power, PC alerts and station software.',
            security: 'Programs, USB drives, browser extensions and Windows settings.',
            notifications: 'Receipts, staff alerts and messages to players.',
            privacyAndRecords: 'What players are told, and how long records are kept.',
            clavianBoot: 'How your boot server answers PCs, keeps their changes and caches images.',
        },
        // Sub-headings inside a group.
        section: {
            profile: 'Venue profile',
            brand: 'Your brand',
            businessDetails: 'Business details',
            payg: 'Pay-as-you-go',
            grace: 'Grace',
            warnings: 'Warnings before time runs out',
            passes: 'Passes',
            away: 'Stepping away',
            cleanup: 'Between players',
            offline: 'Offline play',
            kioskScreen: 'Kiosk screen',
            calls: 'Calls for help',
            payments: 'Payments',
            wallet: 'Wallets and pay later',
            refunds: 'Refunds',
            tax: 'Tax and invoices',
            taxCategories: 'Tax categories',
            day: 'Business day',
            drawers: 'Shifts and cash drawers',
            counters: 'Counters',
            kitchen: 'Kitchen',
            pcOrders: 'Ordering from the PCs',
            stock: 'Stock',
            accounts: 'Accounts and guests',
            codes: 'Sign-in codes',
            migration: 'Players moved from another system',
            sales: 'Sales outreach',
            age: 'Age and ID checks',
            limits: 'Limits',
            security: 'Staff sign-in',
            printers: 'Printers',
            power: 'Power and Wake-on-LAN',
            alerts: 'PC health alerts',
            software: 'Station software',
            apps: 'Programs',
            devices: 'USB drives and browsers',
            windows: 'Windows settings',
            receipts: 'Receipts',
            staffAlerts: 'Staff alerts',
            players: 'Messages to players',
            privacy: 'Privacy',
            records: 'Records',
            bootNetwork: 'Network',
            bootWriteback: 'Changes during a session',
            bootCache: 'Caches',
            bootRollout: 'New image versions',
            bootServerUpdates: 'Boot server software',
            other: 'More settings',
        },
        advanced: {
            title: 'Advanced',
            show_one: 'Show {{count}} setting you rarely need',
            show_other: 'Show {{count}} settings you rarely need',
            hide: 'Hide the advanced settings',
        },
        // A dependant collapses under its master while the master says it does nothing.
        depends: {
            show_one: 'Show the setting anyway',
            show_other: 'Show the {{count}} settings anyway',
            hide: 'Hide them',
            // Why, by the master's key (dots as `__`).
            sessions__payAsYouGoEnabled: 'Turn on pay-as-you-go to use these.',
            sessions__paygHourlyRate: 'Set a pay-as-you-go price to use this.',
            wallet__enabled: 'Turn on wallets to use these.',
            inventory__enabled: 'Turn on stock tracking to use this.',
            sessions__graceSeconds: 'Grace is off. Set a grace period to use these.',
            cleanup__profile: 'Used only when PCs are cleared with Full.',
            offline__continueRunning: 'Turn on keeping running sessions going to use this.',
            offline__knownPlayers: 'Turn on known players to use these.',
            stations__setupBeforePlay: 'Turn on setting up PCs before play to use this.',
            credit__enabled: 'Turn on pay later to use this.',
            tax__playTaxed: 'Turn on GST on play to use this.',
            shifts__enabled: 'Turn on counting the drawer per shift to use these.',
            sessions__guestsEnabled: 'Turn on serving guests to use this.',
            boot__shaderCache: 'Turn on keeping the shader cache to use this.',
            auth__otpChannel: 'Used only when codes go by both SMS and WhatsApp.',
            compliance__idCheckRequired: 'Used only with the ID check shown above.',
            station__idlePowerAction: 'Choose Sleep or Shut down to use this.',
            station__clientChannel: 'Used only when updates are pinned to one version.',
            fallback: 'Not used with the setting above as it is.',
            // A master on another screen: the venue reads its state, and can open it.
            elsewhere: '{{setting}}: off for your organisation, so these do nothing yet.',
            elsewhereOn: '{{setting}}: on for your organisation.',
            openElsewhere: 'Change it in organisation settings',
        },
        overridden: {
            offline__strictLock: 'Lock everything while offline is on, so this has no effect.',
        },
        // Where the value in force comes from, as a badge (the same words as the intro).
        source: {
            default: 'Default',
            platform: 'Head office',
            organization: 'Your organisation',
            venue: 'This venue',
        },
        // The same, inside a sentence.
        sourceName: {
            default: 'the default',
            platform: 'head office',
            organization: 'your organisation',
            venue: 'this venue',
        },
        // Each row's source line (spec/13 §13): the value in force, in plain words, and where it comes from.
        using: {
            default: 'Using the default — {{value}}',
            platform: 'Using head office’s default — {{value}}',
            organization: 'Using your organisation’s setting — {{value}}',
            venue: 'Set for this venue — {{value}}',
        },
        changed: {
            venue: 'Changed for this venue',
            organization: 'Changed for your organisation',
            platform: 'Set for the whole platform',
        },
        resetTo: {
            default: 'Reset to the default {{value}}',
            platform: 'Reset to head office’s {{value}}',
            organization: 'Reset to your organisation’s {{value}}',
            venue: 'Reset to this venue’s {{value}}',
        },
        // Why a row cannot be changed here.
        why: {
            role: 'Your role can’t change this.',
            organization: 'Set for the whole organisation.',
            openOrganization: 'Change it in organisation settings',
            dated: 'Changed through the tax registration above.',
        },
        locked: {
            badge: 'Set by head office',
            platform: 'Head office has locked this for every organisation, so it can’t be changed here.',
            organization: 'Head office has locked this for your organisation, so it can’t be changed here.',
        },
        // HQ's own lock, on a screen that may lock the value it stores (spec/03 §9): the platform's, or
        // one organisation's.
        lock: {
            platform: 'Lock it for every organisation and venue',
            organization: 'Lock it for this organisation and its venues',
            badge: 'Locked here',
            bindsPlatform: 'No organisation or venue can change it while it is locked.',
            bindsOrganization: 'The organisation and its venues cannot change it while it is locked.',
            resetLifts: 'The reset lifts the lock too.',
        },
        reset: 'Reset to inherited: {{value}}',
        // A setting with a unit (money) names its registry default under the control (spec/13 §13).
        defaultValue: 'Default: {{value}}',
        resetPending: 'Goes back to {{value}}, from {{source}}, when you save.',
        undoReset: 'Keep this value',
        placeOverrides_one: '{{count}} station class or zone has a value of its own.',
        placeOverrides_other: '{{count}} station classes or zones have values of their own.',
        // Per-class values (spec/13 §12, §13): which classes use their own, and where to change them.
        classValues_one: '{{classes}} uses its own value.',
        classValues_other: '{{classes}} use their own values.',
        classValuesLink: 'Change it per class in Stations → Classes',
        // The organisation's screen (spec/13 §13): which venues keep a value of their own.
        venueValues_one: '{{count}} venue uses its own value',
        venueValues_other: '{{count}} venues use their own value',
        venueValue: '{{venue}}: {{value}}',
        unknownVenue: 'A venue you can’t open',
        save: 'Save changes',
        discard: 'Discard changes',
        noChanges: 'No unsaved changes.',
        unsaved_one: '{{count}} unsaved change.',
        unsaved_other: '{{count}} unsaved changes.',
        saved_one: 'Saved. {{count}} change.',
        saved_other: 'Saved. {{count}} changes.',
        value: {
            on: 'On',
            off: 'Off',
            notSet: 'Not set',
            none: 'None',
            items_one: '{{count}} item',
            items_other: '{{count}} items',
            anyTime: 'Any time',
        },
        // How a number reads with its unit (the stored value never changes).
        unit: {
            seconds_one: '{{count}} second',
            seconds_other: '{{count}} seconds',
            minutes_one: '{{count}} minute',
            minutes_other: '{{count}} minutes',
            hours_one: '{{count}} hour',
            hours_other: '{{count}} hours',
            days_one: '{{count}} day',
            days_other: '{{count}} days',
            months_one: '{{count}} month',
            months_other: '{{count}} months',
            years_one: '{{count}} year',
            years_other: '{{count}} years',
            percent: '{{value}}%',
            celsius: '{{value}} °C',
            perWeek_one: '{{count}} a week',
            perWeek_other: '{{count}} a week',
            perMonth_one: '{{count}} a month',
            perMonth_other: '{{count}} a month',
            shortMinutes: '{{count}} min',
            // The word beside a number field.
            word: {
                seconds: 'seconds',
                minutes: 'minutes',
                hours: 'hours',
                days: 'days',
                months: 'months',
                years: 'years',
                percent: '%',
                celsius: '°C',
                week: 'a week',
                month: 'a month',
            },
            in: 'In',
            whole: 'Enter a whole number.',
            between: 'Enter a number from {{min}} to {{max}}.',
            percentInvalid: 'Enter a percentage like 10 or 12.5, from 0 to 100.',
            percentFraction: 'Use at most 2 digits after the decimal point.',
        },
        conflict: {
            title: 'Changed elsewhere',
            body: 'Someone saved these settings after you opened them. Their values are loaded now; your unsaved changes are beside them.',
            caption: 'Your unsaved changes beside the values saved now',
            setting: 'Setting',
            theirs: 'Saved now',
            yours: 'Your change',
            yourReset: 'Reset to {{value}}',
            andLocked: '{{value}}, locked',
            andUnlocked: '{{value}}, unlocked',
            nowReadOnly: 'can no longer be changed here',
            reapply: 'Re-apply my changes',
            keepTheirs: 'Keep the saved values',
            reapplied: 'Your changes are back on top of the latest values. Save to keep them.',
        },
        errors: {
            locked: 'Head office locked one of these settings while you were editing. It is shown locked now.',
            entitlement: 'A setting you changed is not in the organisation’s plan.',
            forbidden: 'Your role does not allow this change.',
            validation: 'Some values were refused. They are marked below.',
            slugTaken: 'Another venue already uses this slug.',
            unparsed: 'A value is not valid yet. Finish it, or discard it, then save.',
        },
        // Pay-as-you-go's home is Pricing → Pay as you go; Settings shows its summary (spec/13 §13, §18).
        payg: {
            summaryOn: 'On · {{rate}} an hour, charged in steps of {{step}}',
            summaryOff: 'Off · players play on passes and time staff add',
            summaryNoRate: 'On · no price set yet',
            where: 'Pay-as-you-go — its price, billing step, the spend allowed at sign-in and wallets paying for play — is set in Pricing.',
            open: 'Open Pricing → Pay as you go',
        },
        // Whether players can pay online here: a read-only line above the Payments settings.
        onlineStatus: {
            label: 'Online payments',
            active: 'Online payments: active',
            pending: 'Online payments: pending verification',
            action_required: 'Online payments: needs attention',
            not_onboarded: 'Online payments: not set up by the platform yet',
            rejected: 'Online payments: unavailable',
            suspended: 'Online payments: unavailable',
            noProvider: 'Online payments are not available yet',
        },
        // The limits a role can lower (spec/07 §6.4): the lower of the two applies.
        limits: {
            title: 'The lower limit applies',
            body: 'Each role can have lower limits of its own. A staff member gets the lower of this and their role’s limit.',
            link: 'See each role’s limits in Staff → Roles',
        },
        // The three age rules, explained together.
        ageRules: {
            title: 'Three rules decide who may play',
            minAge: 'The youngest age to play applies at every PC.',
            classAge: 'A station class can set a higher age limit — for example 18 for a VR room.',
            verified: 'Trusting only checked dates of birth makes those limits use a date of birth staff have checked.',
            classLink: 'Set class age limits in Stations → Classes',
        },
        // What a sub-option is to the setting above it, by the master's key: one receipt setting, read as one.
        under: {
            notifications__receiptDefault: 'Whatever checkout does, a paid sale can also be emailed by itself:',
        },
        // The Offline play section's opening note (spec/05 §8.1): what each option does, and how they combine.
        offlinePlay: {
            title: 'What a PC does when it can’t reach Clavian — an internet outage, or a server update',
            keepRunning: 'Keep running sessions going: a session plays on to its paid time, then up to the extra minutes. Time beyond the usual grace is charged to the player when the PC is back online. On by default, 5 minutes.',
            staffCodes: 'Staff codes: one-time codes from Floor → Offline play, printed ahead of time, start a session on any PC here. They wait there to be settled as paid at the desk or comp.',
            knownPlayers: 'Known players: a player who recently signed in with a password on a PC can sign in there again offline. The PC keeps only a salted hash. Their time is charged to their wallet or account on reconnect, up to the limit.',
            strictLock: 'Lock everything when offline overrides the rest: running sessions end at their paid time and nobody can sign in.',
        },
        // Settings → Cash & shifts → Counters (ADR-0032).
        counters: {
            intro: 'A shift starts at one of these counters. Each can have a default float and the Clavian Counter PC there (for its receipt printer and cash drawer). Archive a counter you no longer use; its past shifts keep its name.',
            listTitle: 'Counters',
            summary_one: '{{count}} counter',
            summary_other: '{{count}} counters',
            summaryArchived: '{{active}} · {{archived}} archived',
            emptyText: 'A counter is where a cash drawer sits — the front desk, a café bar. A shift starts at one, with its float. Add your first counter to start taking cash.',
            empty: 'No counters yet. Add the first one below.',
            addTitle: 'Add a counter',
            editTitle: 'Edit {{name}}',
            name: 'Name',
            float: 'Default float ({{currency}})',
            pc: 'Clavian Counter PC',
            pcHint: 'The counter PC at this counter, for its receipt printer and cash drawer.',
            noPc: 'No Counter PC',
            pcNumber: 'PC {{number}}',
            rowMeta: 'Default float {{float}} · {{pc}}',
            add: 'Add counter',
            added: '{{name}} added.',
            edit: 'Edit {{name}}',
            editShort: 'Edit',
            editLabel: 'Edit {{name}}',
            save: 'Save',
            archive: 'Archive {{name}}',
            archiveShort: 'Archive',
            restore: 'Restore {{name}}',
            restoreShort: 'Restore',
            archived: 'Archived',
            archivedToast: '{{name}} archived.',
            restoredToast: '{{name}} restored.',
            invalid: 'Give the counter a name, and the float as an amount.',
            failed: 'The counter could not be saved.',
            drawerOpen: 'A drawer is open here: close its shift before archiving it.',
        },
        // Days and shifts (ADR-0029): the two ways a day ends, which suits whom, and where the cutoff is set.
        daysAndShifts: {
            title: 'Two ways to end the business day',
            manual: 'Manual (the default): a manager reads the end-of-day report and closes the day once every shift is counted. Suits a venue that closes for the night.',
            auto: 'Automatic: the day rolls over at the cutoff by itself and its report waits for a manager to sign it off. Recommended for venues open 24×7 — put the cutoff at your quietest hour.',
            cutoff: 'This venue’s day runs from {{cutoff}} to {{cutoff}}. The cutoff is set in the Venue profile; a change takes effect from the next business day.',
            cutoffLink: 'Venue profile',
            peopleTitle: 'When two or more people work at once',
            perDrawer: 'A drawer per counter (the default): each counter has its own drawer and shift, several at once.',
            sharedDrawer: 'One shared drawer: one shift that colleagues join; the count belongs to the shift.',
            floorWithoutDrawer: 'Floor staff without a drawer: they take card, UPI and wallet; cash goes to someone with an open drawer.',
            help: 'How days and shifts work',
        },
        // Clearing PCs (F-LCK-07, spec/11 §5): the callout shown when clearing is set to Off.
        stationsAndLocker: {
            cleanupOffTitle: 'Clearing is off — not recommended',
            cleanupOffWarning: 'With clearing off, a PC keeps everything between players except the kiosk’s own data — the next player can reach the last one’s browsing, downloads and game-launcher sign-ins. Only use Off if you reset each PC’s disk another way.',
        },
        empty: 'Nothing in this group can be set here yet.',
        unavailable: 'These settings could not be loaded.',
        readOnly: 'Read only: your role cannot change these settings.',
        // Opening hours and any other time ranges (the week editor, a day's ranges, a holiday).
        hours: {
            open: 'Open',
            closed: 'Closed',
            dayState: '{{day}}: open or closed',
            nextDay: 'next day',
            allDay: 'all day',
            open24: 'Open 24 hours',
            open24For: 'Open 24 hours on {{day}}',
            rangeNumber: ', range {{n}}',
            removeRange: 'Remove range {{n}} on {{row}}',
            addRange: 'Add hours',
            anotherRange: 'Add a break',
            onRow: 'on {{row}}',
            copyToAll: 'Copy to all days',
            applyToAll: 'Apply to all days',
            fromRow: 'from {{row}}',
            timezone: 'Times are in {{timezone}}.',
            quickTitle: 'Quick setup',
            sameEveryDay: 'Same hours every day',
            sameOpens: 'Every day opens',
            sameCloses: 'Every day closes',
            applySame: 'Apply to every day',
            presets: 'Or start from',
            preset: {
                lateNight: 'Every day 10 am – 2 am',
                allDay: 'Open 24 hours, every day',
                weekdaysWeekends: 'Weekdays 12 pm – 12 am, weekends 10 am – 2 am',
            },
            overnight: 'A closing time earlier than the opening time runs past midnight into the next day, and that night belongs to the day it opened. Closing time drives the closing warnings and what happens to running sessions:',
            closingLink: 'At closing time',
            businessDay: 'Takings after midnight belong to the business day, which ends at the cutoff set under Details.',
            // Problems, in plain words.
            missingTime: 'Set an opening and a closing time for {{day}}, or mark it closed.',
            sameTime: 'Opening and closing at the same time means open 24 hours on {{day}}.',
            summary: {
                closedAllWeek: 'Closed all week',
                everyDay: 'Open 7 days · {{hours}}',
                allDay: 'Open 24 hours',
                closed: 'Closed',
                group: '{{days}} {{hours}}',
                separator: ' · ',
                and: ', ',
                range: '{{from}} – {{to}}',
                nextDay: '{{time}} (next day)',
            },
            dayShort: {
                mon: 'Mon',
                tue: 'Tue',
                wed: 'Wed',
                thu: 'Thu',
                fri: 'Fri',
                sat: 'Sat',
                sun: 'Sun',
            },
            dayRange: '{{from}}–{{to}}',
        },
        // The venue's holiday calendar (spec/13 §13): a list of dated rows, each added and edited in a small dialog.
        holidays: {
            title: 'Holidays',
            summary_one: '{{count}} holiday this year',
            summary_other: '{{count}} holidays this year',
            summaryNone: 'None this year',
            summaryPast: '{{count}} past',
            emptyText: 'Days the venue is closed or keeps different hours — a festival, a private event. Players see them on the venue page, and pass and rate windows can treat them as holidays.',
            add: 'Add holiday',
            addTitle: 'Add a holiday',
            editTitle: 'Edit {{name}}',
            edit: 'Edit {{name}}',
            editShort: 'Edit',
            remove: 'Remove {{name}}',
            past: 'Past',
            closed: 'Closed',
            regular: 'Regular hours',
            special: 'Special hours',
            name: 'Name',
            namePlaceholder: 'Diwali',
            date: 'Date',
            hoursKind: 'That day',
            hoursKindLabel: 'Hours on the holiday',
            save: 'Save holiday',
            cancel: 'Cancel',
            nameRequired: 'Give the holiday a name.',
            dateRequired: 'Choose its date.',
            dateTaken: 'There is already a holiday on that date.',
            hoursRequired: 'Set the special hours, or choose Closed or Regular hours.',
            quickTitle: 'Or add a national holiday',
            quick: '{{name}} · {{date}}',
            addQuick: 'Add {{name}} on {{date}}',
            national: {
                republicDay: 'Republic Day',
                independenceDay: 'Independence Day',
                gandhiJayanti: 'Gandhi Jayanti',
                christmas: 'Christmas Day',
                newYear: 'New Year’s Day',
            },
            saveHint: 'Holidays are saved with the venue profile’s Save.',
        },
        // The kiosk's top-up amounts and extras, with a preview of its tiles (F-WAL-03, F-KIO-10; 2026-10-01).
        topUp: {
            presetLabel: 'Amount {{number}}',
            addPreset: 'Add an amount',
            presetInput: 'Top-up amount',
            presetsEmpty: 'None set: the PC offers the smallest top-up and each extra’s threshold.',
            offerMin: 'Extra {{number}}: top-up of at least',
            offerBonus: 'Gets this much extra',
            addOffer: 'Add an extra',
            removeOffer: 'Remove extra {{number}}',
            editOffer: 'Edit extra {{number}}',
            offerTitle: 'Top up at least {{min}}',
            offerMeta: 'Gets {{bonus}} extra',
            offerAddTitle: 'Add a top-up extra',
            offerEditTitle: 'Edit the top-up extra',
            offerMinField: 'Top-up of at least',
            offerBonusField: 'Extra it gets',
            offerSave: 'Save extra',
            offerInvalid: 'Enter both amounts.',
            offersEmpty: 'No extras: a top-up adds exactly what is paid. Add one to reward bigger top-ups — add ₹500, get ₹50 extra.',
            offersSummary_one: '{{count}} extra',
            offersSummary_other: '{{count}} extras',
            previewTitle: 'What the PC shows',
            previewPresets: 'Your amounts, cheapest first, each with the extra it earns. Players can also type their own amount.',
            previewDerived: 'No amounts set, so the PC offers the smallest top-up and each extra’s threshold. Players can also type their own amount.',
            extra: '+{{bonus}} extra',
            noExtra: 'No extra',
            bestValue: 'Best value',
            otherAmount: 'Other amount',
            otherHint: 'Any amount from {{min}} to {{max}}',
            otherHintPlain: 'Any amount within your organisation’s top-up limits',
            outside: 'Not shown on the PC: {{amounts}}. Top-ups here are from {{min}} to {{max}}.',
        },
        control: {
            add: 'Add {{item}}',
            remove: 'Remove {{item}}',
            clear: 'Remove {{item}}',
            item: 'Item',
            empty: 'None yet.',
            notSet: 'Not set.',
            noOptions: 'Nothing to choose from.',
            jsonHint: 'This value is edited as JSON.',
            jsonInvalid: 'This is not valid JSON yet.',
            // A money setting is typed in the currency's major unit (rupees), never its minor unit.
            moneyInvalid: 'Enter an amount like 1,500 or 99.50.',
            moneyFraction_one: 'Use at most {{count}} digit after the decimal point.',
            moneyFraction_other: 'Use at most {{count}} digits after the decimal point.',
            moneyWhole: 'Enter a whole amount, with no decimal point.',
            // Lists of simple values, as chips (the collection pattern).
            addValue: 'Add a value',
            valueInvalid: 'That value is not allowed here.',
            emptyList: 'None yet.',
            // Minutes before a session ends.
            minutesInput: 'Minutes before the end',
            minutesPlaceholder: 'e.g. 15',
            minutesInvalid: 'Enter whole minutes from 1 to 1,440.',
            minutesEmpty: 'None: no warnings.',
            staffAlertsEmpty: 'None: staff are alerted only when time is up.',
            // Spoken warnings: chosen from the warnings.
            spokenNone: 'None are read out.',
            spokenNotWarned: '{{minutes}} is not a time warning, so it is never read out.',
            spokenEmpty: 'Add time warnings first.',
            // Roles.
            roleInput: 'Custom role name',
            rolePlaceholder: 'A custom role’s name',
            roleSuggest: 'Add a role',
            rolesEmpty: 'None: a second step is optional for every venue role.',
            // Windows Settings pages.
            pageInput: 'Another page (advanced)',
            pagePlaceholder: 'A Windows Settings page name',
            pageInvalid: 'Use the page’s short name: lower-case letters, digits and dashes.',
            pageSuggest: 'Add a page',
            pageLast: 'Keep at least one page.',
            // Game-save folders.
            pathInput: 'Custom folder path (advanced)',
            pathPlaceholder: '%UserProfile%\\Saved Games',
            pathInvalid: 'Enter a folder path, up to 260 characters.',
            pathSuggest: 'Common folders',
            pathsEmpty: 'None: a Full clear keeps nothing extra.',
            // Program file names.
            fileInput: 'Program file name',
            filePlaceholder: 'torrent.exe',
            fileInvalid: 'Use a file name, without a folder: no \\ / : * ? " < > |.',
            filesEmpty: 'None: only the usual escape tools are blocked.',
            // Browser extension ids.
            extensionInput: 'Extension ID',
            extensionPlaceholder: 'The ID from its store page',
            extensionInvalid: 'Enter the extension’s ID, up to 64 characters.',
            extensionsEmpty: 'None: no extension can be installed.',
            // The update window.
            windowAnyTime: 'Any time',
            windowBetween: 'Only between',
            windowFrom: 'From',
            windowTo: 'Until',
            windowInvalid: 'Set both times.',
            // The order sign-in codes try.
            channelOrder: {
                whatsappFirst: 'WhatsApp first, then SMS',
                smsFirst: 'SMS first, then WhatsApp',
                whatsappOnly: 'WhatsApp only (SMS if it fails)',
                smsOnly: 'SMS only',
            },
            versionPlaceholder: 'e.g. 1.10.8500',
            upiPlaceholder: 'venue@okhdfcbank',
            prefixPlaceholder: 'INV',
            // A seconds value that is not whole minutes keeps its seconds.
            durationUnit: 'Unit',
        },
        // A registry setting's label, by key (dots as `__`). One the console has no copy for reads as
        // its name, made readable — and the completeness test fails, so it never ships that way.
        label: {
            audit__retentionYears: 'Keep the audit log for',
            auth__otpChannel: 'How sign-in codes are sent',
            auth__phoneSignIn: 'Phone sign-in',
            auth__qrRequireOnSite: 'Phones sign in at a PC only on the venue Wi-Fi',
            migration__claimWindowDays: 'Moved players can claim for',
            migration__carriedOverTimeValidityDays: 'Carried-over time lasts',
            migration__reviewAmount: 'List balances above',
            migration__reviewSeconds: 'List prepaid time above',
            leads__dailyOutreachCap: 'First messages per member per day',
            leads__followUpDays: 'Follow up after',
            billing__franchiseeBilledCentrally: 'Bill franchisees centrally',
            calls__expireMinutes: 'Clear unanswered calls after',
            calls__reAlertMinutes: 'Remind staff about a call after',
            cleanup__gameSaveAllowList: 'Game-save folders to keep',
            cleanup__keepLauncherAccounts: 'Keep the venue’s game-launcher accounts signed in',
            cleanup__maxSeconds: 'Most time a PC spends clearing',
            cleanup__profile: 'Clear PCs between players',
            compliance__acceptedIdMethods: 'ID checks that count',
            compliance__idCheckRequired: 'ID check before playing',
            compliance__idCheckValidityMonths: 'An ID check lasts',
            compliance__idPhotoRetentionDays: 'Keep ID photos for',
            credit__enabled: 'Pay later',
            credit__limitAmount: 'Pay-later limit',
            fnb__autoUnavailableOnZeroStock: 'Mark items sold out when stock runs out',
            fnb__kioskOrdering: 'Players can order food from the PCs',
            fnb__sendToKitchenBeforePayment: 'Send counter orders to the kitchen before payment',
            hardware__drawerKickOnCash: 'Open the cash drawer for cash payments',
            inventory__enabled: 'Track stock',
            invoices__billOfSupplyPrefix: 'Bill of supply numbers start with',
            invoices__billPrefix: 'Bill numbers start with',
            invoices__creditNotePrefix: 'Credit note numbers start with',
            invoices__prefix: 'Tax invoice numbers start with',
            kiosk__allowSignUp: 'New players can create an account at the PCs',
            kiosk__trailers: 'Play game trailers',
            kiosk__uiSounds: 'Interface sounds',
            locker__allowExplorer: 'Allow a restricted Windows Explorer',
            locker__allowedSettingsPages: 'Windows settings players can open',
            locker__blockAltF4InSession: 'Block Alt+F4 during sessions',
            locker__executionPolicy: 'Programs players can start',
            locker__extensionAllowlist: 'Browser extensions players can install',
            locker__extraBlockedImages: 'More programs to block',
            locker__maintenanceMinutes: 'Maintenance lasts',
            locker__usbStorage: 'USB drives',
            notifications__floorToOnDutyOnly: 'Floor alerts only to staff on a shift',
            notifications__marketingMaxPerWeek: 'Marketing messages per player',
            notifications__otpChannelOrder: 'Which channel codes try first',
            notifications__quietHoursDefault: 'Quiet hours',
            notifications__receiptDefault: 'Receipts at checkout',
            offline__continueRunning: 'Keep running sessions going',
            offline__graceMinutes: 'Extra time while offline',
            offline__knownPlayerMaxMinutes: 'Longest offline sign-in',
            offline__knownPlayerWindowDays: 'Signed in on that PC within',
            offline__knownPlayers: 'Known players can sign in offline',
            offline__staffCodes: 'Staff codes start sessions offline',
            offline__strictLock: 'Lock everything while offline',
            org__branding: 'Your colours on lock screens and receipts',
            passes__fullRefundUsedSeconds: 'Full refund if played less than',
            passes__fullRefundWithinHours: 'Full refund window',
            passes__giftsPerMonth: 'Pass gifts per player',
            passes__proRataRefunds: 'Refund the unused part of a pass',
            payments__commissionBps: 'Platform commission',
            payments__counterUpiVpa: 'UPI ID for the counter QR',
            payments__intentTtlMinutes: 'Online payment requests expire after',
            payments__onlineEnabled: 'Online payments',
            players__ageRequiresVerifiedDob: 'Trust only checked dates of birth',
            players__minAge: 'Youngest age to play',
            pos__manualDiscountMaxBps: 'Largest manual discount',
            pos__refundApprovalAmount: 'Refunds above this need a second person',
            receipts__autoEmail: 'Also email every paid sale its receipt',
            sessions__autoLockIdleMinutes: 'Lock an idle PC after',
            sessions__compMaxMinutesPerAction: 'Free time per grant',
            sessions__compMaxMinutesPerShift: 'Free time per shift',
            sessions__graceCloseGames: 'Close games during grace',
            sessions__graceOncePerSession: 'Grace only once per session',
            sessions__graceSeconds: 'Grace period',
            sessions__guestTicketValidityHours: 'Guest tickets last',
            sessions__guestsEnabled: 'Serve guests',
            sessions__lockPausesBilling: 'Stop the clock while a PC is locked',
            sessions__maxLockMinutes: 'Lock limit',
            sessions__payAsYouGoEnabled: 'Pay-as-you-go',
            sessions__paygBillingUnitSeconds: 'Pay-as-you-go billing step',
            sessions__paygDefaultBudget: 'Spend allowed without choosing how to pay',
            sessions__paygHourlyRate: 'Pay-as-you-go price per hour',
            sessions__staffAlertMinutes: 'Staff alerts for consoles and sims',
            sessions__voiceWarnings: 'Spoken time warnings',
            sessions__walletFundsPlay: 'Wallets pay for play time',
            sessions__warningMinutes: 'Time warnings',
            shifts__blindClose: 'Count the drawer blind',
            shifts__cashHandling: 'When two or more people work at once',
            shifts__dayCloseMode: 'How the business day ends',
            shifts__enabled: 'Count the cash drawer per shift',
            shifts__varianceAlertAmount: 'Alert the owner when the drawer is off by',
            shifts__varianceNoteAmount: 'Ask for a note when the drawer is off by',
            staff__pinSessionMinutes: 'Counter PIN stays unlocked for',
            staff__require2faRoles: 'Roles that need a second sign-in step',
            station__alertExpiryHours: 'Close one-off PC alerts after',
            station__alertGpuTempC: 'Graphics card temperature alert',
            station__clientChannel: 'Station software updates',
            station__clientPinnedVersion: 'Pinned version',
            station__clientUpdateWindow: 'Update hours',
            station__disklessMode: 'Diskless PCs (CCBoot)',
            station__idlePowerAction: 'When a locked PC sits idle',
            station__idlePowerMinutes: 'Idle time before it does that',
            stations__autoRelinkKnownHardware: 'Reconnect reinstalled PCs automatically',
            stations__setupBeforePlay: 'Set up PCs before play',
            stations__setupMaxMinutes: 'Longest setup',
            support__notifyPlayerOnView: 'Tell players when staff view their screen',
            tax__customLineCategory: 'Tax category for custom items at Sell',
            tax__gstin: 'GSTIN',
            tax__mode: 'Tax registration',
            boot__autoAddPcs: 'Add new PCs automatically',
            boot__dhcpMode: 'How the boot server answers PCs',
            boot__menu: 'Boot menu',
            boot__ramCacheGb: 'Memory cache size (GB)',
            boot__rolloutRestart: 'After a new version is ready',
            boot__serverUpdateChannel: 'Boot server updates offered',
            boot__shaderCache: 'Keep the shader cache',
            boot__shaderCacheGb: 'Shader cache size per PC (GB)',
            boot__writebackPolicy: 'Where a PC keeps its changes',
            boot__writebackQuotaGb: 'Changes a PC may keep on the server (GB)',
            tax__playTaxCategory: 'Tax category for play',
            tax__playTaxed: 'Charge GST on play',
            tax__pricesInclusive: 'Prices include GST',
            tax__walletTopupTaxPoint: 'When wallet money is taxed',
            venue__closingAction: 'At closing time',
            venue__powerSchedule: 'Power schedule',
            wallet__adjustMaxAmount: 'Largest wallet adjustment',
            migration__counterClaimApprovalAmount: 'Manager approves moved balances above',
            wallet__cashRefundsAllowed: 'Refund wallet money',
            wallet__enabled: 'Wallets',
            wallet__maxBalanceAmount: 'Largest wallet balance',
            wallet__maxTopupAmount: 'Largest top-up',
            wallet__minTopupAmount: 'Smallest top-up',
            wallet__spendOrder: 'Spent first',
            wallet__topupOffers: 'Top-up extras',
            wallet__topupPresets: 'Top-up amounts on the PC',
        },
        // One line under each label: what it does, in plain words (the server's own description is a developer's
        // fallback only, never shown while this copy exists).
        help: {
            audit__retentionYears: 'Older months are archived.',
            auth__otpChannel: 'How players get a one-time code on their phone. SMS is always the fallback.',
            auth__qrRequireOnSite: 'On: a phone that scans a PC’s sign-in code must be on the venue’s Wi-Fi. Off: any phone can, after it shows which PC and venue it signs in to.',
            auth__phoneSignIn: 'On: people can sign in, create an account and be invited with their phone number and a code. Off: they use email, a password or Google instead, and nobody already signed in is signed out.',
            migration__claimWindowDays: 'How long a player moved from a café’s previous system has to claim their profile and balance, from the switch-over day.',
            migration__carriedOverTimeValidityDays: 'Prepaid time carried over becomes a time pass valid this long from the switch-over day.',
            migration__reviewAmount: 'A move lists every balance above this one by one, to check against the old system.',
            migration__reviewSeconds: 'A move lists every prepaid time balance above this one by one.',
            leads__dailyOutreachCap: 'The most first messages one HQ member can open to new leads in a day. Following up a lead already contacted does not count.',
            leads__followUpDays: 'After a message is opened to a lead, its next follow-up is set this many days ahead unless one is already set.',
            billing__franchiseeBilledCentrally: 'On: the franchisor pays for its franchisees’ venues. Off: each franchisee pays for its own.',
            calls__expireMinutes: 'A call for help nobody answered leaves the list after this long.',
            calls__reAlertMinutes: 'An unanswered call for help alerts the floor a second time after this long.',
            cleanup__gameSaveAllowList: 'Folders a Full clear keeps, so players’ game saves survive.',
            cleanup__keepLauncherAccounts: 'Launchers stay signed in to the venue’s own accounts between players. A player’s own sign-in is always removed.',
            cleanup__maxSeconds: 'A PC that takes longer to clear reports it.',
            cleanup__profile: 'Light signs players out of browsers and game launchers and clears downloads, temporary files and the clipboard. Full also wipes Desktop, Documents, Pictures, Videos and Music.',
            compliance__acceptedIdMethods: 'A staff check at the desk counts only for your organisation.',
            compliance__idCheckRequired: 'Whether players must show ID before they play here.',
            compliance__idCheckValidityMonths: 'With Always, how long a check lasts before the player is asked again.',
            compliance__idPhotoRetentionDays: 'A photo taken with a staff ID check is deleted after this long. The check itself stays.',
            credit__enabled: 'Registered players can buy now and pay later. You collect what they owe at the desk.',
            credit__limitAmount: 'The most a player can owe. Zero turns pay later off. A venue can set a lower limit, and a limit set for one player replaces both.',
            fnb__autoUnavailableOnZeroStock: 'A dish or item is marked sold out here as soon as there is not enough stock to make one, and comes back when stock arrives. One someone marked sold out stays that way.',
            fnb__kioskOrdering: 'Off: the PC has no Food tab, and food is ordered at the counter.',
            fnb__sendToKitchenBeforePayment: 'A counter food order goes to the kitchen as soon as it is placed. Orders from the PCs and the app go once they are paid.',
            hardware__drawerKickOnCash: 'The drawer opens through the receipt printer when a cash payment is taken. Off: only Open drawer opens it.',
            inventory__enabled: 'Count food and drink ingredients, retail items and supplies here, and take them off as they sell. When you turn it on, count everything once to start.',
            invoices__billOfSupplyPrefix: 'At venues on the composition scheme. Capital letters, digits and dashes.',
            invoices__billPrefix: 'At venues not registered for GST. Capital letters, digits and dashes.',
            invoices__creditNotePrefix: 'Credit notes are issued for refunds of invoiced sales. Capital letters, digits and dashes.',
            invoices__prefix: 'Capital letters, digits and dashes. Invoices already issued keep their numbers.',
            kiosk__allowSignUp: 'Off: new accounts are made at the front desk or in the app. Players who have one sign in as usual.',
            kiosk__trailers: 'Muted trailers on the lock screen and a game’s page. Off: PCs show the game’s art and download no trailers.',
            kiosk__uiSounds: 'Soft sounds when players tap the PC’s screen. Players can turn them off for their own session.',
            locker__allowExplorer: 'For games that misbehave without it. The desktop, Start menu and drives stay hidden.',
            locker__allowedSettingsPages: 'Every other Windows Settings page stays hidden during a session.',
            locker__blockAltF4InSession: 'Stops Alt+F4 closing games. Off: players close a game the usual way.',
            locker__executionPolicy: 'Blocking escape tools stops command prompts, Task Manager, registry and script tools. Library only also blocks anything not in your library or part of Windows.',
            locker__extensionAllowlist: 'By their Chrome Web Store or Edge Add-on ID. Every other extension stays blocked.',
            locker__extraBlockedImages: 'Programs blocked on every PC, by file name.',
            locker__maintenanceMinutes: 'How long a PC stays open for maintenance before it locks itself again.',
            locker__usbStorage: 'What a player can do with a USB drive.',
            notifications__floorToOnDutyOnly: 'Calls and floor alerts go only to staff with an open shift.',
            notifications__marketingMaxPerWeek: 'The most promotional messages a player gets in a week.',
            notifications__otpChannelOrder: 'When codes may go by both, which one is tried first.',
            notifications__quietHoursDefault: 'Messages that are not urgent wait until these hours end. Players can change theirs.',
            notifications__receiptDefault: 'What checkout does with a receipt unless staff choose otherwise.',
            offline__continueRunning: 'When a PC loses its connection — an internet outage or an update — sessions play on to their paid time, then up to the extra time below.',
            offline__graceMinutes: 'How long past its paid time a session may play while the PC is offline. Time beyond the usual grace is charged when the PC is back online.',
            offline__knownPlayerMaxMinutes: 'How long a player who signed in offline can play before the PC locks again.',
            offline__knownPlayerWindowDays: 'How recently a player must have signed in on the PC to sign in there offline.',
            offline__knownPlayers: 'A player who recently signed in on a PC with a password can sign in there again while it is offline. Their time is charged when the PC is back online.',
            offline__staffCodes: 'Staff can start a session on an offline PC with a one-time code from Floor → Offline play.',
            offline__strictLock: 'Sessions end at their paid time and nobody can sign in. Overrides the options above.',
            org__branding: 'Your organisation’s own colours on its venues’ lock screens and receipts, where head office allows it.',
            passes__fullRefundUsedSeconds: 'How much of a pass can have been played and still be refunded in full, inside the refund window.',
            passes__fullRefundWithinHours: 'How long after buying a barely used pass a player can still get all their money back.',
            passes__giftsPerMonth: 'How many unused passes a player can give to friends each month.',
            passes__proRataRefunds: 'Outside the full-refund window, refund a pass for the time it has left.',
            payments__commissionBps: 'Set by the platform. Taken from each online payment, together with the payment provider’s fee.',
            payments__counterUpiVpa: 'Sell shows it as a QR for the amount due when UPI is chosen. The money goes straight to you. Empty: no QR.',
            payments__intentTtlMinutes: 'How long a player has to pay an online payment request.',
            payments__onlineEnabled: 'Players can pay by a UPI QR or link through your payment provider.',
            players__ageRequiresVerifiedDob: 'On: a date of birth nobody has checked does not count for age limits, and the player is sent to have it checked.',
            players__minAge: 'Players younger than this cannot start a session.',
            pos__manualDiscountMaxBps: 'The most a staff member can take off a sale by hand.',
            pos__refundApprovalAmount: 'A second staff member approves a refund bigger than this.',
            receipts__autoEmail: 'Automatically, when the player has an email address. Staff can still send one from the order.',
            sessions__autoLockIdleMinutes: 'Lock a PC nobody has touched for this long, as if the player had chosen Lock. Zero never locks it.',
            sessions__compMaxMinutesPerAction: 'The most free time a staff member can add to a session at once.',
            sessions__compMaxMinutesPerShift: 'The most free time one staff member can give in a shift. Time the system adds does not count.',
            sessions__graceCloseGames: 'Games close when grace starts, so players buy more time instead of playing on for free. Other apps stay open.',
            sessions__graceOncePerSession: 'After a session has used its grace, running out again ends it.',
            sessions__graceSeconds: 'Free time a player gets when their paid time runs out — to buy more time, top up or log out. Zero turns grace off.',
            sessions__guestTicketValidityHours: 'How long after it is sold a guest ticket can be used.',
            sessions__guestsEnabled: 'Walk-ins without an account play on a guest ticket bought at the counter.',
            sessions__lockPausesBilling: 'Off: play is still charged while a player has locked their PC to step away. On: locked time is free, up to the lock limit.',
            sessions__maxLockMinutes: 'How long a session can stay locked before it ends and frees the PC. Unused pass time is kept.',
            sessions__payAsYouGoEnabled: 'Players pay for play by the hour. Off: they play on passes and time staff add.',
            sessions__paygBillingUnitSeconds: 'A started step is charged in full.',
            sessions__paygDefaultBudget: 'How much a player who signs in at a PC without choosing how to pay can play for on their account. Zero asks them to add time or money first.',
            sessions__paygHourlyRate: 'What an hour of pay-as-you-go play costs.',
            sessions__staffAlertMinutes: 'On stations staff run — consoles, sims, tables — how many minutes before time runs out staff are alerted. Staff are always alerted when time is up.',
            sessions__voiceWarnings: 'Which of the time warnings the PC also reads out loud.',
            sessions__walletFundsPlay: 'A player’s wallet pays for play at the pay-as-you-go price, after their passes and before anything goes on their account.',
            sessions__warningMinutes: 'How many minutes before a session ends the player is warned. Each warning offers Add time on the PC; with none, players are not warned.',
            shifts__blindClose: 'The expected amount stays hidden until the count is entered.',
            shifts__cashHandling: 'Who uses which drawer decides whose drawer a payment goes into.',
            shifts__dayCloseMode: 'Manual suits a venue that closes for the night; automatic suits one open 24×7.',
            shifts__enabled: 'Staff start a shift with the float, hand over with a count and end with a final count. Turn it off if you do not track drawers.',
            shifts__varianceAlertAmount: 'The owner is told about a difference bigger than this.',
            shifts__varianceNoteAmount: 'Staff explain a difference bigger than this when they close.',
            staff__pinSessionMinutes: 'A shared counter device locks itself after this long without use.',
            staff__require2faRoles: 'These roles must use a passkey or a code app to open the console. Head office roles and roles that can move money always must.',
            station__alertExpiryHours: 'A one-off alert — a tamper attempt, a restart — closes by itself after this long with no repeat.',
            station__alertGpuTempC: 'Raise an alert when a PC’s graphics card runs hotter than this. Only NVIDIA cards report it.',
            station__clientChannel: 'Which station software updates your PCs get.',
            station__clientPinnedVersion: 'The version every PC runs while updates are pinned. The only way to move PCs to an older version.',
            station__clientUpdateWindow: 'When an idle PC may install an update, in venue time. Never during a session.',
            station__disklessMode: 'PCs that start from a CCBoot image lose what they install when they restart, so they leave updates to the image. Detect recognises them; the other two choices decide for every PC here.',
            station__idlePowerAction: 'What a locked PC with nobody at it does after the idle time below. Never during a session.',
            station__idlePowerMinutes: 'How long a PC stays locked and unused first.',
            stations__autoRelinkKnownHardware: 'A reinstalled PC whose hardware is recognised rejoins its station with no pairing code. Each one is recorded in the audit log.',
            stations__setupBeforePlay: 'A PC that starts without the apps you marked Install for players installs them before it opens for play. Staff can skip it from the Floor. Off: they install while a PC is idle.',
            stations__setupMaxMinutes: 'After this long a PC opens for play even if it has not finished setting up.',
            support__notifyPlayerOnView: 'The PC shows “Staff are viewing this screen” while support looks at it.',
            tax__customLineCategory: 'Empty: the standard 18% for services, with no HSN/SAC code on the invoice.',
            tax__gstin: 'The venue’s 15-character GST number, set with its tax registration.',
            tax__mode: 'Whether this venue charges GST. Changed through its tax registration.',
            tax__playTaxCategory: 'The GST rate and code for passes and play time. Empty: the standard 18% for services.',
            boot__autoAddPcs: 'A new PC that starts from the network appears under New PCs on the Floor, with the venue’s default Windows, while you have licensed PCs free.',
            boot__dhcpMode: 'Alongside your router (proxy) is the safe choice. Choose full only if the boot server should hand out network addresses itself.',
            boot__menu: 'Lets a PC show a menu as it starts, to pick a Windows image, or to change Windows on it with a staff PIN.',
            boot__ramCacheGb: 'How much of the boot server’s memory caches images. Empty uses most of it.',
            boot__rolloutRestart: 'When a new version of Windows or the games is rolled out, restart the PCs nobody is playing on onto it. A PC with a session is never restarted.',
            boot__serverUpdateChannel: 'Which updates of the boot server’s own software it downloads. An update is only installed when you choose Update now.',
            boot__shaderCache: 'Each PC keeps its graphics shader cache between boots, so games do not rebuild it every time.',
            boot__shaderCacheGb: 'How much space each PC’s shader cache may use.',
            boot__writebackPolicy: 'Where changes made during a session are kept until the next boot.',
            boot__writebackQuotaGb: 'How much one PC may keep on the boot server during a boot.',
            tax__playTaxed: 'Charge GST on passes and play time at venues on the regular scheme. Off: play is untaxed.',
            tax__pricesInclusive: 'Prices you set already include GST, as is usual in India. Applies only at venues on the regular scheme.',
            tax__walletTopupTaxPoint: 'Usually when it is spent: top-ups themselves are not taxed.',
            venue__closingAction: 'What happens to running sessions when the venue closes. Players are warned 15 and 5 minutes before.',
            venue__powerSchedule: 'Wake PCs before opening and shut down idle, locked ones after closing — never during a session.',
            wallet__adjustMaxAmount: 'The most a staff member can add to or take from a wallet in one adjustment.',
            migration__counterClaimApprovalAmount: 'Claiming a balance moved from your previous system at the counter needs a manager’s PIN above this much money.',
            wallet__cashRefundsAllowed: 'Staff can give wallet money back the way it was paid — after a mistaken top-up, say. Off refuses every wallet refund.',
            wallet__enabled: 'Players top up a balance and spend it at your venues on play, passes and food. The money can only be spent with you.',
            wallet__maxBalanceAmount: 'The most a wallet can hold, bonus money included.',
            wallet__maxTopupAmount: 'The most a player can add to their wallet at once.',
            wallet__minTopupAmount: 'The least a player can add to their wallet at once.',
            wallet__spendOrder: 'Which part of a wallet a purchase uses first.',
            wallet__topupOffers: 'Add at least an amount, get a bonus on top. A venue’s own list replaces the organisation’s there.',
            wallet__topupPresets: 'The amounts the PC offers. Players can always type their own amount.',
        },
        // A property inside a setting's value, by name.
        field: {
            mode: 'Mode',
            wakeLeadMinutes: 'Wake PCs this many minutes before opening',
            shutdownGraceMinutes: 'Shut locked PCs down this many minutes after closing (never during a session)',
            // A top-up offer (`wallet.topupOffers`): "add at least this, get this bonus".
            minTopupAmount: 'Top-up of at least',
            bonusAmount: 'Bonus',
            // Quiet hours.
            start: 'From',
            end: 'Until',
            // `org.branding`.
            primary: 'Main colour',
            accent: 'Accent colour',
            logo: 'Logo',
        },
        option: {
            boot__dhcpMode: {
                proxy: 'Alongside my router (proxy)',
                full: 'As the network’s DHCP server (full)',
            },
            boot__menu: {
                off: 'Off',
                on: 'On',
            },
            boot__rolloutRestart: {
                never: 'Do nothing — I will restart PCs myself',
                idle: 'Restart PCs onto it when nobody is playing',
            },
            boot__serverUpdateChannel: {
                stable: 'Stable releases',
                beta: 'Early releases (beta)',
            },
            boot__writebackPolicy: {
                auto: 'Its own drive when it has one, else the server',
                forceLocal: 'Always its own drive',
                forceServer: 'Always the boot server',
            },
            auth__otpChannel: {
                sms: 'SMS',
                whatsapp: 'WhatsApp',
                both: 'Both — in the order below',
            },
            cleanup__profile: {
                light: 'Light — recommended',
                full: 'Full',
                off: 'Off — not recommended',
            },
            compliance__acceptedIdMethods: {
                digital: 'A digital verification',
                staff_document: 'Staff check a document at the desk',
            },
            compliance__idCheckRequired: {
                never: 'Never',
                before_first_session: 'Once, before their first session',
                always_verified: 'Always — and again when the check gets old',
            },
            locker__executionPolicy: {
                deny_list: 'Block known escape tools — recommended',
                allow_list: 'Library only',
            },
            locker__usbStorage: {
                block: 'Block — recommended',
                read_only: 'Read only: open files, copy nothing onto the drive',
                allow: 'Allow',
            },
            mode: {
                off: 'Off',
                opening_hours: 'Follow the opening hours',
            },
            notifications__otpChannelOrder: {
                sms: 'SMS',
                whatsapp: 'WhatsApp',
            },
            notifications__receiptDefault: {
                ask: 'Ask each time',
                print: 'Print it',
                email: 'Email it',
                none: 'No receipt',
            },
            // The pay-as-you-go billing step (`sessions.paygBillingUnitSeconds`): a started step is charged in full.
            sessions__paygBillingUnitSeconds: {
                '60': 'Every minute',
                '300': 'Every 5 minutes',
                '900': 'Every 15 minutes',
                '1800': 'Every 30 minutes',
            },
            shifts__cashHandling: {
                per_drawer: 'A drawer per counter',
                shared_drawer: 'One shared drawer',
                floor_without_drawer: 'Floor staff without a drawer',
            },
            shifts__dayCloseMode: {
                manual: 'A manager closes it (manual)',
                auto: 'It rolls over at the cutoff by itself (automatic)',
            },
            station__clientChannel: {
                stable: 'Stable — recommended',
                beta: 'Beta — earlier releases',
                pinned: 'Pinned to one version',
            },
            station__idlePowerAction: {
                none: 'Stay on',
                sleep: 'Sleep',
                shutdown: 'Shut down',
            },
            station__disklessMode: {
                auto: 'Detect — recommended',
                on: 'Treat every PC as diskless',
                off: 'Treat every PC as having its own disk',
            },
            tax__mode: {
                regular: 'Registered, regular scheme',
                composition: 'Registered, composition scheme',
                unregistered: 'Not registered',
            },
            tax__walletTopupTaxPoint: {
                redemption: 'When it is spent',
                sale: 'When it is topped up',
            },
            venue__closingAction: {
                warn_only: 'Warn players, and keep their sessions running',
                end_sessions_at_close: 'Warn players, then end their sessions',
            },
            wallet__spendOrder: {
                bonus_first: 'Bonus money first — it can expire',
                cash_first: 'Money they paid first',
            },
        },
        // Windows Settings pages a player may open (`locker.allowedSettingsPages`), by their short name.
        page: {
            sound: 'Sound',
            mousetouchpad: 'Mouse and touchpad',
            display: 'Display',
            keyboard: 'Keyboard',
            bluetooth: 'Bluetooth',
            'easeofaccess-display': 'Accessibility: text size',
            'easeofaccess-mouse': 'Accessibility: mouse pointer',
            regionlanguage: 'Language and region',
            'apps-volume': 'Volume for each app',
        },
        // Folders a Full clear keeps (`cleanup.gameSaveAllowList`).
        path: {
            savedGames: 'Saved Games folder',
            myGames: 'My Games (in Documents)',
            appDataRoaming: 'Roaming app data',
            appDataLocal: 'Local app data',
            appDataLocalLow: 'LocalLow app data',
        },
        // Organisation roles, as `staff.require2faRoles` lists them.
        role: {
            owner: 'Owner',
            manager: 'Manager',
            cashier: 'Cashier',
            floor: 'Floor staff',
            kitchen: 'Kitchen',
            technician: 'Technician',
            accountant: 'Accountant',
        },
        // A station class's own PC settings (spec/13 §12): the class-scoped settings, edited in the class dialog.
        classSettings: {
            title: 'PC settings for this class',
            intro: 'Each one follows this venue’s setting unless you change it for this class.',
            newClass: 'Create the class first, then set its PC settings here.',
            usingVenue: 'Using this venue’s setting — {{value}}',
            changed: 'Changed for this class',
            resetToVenue: 'Use this venue’s {{value}} again',
            readOnly: 'Your role can’t change PC settings.',
            unavailable: 'The PC settings could not be loaded.',
        },
    },
    // The venue's tax mode and GSTIN, in the Tax and invoices settings group (F-PAY-11; spec/06 §10.3–§10.4).
    // An organisation's tax categories (F-PAY-11; spec/06 §10.1).
    taxCategories: {
        title: 'Tax categories',
        hint: 'The GST rate and HSN/SAC code each product and custom item is taxed under. Charged only at venues registered under the regular scheme. Your accountant confirms the codes.',
        unavailable: 'The tax categories could not be loaded.',
        add: 'Add a category',
        edit: 'Edit',
        editNamed: 'Edit {{name}}',
        name: 'Name',
        nameRequired: 'Give the category a name.',
        nameTaken: 'A category with that name already exists.',
        code: 'HSN or SAC code',
        codeHint: '4 to 8 digits, as printed on the tax invoice.',
        codeInvalid: 'An HSN or SAC code is 4 to 8 digits.',
        noCode: 'No code',
        codeValue: 'HSN/SAC {{code}}',
        summary_one: '{{count}} category in use',
        summary_other: '{{count}} categories in use',
        archiveTitle: 'Archive {{name}}?',
        archiveBody: 'It leaves the pickers for new products and custom items. Products already taxed under it keep it until you change them, and what was sold under it keeps its code.',
        keep: 'Keep it',
        rate: 'GST rate (%)',
        rateHint: 'For example 18 or 5. A new rate applies from now on; placed sales keep theirs.',
        rateInvalid: 'Enter a rate from 0 to 100, with at most two decimals.',
        rateValue: '{{rate}}%',
        save: 'Save',
        cancel: 'Cancel',
        archive: 'Archive',
        restore: 'Restore',
        archived: 'Archived',
        atRedemption: 'Taxed where spent',
        saved: 'Tax category saved',
        refused: 'The category could not be saved.',
        readOnly: 'Your role can read the tax categories but not change them.',
        noAccess: 'Your role cannot see the tax categories.',
        passesNote: 'Passes and play time are untaxed unless “Charge GST on play” is on above; then they are taxed under the category chosen for play.',
        standardRate: 'Standard rate for services (18%, no code)',
        option: '{{name}} · {{rate}}%',
    },
    // The organisation's legal name, PAN and registered address, as its owner completes them (Money → Business details).
    businessDetails: {
        title: 'Business details',
        hint: 'The legal name, PAN and registered address your tax invoices and bills of supply are issued under.',
        complete: 'Complete',
        incomplete: 'Not complete',
        needed: 'Add the legal name and address to record a GST registration.',
        tradeName: 'Business name: {{name}}. Head office changes it if it needs to.',
        field: {
            legalName: 'Legal name',
            pan: 'PAN',
            registeredAddress: 'Registered address',
        },
        help: {
            legalName: 'The registered legal entity, exactly as on its documents.',
            pan: 'Five capital letters, four digits and a capital letter, for example AAACP1234Q.',
        },
        save: 'Save business details',
        saved: 'Business details saved.',
        unchanged: 'Nothing has changed.',
        failed: 'The business details could not be saved. Try again.',
        notPermitted: 'Only an owner can change the business details.',
        unavailable: 'The business details could not be loaded.',
        error: {
            required: 'Fill this in.',
            pan: 'A PAN is five capital letters, four digits and a capital letter.',
            keepLegalName: 'The legal name can be changed, not removed.',
            keepAddress: 'The registered address can be changed, not removed.',
        },
    },
    taxRegistration: {
        title: 'Tax mode',
        hint: 'How the venue is registered for GST decides whether its sales charge tax and which document each paid order gets.',
        unavailable: 'The tax mode could not be loaded.',
        mode: {
            regular: 'Regular',
            composition: 'Composition',
            unregistered: 'Unregistered',
        },
        modeHint: {
            regular: 'Registered under GST, regular scheme: GST is charged and each paid order gets a tax invoice.',
            composition: 'Registered under the composition scheme: no tax is charged and each paid order gets a bill of supply.',
            unregistered: 'Not registered: no tax is charged and each paid order gets a plain bill.',
        },
        current: 'In force now',
        gstin: 'GSTIN',
        noGstin: 'None',
        since: 'Since',
        sinceDefault: 'No change recorded: the default',
        defaultNote: 'Nothing recorded yet: the venue is unregistered and sells with plain bills.',
        readOnly: 'Only a member who may manage tax can change this.',
        change: 'Change tax mode',
        scheduledTitle: 'Scheduled changes',
        scheduledHint: 'A change dated later can be withdrawn until its day begins.',
        scheduledItem: '{{mode}} from {{date}}',
        scheduledList: 'Scheduled changes',
        withdraw: 'Withdraw',
        withdrawFor: 'Withdraw the change to {{mode}} from {{date}}',
        withdrawConfirm: 'Withdraw the change to {{mode}} from {{date}}? The venue keeps the mode it has on that day.',
        withdrawSubmit: 'Withdraw change',
        withdrawn: 'The change to {{mode}} from {{date}} was withdrawn.',
        historyTitle: 'History',
        historyCaption: 'Every tax mode change recorded for this venue, newest first',
        historyEmpty: 'No change has been recorded yet.',
        status: {
            in_force: 'In force',
            scheduled: 'Scheduled',
            withdrawn: 'Withdrawn',
            superseded: 'Superseded',
        },
        form: {
            label: 'Change tax mode',
            mode: 'New tax mode',
            gstin: 'GSTIN',
            gstinHint: 'The 15 characters on the GST registration certificate, starting with the state code.',
            effectiveOn: 'Takes effect from',
            effectiveOnHint: 'The business day it starts: today or later.',
            reason: 'Reason',
            reasonHint: 'For the audit log, such as the date on the registration certificate.',
            review: 'Review change',
        },
        error: {
            gstinRequired: 'A regular or composition venue needs its GSTIN.',
            gstinInvalid: 'That is not a valid GSTIN. Check all 15 characters against the registration certificate.',
            gstinNotAllowed: 'An unregistered venue has no GSTIN.',
            effectiveOnRequired: 'Choose the day the change takes effect.',
            effectiveInPast: 'A change can start today or later, never on an earlier day: documents already issued are never reissued.',
            unchanged: 'That is already the tax mode and GSTIN in force.',
            notPermitted: 'Your role cannot change the tax mode.',
            failed: 'The change could not be recorded. Try again.',
            withdrawBegun: 'That change’s day has begun, so it is permanent. Record another change to undo it.',
            businessDetailsRequired: 'A tax invoice or a bill of supply carries the business’s legal name and registered address. An owner adds them in the organisation’s settings, under Money, then record this again. Until then the venue sells with plain bills.',
            withdrawFailed: 'The change could not be withdrawn. Try again.',
        },
        confirm: {
            title: 'Change to {{mode}} from {{date}}?',
            effect: {
                regular: 'Every order placed from the start of that business day is charged GST and gets a tax invoice under GSTIN {{gstin}}.',
                composition: 'Every order placed from the start of that business day gets a bill of supply under GSTIN {{gstin}}, with no tax charged.',
                unregistered: 'Every order placed from the start of that business day gets a plain bill, with no tax charged.',
            },
            keeps: 'Orders placed before then keep their own treatment, documents already issued stay exactly as they are, and no price changes.',
            today: 'It takes effect at once and is permanent: undoing it takes another dated change.',
            later: 'Until that day begins, it can be withdrawn.',
            submit: 'Record the change',
        },
        recorded: 'Recorded: {{mode}} from {{date}}.',
    },
    // Your account (spec/13 §2's staff menu): profile, staff PIN, second factor, devices, notifications.
    account: {
        title: 'Your account',
        intro: 'Your PIN at the counter, how you sign in, and what reaches you.',
        appearance: {
            title: 'Appearance',
            hint: 'How the console looks on this device. The PCs’ screens are not affected.',
            label: 'Theme',
            dark: 'Dark',
            light: 'Light',
            system: 'Match this device',
        },
        profile: {
            title: 'Profile',
            hint: 'Who you are signed in as.',
            name: 'Name',
            email: 'Email',
            noName: 'Not given',
            noEmail: 'No email address',
        },
        pin: {
            title: 'Staff PIN',
            hint: 'Used to open a PC for maintenance at the kiosk and to sign in on shared devices. Six digits, one for each venue you work at. Only you set it; a manager can only reset it.',
            listLabel: 'Your venues',
            noVenues: 'There is no venue of this organisation for you to set a PIN at yet. Each venue you work at is listed here.',
            unavailable: 'Your PINs could not be loaded.',
            thisVenue: 'This venue',
            isSet: 'Set',
            notSet: 'Not set',
            isLocked: 'Locked',
            lockedRow: 'Locked after too many wrong tries. Ask a manager to reset it, then set a new one.',
            set: 'Set PIN',
            setFor: 'Set PIN at {{venue}}',
            change: 'Change PIN',
            changeFor: 'Change PIN at {{venue}}',
            eyebrow: 'Staff PIN',
            sheetTitle: 'Your PIN at {{venue}}',
            sheetHint: 'Setting a new PIN replaces the old one. Nobody else ever sees it.',
            formLabel: 'Set your staff PIN',
            new: 'New PIN',
            confirm: 'Type it again',
            save: 'Save PIN',
            saved: 'PIN saved.',
            locked: 'Your PIN here is locked after too many wrong tries. Ask a manager to reset it, then set a new one.',
            sharedDevice: 'This is a shared counter device. Set your PIN from your own sign-in.',
            failed: 'The PIN could not be saved. Try again.',
            problem: {
                length: 'A PIN is exactly six digits.',
                repeated: 'Pick digits that are not all the same.',
                run: 'Pick a PIN that is not a run like 123456 or 987654.',
                mismatch: 'The two PINs do not match.',
            },
        },
        twoFactor: {
            title: 'Two-step sign-in',
            hint: 'A code from your authenticator app after your password.',
            on: 'On',
            off: 'Off',
            offHint: 'Two-step sign-in is off. You are asked to set it up when your role needs it.',
            newCodes: 'New backup codes',
            disable: 'Turn off',
            codesTitle: 'New backup codes',
            codesHint: 'Your old backup codes stop working as soon as the new ones are made.',
            codesConfirm: 'Make new codes',
            codesOnce: 'Keep these somewhere safe. They are shown once, and each works once.',
            guardTitle: 'Save your backup codes before you close',
            guardBody: 'These backup codes are shown only now and cannot be shown again. Copy them first.',
            codesLabel: 'Your backup codes',
            copy: 'Copy codes',
            copied: 'Copied',
            disableTitle: 'Turn off two-step sign-in',
            disableHint: 'Your password alone will sign you in. If your role needs two-step sign-in, the console asks you to set it up again.',
            disableConfirm: 'Turn off',
            disabled: 'Two-step sign-in is off.',
            password: 'Password',
            passwordHint: 'Your account password, if it has one.',
            wrongPassword: 'That password is not right.',
            needsSecondFactor: 'Sign in again with your authenticator code first.',
            failed: 'That did not work. Try again.',
        },
        devices: {
            title: 'Signed-in devices',
            hint: 'Where you are signed in now. Sign one out and it stops working at once.',
            listLabel: 'Signed-in devices',
            unavailable: 'Your devices could not be loaded.',
            unknownDevice: 'Unknown device',
            current: 'This device',
            lastActive: 'Active {{when}}',
            secondFactor: 'Two-step',
            end: 'Sign out',
            endFor: 'Sign out {{device}}',
            confirmLabel: 'Sign out {{device}}?',
            confirm: 'Sign this device out?',
            confirmCurrent: 'This signs you out here.',
            ended: 'Device signed out.',
            failed: 'The device could not be signed out. Try again.',
        },
        connected: {
            title: 'Connected accounts',
            hint: 'Sign in with Google, Discord or Steam as well as your password. You can unlink one while you still have another way to sign in.',
            listLabel: 'Connected accounts',
            provider: {
                google: 'Google',
                discord: 'Discord',
                steam: 'Steam',
            },
            linked: 'Linked',
            notLinked: 'Not linked',
            linkedOn: 'Linked {{date}}',
            link: 'Link {{provider}}',
            unlink: 'Unlink',
            unlinkLabel: 'Unlink {{provider}}',
            onlyWay: 'Your only way to sign in. Link another account first.',
            unavailable: 'Your connected accounts could not be loaded.',
            confirmTitle: 'Unlink {{provider}}?',
            confirmBody: 'You will not be able to sign in with {{provider}} any more. You can link it again at any time.',
            confirm: 'Unlink {{provider}}',
            cancel: 'Keep it',
            linkedToast: '{{provider}} is linked. You can sign in with it now.',
            unlinkedToast: '{{provider}} is unlinked.',
            stale: 'For your security, sign in again before you unlink {{provider}}, then come back here.',
            signInAgain: 'Sign in again',
            lastWay: '{{provider}} is your only way to sign in, so it cannot be unlinked yet.',
            linkFailed: 'Linking {{provider}} did not work. Try again.',
            unlinkFailed: 'Unlinking {{provider}} did not work. Try again.',
            yourAccount: 'your account',
            error: {
                cancelled: 'You cancelled linking {{provider}}. Nothing was changed.',
                expired: 'That took too long or was started in another browser. Start again.',
                takenByOther: 'That {{provider}} account is already linked to another Clavian account.',
                unverified: '{{provider}} has not confirmed an email address for that account, so it cannot be linked. Confirm your email with {{provider}}, then try again.',
                generic: 'Linking {{provider}} did not work. Try again.',
            },
        },
        notifications: {
            title: 'Notifications',
            hint: 'What reaches you at this organisation: in the console and on your phone.',
            unavailable: 'Your notification preferences could not be loaded.',
            quiet: 'Quiet hours',
            quietHint: 'Hold non-urgent alerts between these times. Urgent ones still arrive.',
            from: 'From',
            until: 'Until',
            quietInvalid: 'Use a 24-hour time, like 22:00.',
            saveQuiet: 'Save quiet hours',
            saved: 'Saved.',
            failed: 'That change could not be saved. Try again.',
            matrix: 'Which alerts reach you, by channel',
            category: 'Alerts',
            channel: {
                inbox: 'In the console',
                push: 'On your phone',
                whatsapp: 'WhatsApp',
                sms: 'SMS',
                email: 'Email',
            },
            toggle: '{{category}}: {{channel}}',
            categories: {
                floor: 'Floor',
                orders: 'Orders',
                payments: 'Payments',
                stock: 'Stock',
                shifts: 'Shifts',
                staffing: 'Staffing',
                account: 'Account',
                security: 'Security',
                insights: 'Insights',
            },
            alwaysOn: 'Orders, payments and security always reach the console.',
        },
        // The owners' daily summary (N-27): opt in, for the organisation or each venue. Owners only.
        dailySummary: {
            title: 'Daily summary',
            hint: 'A short email after each business day: takings, play, food and drink, cash, and anything that needs you. Only owners can get it.',
            unavailable: 'Your daily summary settings could not be loaded.',
            organization: 'For the whole organisation',
            organizationHint: 'One email once every venue has closed its day, with a row for each venue.',
            venues: 'For each venue',
            venuesHint: 'One email per venue you pick, as soon as its day is closed.',
            venuesLabel: 'Venues to get a daily summary for',
            noVenues: 'No live venue yet.',
            where: 'It goes to {{email}} and your inbox here. Quiet hours hold it until they end.',
            whereNoEmail: 'Add and verify an email address to get it by email. Until then it arrives in your inbox here.',
            sendNow: 'Email me the latest summary',
            sendNowHint: 'The latest day for the whole organisation, to you only, now.',
            sent: 'On its way to your email.',
            sendFailed: 'The summary could not be sent. Try again.',
            unverified: 'Verify your email address first.',
            failed: 'That change could not be saved. Try again.',
        },
    },
    // The organisation's Venues (spec/13 §13, F-ORG-01), and its franchisees' (F-ORG-17).
    venues: {
        title: 'Venues',
        intro: 'Every venue the organisation runs.',
        new: 'New venue',
        notPermitted: 'Your role cannot see this organisation’s venues.',
        unavailable: 'The venues could not be loaded.',
        listLabel: 'Venues',
        emptyTitle: 'No venues yet',
        empty: 'Add the first venue with New venue. It starts in setup until you open it.',
        emptyReadOnly: 'The organisation has no venues yet.',
        noAddress: 'No address yet',
        open: 'Open venue',
        status: {
            setup: 'In setup',
            live: 'Live',
            paused: 'Paused',
            closed: 'Closed',
        },
        archive: 'Archive venue',
        archiveConfirm: 'Archive {{name}}? It closes for good and leaves the venue list and the app. Its orders and sessions are kept.',
        archived: '{{name}} is archived.',
        archiveFailed: 'The venue could not be archived. Try again.',
        create: {
            eyebrow: 'Venues',
            title: 'New venue',
            stepName: 'Name',
            stepWhere: 'Where',
            stepConfirm: 'Confirm',
            name: 'Venue name',
            nameRequired: 'Give the venue a name.',
            slug: 'Web address name',
            slugHint: 'Used in links, e.g. playground-hsr. Lower-case letters, digits and hyphens.',
            slugInvalid: 'Use at least three lower-case letters, digits and single hyphens.',
            slugTaken: 'Another venue already uses this name in its address. Choose another.',
            timezone: 'Timezone',
            timezoneHint: 'Decides the business day and opening hours, e.g. Asia/Kolkata. It cannot change once the venue trades.',
            timezoneInvalid: 'Use a timezone name like Asia/Kolkata.',
            addAddress: 'Add an address',
            addressHint: 'The address is optional now; you can add it in the venue’s settings.',
            address: 'Address',
            noAddress: 'Added later',
            addressPart: 'Fill this in too, or leave the whole address empty.',
            line1: 'Street address',
            locality: 'City',
            region: 'State',
            postalCode: 'PIN code',
            country: 'Country code',
            countryInvalid: 'Use a two-letter country code, like IN.',
            confirmHint: 'The venue starts in setup, in the organisation’s currency. Its hours, photos and stations come next.',
            submit: 'Create venue',
            done: '{{name}} is created.',
            failed: 'The venue could not be created. Try again.',
        },
        franchisees: {
            title: 'Franchisee venues',
            hint: 'Venues your franchisees run. Read only.',
            unavailable: 'The franchisees could not be loaded.',
            none: 'No venues yet.',
            listLabel: '{{name}} venues',
        },
    },
    venueProfile: {
        readOnly: 'Read only: your role cannot edit the venue profile.',
        accessUnavailable: 'Read only: your access could not be checked, so nothing is offered for editing. Try again later.',
        setupOnly: 'Changes only while the venue is setting up: once it trades, the timezone decides which business day every order and session belongs to.',
        cutoffWhileTrading: 'A change takes effect from the next business day: today and every past day keep the cutoff they had, so no takings move between days.',
        cutoffChanged: '{{cutoff}} from the business day of {{from}}; {{previous}} before.',
        status: {
            setup: 'Setting up',
            live: 'Live',
            paused: 'Paused',
            closed: 'Closed',
        },
        // Going live, pausing and resuming (spec/13 §13; F-HQ-03): the venue's status at the head of its profile.
        statusControl: {
            label: 'Venue status',
            hint: {
                setup: 'Setting up: players cannot find this venue in the apps yet, and its timezone can still change. Go live when you open.',
                live: 'Open for business: its closing-time rules run, and players find it in the apps when it is listed.',
                paused: 'Paused: hidden from players in the apps, and its closing-time rules stop, until you resume it.',
                closed: 'Closed for good.',
            },
            goLive: 'Go live',
            pause: 'Pause',
            resume: 'Resume',
            confirm: {
                live: {
                    title: 'Go live with {{venue}}?',
                    body: 'The venue opens for business: its closing-time rules start running, and players can find it in the apps when it is listed.',
                    oneWay: 'This is one-way. You can pause the venue later, but it cannot go back to setting up.',
                    timezone: 'Its timezone, {{timezone}}, can no longer be changed: it decides which business day every order and session belongs to.',
                    action: 'Go live',
                },
                paused: {
                    title: 'Pause {{venue}}?',
                    body: 'Players will not find it in the apps, and its closing-time rules stop, until you resume it. Stations and this console keep working.',
                    action: 'Pause venue',
                },
            },
            done: {
                live: '{{venue}} is live.',
                paused: '{{venue}} is paused.',
                resumed: '{{venue}} is live again.',
            },
            stale: 'Someone changed this venue just now. Check its status, then try again.',
            refused: 'The venue cannot make that change from where it is now.',
            failed: 'The status could not be changed. Try again.',
        },
        section: {
            details: 'Details',
            location: 'Address and map pin',
            contact: 'Contact',
            hours: 'Opening hours',
            holidays: 'Holidays',
            offer: 'Amenities and listing',
            logo: 'Logo',
            photos: 'Photos',
            closing: 'Closing time',
        },
        // A field's label, by the property's name.
        label: {
            name: 'Venue name',
            slug: 'Slug',
            timezone: 'Timezone',
            businessDayCutoff: 'Business day ends at',
            address: 'Address',
            line1: 'Address line 1',
            line2: 'Address line 2',
            landmark: 'Landmark',
            locality: 'City or town',
            region: 'State',
            postalCode: 'PIN code',
            country: 'Country',
            coordinate: 'Map pin',
            lat: 'Latitude',
            lng: 'Longitude',
            contact: 'Contact',
            phone: 'Phone',
            email: 'Email',
            website: 'Website',
            openingHours: 'Opening hours',
            mon: 'Monday',
            tue: 'Tuesday',
            wed: 'Wednesday',
            thu: 'Thursday',
            fri: 'Friday',
            sat: 'Saturday',
            sun: 'Sunday',
            opens: 'Opens',
            closes: 'Closes',
            holidays: 'Holidays',
            // Inside a holiday: its own name, not the venue's (`<parent>__<name>` wins over `<name>`).
            holidays__name: 'Holiday name',
            date: 'Date',
            closed: 'Closed all day',
            hours: 'Special hours',
            amenities: 'Amenities',
            publicListing: 'Show in the player app',
            logoFileId: 'Venue logo',
            photos: 'Photos',
        },
        // What one row of a list is called, by the list's name.
        item: {
            mon: 'Hours',
            tue: 'Hours',
            wed: 'Hours',
            thu: 'Hours',
            fri: 'Hours',
            sat: 'Hours',
            sun: 'Hours',
            hours: 'Hours',
            holidays: 'Holiday',
        },
        description: {
            slug: 'Part of the venue’s address in the console and the player app: lower-case letters, digits and hyphens.',
            businessDayCutoff: 'When one business day ends and the next begins, in the venue’s timezone.',
            coordinate: 'Where the pin goes on the map, in degrees.',
            phone: 'With the country code, for example +91 98765 43210.',
            website: 'An https address.',
            openingHours: 'Hours that close earlier than they open run past midnight into the next day; the same time twice is open all day.',
            hours: 'None: that weekday’s regular hours apply.',
            holidays: 'Dates the venue is closed or keeps special hours.',
            publicListing: 'Only a live venue appears in the player app.',
        },
        option: {
            amenities: {
                parking: 'Parking',
                air_conditioning: 'AC',
                food: 'Food',
                washrooms: 'Washrooms',
                streaming_room: 'Streaming room',
            },
        },
        photos: {
            cover: 'Cover photo',
            gallery: 'Gallery',
            noCover: 'No cover photo yet.',
            noGallery: 'No gallery photos yet.',
            uploadCover: 'Upload a cover photo',
            replaceCover: 'Replace the cover photo',
            addToGallery: 'Add a photo to the gallery',
            remove: 'Remove {{photo}}',
            coverAlt: 'Cover photo of the venue',
            galleryAlt: 'Gallery photo {{n}}',
            uploading: 'Uploading {{name}}.',
            processing: 'Making the smaller sizes.',
            uploaded: 'Uploaded. Save to attach it to the venue.',
            tooLarge: 'That file is over 10 MB.',
            wrongType: 'Use a JPEG, PNG or WebP image.',
            failed: 'The upload failed. Try again.',
            galleryFull: 'The gallery holds 20 photos.',
            hint: 'JPEG, PNG or WebP, up to 10 MB.',
        },
        // The venue's own logo (F-ORG-01): its stations' lock screen and the kiosk's rail show it.
        logo: {
            hint: 'Shown on your stations’ lock screen and in the kiosk while a player plays. A PNG or WebP with a transparent background works best, up to 2 MB: square or wide on the lock screen, and a square one reads best in the kiosk’s side rail. Without one, the kiosk shows your organisation’s logo, or the venue’s name.',
            alt: 'The venue’s logo',
            on: {
                dark: 'On the lock screen',
                light: 'On a light background',
            },
            upload: 'Upload a logo',
            replace: 'Replace the logo',
            remove: 'Remove the logo',
            uploading: 'Uploading {{name}}.',
            uploaded: 'Uploaded. Save to put it on your stations.',
            tooLarge: 'That file is over 2 MB.',
            wrongType: 'Use a PNG, WebP or JPEG image.',
            failed: 'The upload failed. Try again.',
        },
    },
    // ADR-0033: a staff-run station's countdown screen, on a TV or tablet beside it — public, read-only, no sign-in.
    display: {
        title: 'Countdown',
        loading: 'Loading the station…',
        free: 'Free',
        freeHint: 'Ask at the counter to start playing.',
        left: 'left',
        // ADR-0034: an Open console session counts the time played up.
        played: 'played',
        openHint: 'Open session — played so far, paid at the counter',
        playing: 'Now playing',
        locked: 'On hold',
        lockedHint: 'The clock is held while the player steps away.',
        grace: 'Time’s up',
        graceHint: 'Grace time: {{time}} until the session ends.',
        thenGrace: 'Then {{minutes}} min of grace',
        hello: 'Hi {{name}}',
        phone: 'Add time or order food on your phone',
        unlinked: 'This screen is no longer linked.',
        unlinkedHint: 'Ask staff to show a new countdown link from the station.',
        offline: 'Reconnecting…',
        failed: 'The countdown could not be loaded. Retrying.',
    },
    // ADR-0033: staff alerts as a staff-run station's time runs out (N-30), on any screen of the venue console.
    staffAlerts: {
        open: 'Open station',
    },
    // Guided setup (spec/13 §13.1, F-HQ-03): what an organisation and each venue need before opening, each step read from
    // the venue itself, with one action that opens where it is done. Go live waits on the three essentials.
    setup: {
        title: 'Setup',
        nav: 'Setup',
        navLeft_one: '{{count}} left',
        navLeft_other: '{{count}} left',
        navLabel_one: 'Setup, {{count}} essential left',
        navLabel_other: 'Setup, {{count}} essentials left',
        organizationIntro: 'What your business needs before it opens, and where each of your venues stands.',
        venueIntro: 'What {{venue}} needs before it opens.',
        hero: {
            of: 'of {{total}}',
            stations: 'PCs',
            unknown: '-',
            next: 'Next: {{step}}',
            allEssentials: 'The essentials are done. Go live when you are ready.',
        },
        progressLabel: 'Setup progress',
        progress: '{{essentialsDone}} of {{essentialsTotal}} essentials · {{suggestedSettled}} of {{suggestedTotal}} suggested',
        essentialsTitle: 'Essentials',
        essentialsHint: 'Go live waits on these three. Each step is checked against the venue itself, so it ticks off as you go.',
        suggestedTitle: 'Suggested',
        suggestedHint: 'Worth doing before you open. Skip any that does not apply to you.',
        businessTitle: 'Your business',
        businessHint: 'Needed once, for every venue.',
        venuesTitle: 'Your venues',
        venuesHint: 'Each venue is set up, and goes live, on its own.',
        noVenuesTitle: 'No venue yet',
        noVenues: 'A venue is a place with its own opening hours, PCs and prices. Create your first to set it up.',
        unavailable: 'Setup could not be loaded.',
        notPermitted: 'Your role cannot see this venue’s setup.',
        openVenue: 'Set up {{venue}}',
        openVenueShort: 'Open setup',
        venueEssentials: '{{done}} of {{total}} essentials',
        venueLive: 'Open for business',
        venuePaused: 'Paused',
        venueReady: 'Ready to go live',
        status: {
            done: 'Done',
            todo: 'To do',
            skipped: 'Skipped',
        },
        essential: 'Essential',
        skip: 'Skip',
        skipNamed: 'Skip “{{step}}”',
        unskip: 'Undo skip',
        unskipNamed: 'Put “{{step}}” back on the list',
        skippedOn: 'Skipped {{date}}',
        skipFailed: 'That could not be saved. Try again.',
        downloadApps: 'Download apps',
        step: {
            businessDetails: {
                title: 'Business details',
                why: 'Needed before a venue can issue GST tax invoices: your legal name and registered address go on them.',
                action: 'Edit details',
            },
            firstVenue: {
                title: 'Your first venue',
                why: 'A venue is a place with its own opening hours, PCs and prices.',
                action: 'Create a venue',
            },
            openingHours: {
                title: 'Opening hours',
                why: 'When you open decides closing-time warnings and which business day takings belong to.',
                action: 'Set hours',
            },
            price: {
                title: 'How players pay',
                why: 'Create your first pass. Pay as you go and the wallet are set up beside it, now or later.',
                action: 'Set up',
            },
            stationPaired: {
                title: 'Pair your first PC',
                why: 'Install Clavian Station on a gaming PC, then pair it here with the code it shows.',
                action: 'Add and pair a PC',
            },
            taxMode: {
                title: 'Tax mode and GSTIN',
                why: 'Whether this venue charges GST decides what its bills say.',
                action: 'Set tax mode',
            },
            lockdownTest: {
                title: 'Test a PC’s lockdown',
                why: 'Proves a paired PC keeps players inside what you allow. Open the PC on the Floor and run the test.',
                action: 'Open the Floor',
            },
            printer: {
                title: 'Receipt printer',
                why: 'Print receipts at the counter and open the cash drawer.',
                action: 'Add a printer',
            },
            payments: {
                title: 'Payments at the counter',
                why: 'Your UPI ID puts a QR on screen that players scan to pay.',
                action: 'Set up payments',
            },
            kioskSignUp: {
                title: 'Sign-up at the PCs',
                why: 'Choose whether new players can create an account at a PC.',
                action: 'Choose',
            },
            offlineCodes: {
                title: 'Ready for an internet outage',
                why: 'Decide what PCs do when the internet drops, and print codes staff can start play with.',
                action: 'Open offline play',
            },
            staffPin: {
                title: 'Your staff PIN',
                why: 'Opens a PC for maintenance at the kiosk and signs you in on shared devices.',
                action: 'Set your PIN',
            },
            staffInvited: {
                title: 'Invite your staff',
                why: 'Each person signs in as themselves, with only what their role allows.',
                action: 'Invite staff',
            },
            firstSession: {
                title: 'Run a first session',
                why: 'Sign in at a paired PC and play a few minutes, to see time and charges work end to end.',
                action: 'Open the Floor',
            },
        },
        business: {
            title: 'Business details',
            legalName: 'Legal name',
            registeredAddress: 'Registered address',
            pan: 'PAN',
            onRecord: 'On record',
            missing: 'Missing',
            optional: 'Optional, not on record',
        },
        goLive: {
            title: 'Go live',
            ready: '{{venue}} has everything it needs to open. Going live is one-way: it can be paused later, never set up again.',
            waiting: 'Go live when these are done:',
            waitingShort: 'Go live waits on {{steps}}.',
            live: '{{venue}} is open for business.',
            paused: '{{venue}} is paused. Resume it from its settings.',
            refused: 'The venue is not ready yet. Finish:',
            openSetup: 'Open setup',
            checklist: 'What Go live waits on',
        },
        summary: {
            title: 'Setup',
            intro: 'What this venue needs before it opens, each step checked against the venue itself.',
            open: 'Open setup',
            allDone: 'Every step is done or skipped.',
        },
        // How players pay: the three ways players pay for play time, and the venue's first pass.
        pay: {
            title: 'How players pay',
            intro: 'Passes first. Turn on the other two whenever you want them.',
            edit: 'Edit',
            close: 'Done',
            editNamed: 'Edit {{way}}',
            closeNamed: 'Close {{way}}',
            waysLabel: 'Ways players pay',
            recommended: 'Recommended',
            done: 'Done',
            loading: 'Reading how players pay here',
            unavailable: 'How players pay could not be loaded.',
            saveFailed: 'That could not be saved. Try again.',
            saveStale: 'These settings changed since you opened them. They have been read again: try once more.',
            on: 'On',
            off: 'Off',
            passes: {
                title: 'Passes',
                what: 'Blocks of play time bought ahead, like 1 hour or a 3-hour pack.',
                when: 'Best for most cafés: players pay first, and their time counts down as they play.',
                onSale_one: '{{count}} on sale',
                onSale_other: '{{count}} on sale',
                none: 'None yet',
                noneSummary: 'Nothing on sale yet.',
                listLabel: 'Passes on sale here',
                more_one: 'and {{count}} more',
                more_other: 'and {{count}} more',
                createTitle: 'Create your first pass',
                defaultName: '1 hour',
                defaultClassName: 'Standard',
                createHint: 'Suggested values: change any of them first.',
                name: 'Name',
                minutes: 'Play time in minutes',
                price: 'Price ({{currency}})',
                priceHint: 'An hour at this venue’s pay as you go rate.',
                className: 'Station class',
                classNameHint: 'This venue has no station class yet: it is made with the pass.',
                everyClass: 'Plays on every station class here: {{classes}}.',
                newClass: 'Plays on the class above.',
                sold: 'Sold at the counter and on the PCs. Usable for {{days}} days after it is bought.',
                create: 'Create pass',
                created: '{{name}} is on sale.',
                failed: 'The pass could not be created. Check the fields and try again.',
                nameRequired: 'Give the pass a name.',
                minutesRange: 'Enter between 15 and 43,200 minutes.',
                priceInvalid: 'Enter a price above zero, like 60 or 60.50.',
                classRequired: 'Name the class.',
                notPermitted: 'Your role cannot make passes. An owner or manager can.',
                later: 'Change later in Pricing',
            },
            payg: {
                title: 'Pay as you go',
                what: 'An hourly rate per station class, charged for the time played.',
                when: 'For walk-ins who do not know how long they will stay.',
                switchLabel: 'Pay as you go',
                rate: 'Hourly rate ({{currency}})',
                rateHint: 'What an hour costs once it is on. Set a different rate per class later in Pricing.',
                rateNow: '{{rate}} an hour',
                offSummary: 'Off: players buy a pass to play.',
                offHint: 'Off: players buy a pass to play. Turning it on uses the rate above.',
                onHint: 'On: players without a pass are charged for the time they play.',
                rateInvalid: 'Enter a rate above zero, like 60.',
                turnedOn: 'Pay as you go is on.',
                turnedOff: 'Pay as you go is off.',
                confirmOff: 'Turn pay as you go off?',
                confirmOffBody: 'Players then need a pass, or time added by staff, to play, and the wallet stops paying for play.',
                keepOn: 'Keep it on',
                turnOff: 'Turn off',
                readOnly: 'Your role cannot change this. An owner or manager can.',
                later: 'Change later in Pricing',
            },
            wallet: {
                title: 'Wallet',
                what: 'A balance players top up at the counter and spend here on passes, food and play.',
                when: 'For regulars who come back often.',
                switchLabel: 'Wallet',
                scope: 'For every venue of {{organization}}.',
                scopeUnnamed: 'For every venue of your business.',
                limits: 'Top-ups from {{min}} to {{max}}, and a balance of up to {{balance}}.',
                onSummary: 'On for every venue.',
                offSummary: 'Off: players cannot hold a balance.',
                fundsPlay: 'Wallet pays for play',
                fundsPlayHint: 'Off until you turn it on: no session draws on a wallet before then.',
                fundsPlayNeeds: 'Needs the wallet and pay as you go on.',
                turnedOn: 'The wallet is on.',
                turnedOff: 'The wallet is off.',
                fundsOn: 'The wallet now pays for play.',
                fundsOff: 'The wallet no longer pays for play.',
                confirmOff: 'Turn the wallet off?',
                confirmOffBody: 'For every venue: players keep their balances, but cannot top up or spend them until it is on again.',
                keepOn: 'Keep it on',
                turnOff: 'Turn off',
                readOnly: 'Only an owner can turn the wallet on or off.',
                later: 'Change later in Wallet settings',
            },
        },
        // On the Floor of a venue still setting up, until a pass is on sale.
        floorBanner: {
            title: 'Finish setup: create your first pass',
            body: 'Going live waits on a pass players can buy.',
            action: 'Create a pass',
        },
    },
    // The console tour (spec/13 §2): a few stops around the shell on a member's first visit, replayed from the staff menu.
    tour: {
        label: 'A tour of the console',
        take: 'Take the tour',
        stops: {
            setup: { title: 'Setup', body: 'What is left before a venue opens, each step one click from where it is done.' },
            floor: { title: 'Floor', body: 'Every station live: who is playing, for how long, and what needs you.' },
            sell: { title: 'Sell', body: 'One counter for passes, top-ups, food and anything else you sell.' },
            stations: { title: 'Stations', body: 'Add PCs and consoles, pair them, and set their classes and zones.' },
            library: { title: 'Library', body: 'The games and apps players can launch at your PCs.' },
            reports: { title: 'Reports', body: 'Takings, sessions and what sold, by business day.' },
            settings: { title: 'Settings', body: 'Opening hours, prices, payments, printers and the rules your venue runs by.' },
        },
    },
};
export default consoleCopy;
//# sourceMappingURL=console.js.map